<template>
  <ThemeOnly theme="modern">
    <ModernAIChatAgentSetup />
  </ThemeOnly>
  <ThemeOnly theme="modern" :invert="true">
    <div class="relative ai-agent-setup" :dir="isRtl === true ? 'rtl' : 'ltr'">
      <div v-if="initializing === true" class="fixed inset-0 bg-base-100 bg-opacity-75 z-50 flex items-center justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
      
      
      
      <div class="mt-6 ml-4 flex justify-between space-x-4 m-2 mr-4 flex-end relative title-row" :class="{ 'rtl-row': isRtl === true }">
        <div>
          <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ isEditMode === true ? t('aiChatAgentSetup.pageTitle.edit', 'Edit AI Agent') : t('aiChatAgentSetup.pageTitle.new', 'New Agent') }}
          </h1>
          <h1 class="text text-gray-900 dark:text-gray-200" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ isEditMode === true ? t('aiChatAgentSetup.pageSubtitle.edit', 'Edit your custom AI agent.') : t('aiChatAgentSetup.pageSubtitle.new', 'Create a new custom AI agent for your use.') }}
          </h1>
        </div>
        <div v-if="isEditMode === true" class="flex items-center space-x-4 bg-base-100 p-2 rounded-lg shadow-md pl-3 pr-3">
          <span class="text-lg font-semibold">{{ agentName }}</span>
          <span class="badge badge-outline" v-if="agentType === 'PublicAgent'">{{ t('aiChatAgentSetup.agentType.publicAgent', 'Public Agent') }}</span>
          <span class="badge badge-outline" v-if="agentType === 'PrivateAgent'">{{ t('aiChatAgentSetup.agentType.privateAgent', 'Private Agent') }}</span>
          <button @click="openChat" class="btn btn-sm">{{ t('aiChatAgentSetup.buttons.openChat', 'Open Chat') }}</button>
        </div>
      </div>
    </div>
    <div role="tablist" v-show="initializing === false" class="tabs tabs-lifted ai-agent-setup" :dir="isRtl === true ? 'rtl' : 'ltr'" style="margin:1%; margin-top: 2%;">
      <input type="radio" data-currentPage="1" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.agentInformation', 'Agent Information')" checked="checked" />
      <div role="tabpanel" data-currentPage="1" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between" id="tabTitle">
          <h1 class="text-2xl" style="margin-bottom: 0%;" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.title', 'Agent Information') }}</h1>
          <div class="form-control" id="agent-active-toggle-div" v-show="false">
            <label class="label cursor-pointer">
              <span class="label-text mr-2">{{ t('aiChatAgentSetup.agentInformation.activeLabel', 'Active') }}</span>
              <input type="checkbox" v-model="agentActive" class="toggle toggle-accent" />
            </label>
          </div>
        </div>
      <div class="flex">
        <div v-if="route.query.preview === 'left'" class="flex-shrink-0" style="width: 35%; margin-right: 2%;">
          <div class="card bg-base-200 p-3 rounded-md">
            <div class="tabs tabs-lifted">
              <input type="radio" name="setup_preview_tabs" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.preview.live','Live Agent')" checked="checked" />
              <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-2" style="min-height: 460px;">
                <div id="setup-live-preview-container" style="min-height: 440px; position: relative;"></div>
              </div>
              <input type="radio" name="setup_preview_tabs" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.preview.flow','Flow Preview')" />
              <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-2" style="min-height: 460px;">
                <FlowPreviewChat v-if="isEditMode === true && route.query.edit !== undefined" :agent-id="route.query.edit" :key="flowPreviewKey" />
              </div>
            </div>
          </div>
        </div>
        <div id="section-1" :style="route.query.preview === 'left' ? 'width:63%' : 'width:60%'">
            <div class="mb-4">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentNameLabel', 'Agent Name') }}:<span class="text-error">*</span></p>
              <label class="input input-bordered flex items-center space-x-2 w-90" :class="{ 'input-error': agentNameError === true }">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                </svg>
                <input type="text" v-model="agentName" maxlength="100" class="grow" :placeholder="t('aiChatAgentSetup.agentInformation.agentNamePlaceholder', 'Agent name')" :style="getDirStyle('agentName')" @keydown="handleDirKeydown($event, 'agentName')" @input="agentNameError = false" />
              </label>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: agentName.length, max: 100 }, agentName.length + '/100') }}</div>
              <p v-if="agentNameError === true" class="text-error text-xs mt-1">{{ agentNameErrorMessage }}</p>
            </div>
            <div class="mb-4 w-90">
              <div class="flex items-center justify-between mb-2">
                <p class="label cursor-pointer mb-0" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
                  {{ t('aiChatAgentSetup.agentInformation.agentInstructionsLabel', 'Agent Instructions and Description') }}:<span class="text-error">*</span>
                </p>
                <div class="flex items-center">
                  <AgentToneSelector
                    :selected-tone="selectedAgentTone"
                    @update:selected-tone="selectedAgentTone = $event"
                  />
                  <AgentPersonalitySelector
                    :selected-personality="selectedAgentPersonality"
                    @personality-change-requested="handlePersonalityChange"
                    style="margin-inline-start: 16px;"
                  />
                </div>
              </div>
              <div class="relative">
                <button type="button" class="btn btn-ghost btn-xs absolute z-10" style="top: 12px; left: 12px;" @click="showMaxPromptModal = true" :title="t('aiChatAgentSetup.agentInformation.agentInstructionsLabel', 'Agent Instructions and Description')">
                  <Icon icon="lucide:maximize-2" class="w-4 h-4" />
                </button>
                <textarea
                  v-model="agentDescription"
                  class="textarea textarea-bordered w-100"
                  :class="{ 'textarea-error': agentDescriptionError === true }"
                  style="width: 100%;height: 160px;"
                  :style="getDirStyle('agentDescription')"
                  :placeholder="t('aiChatAgentSetup.agentInformation.agentInstructionsPlaceholder', 'Description Prompt')"
                  @keydown="handleDirKeydown($event, 'agentDescription')" @input="handleAgentDescriptionInput()"
                ></textarea>
              </div>
              <p v-if="agentDescriptionError === true" class="text-error text-xs mt-1">{{ agentDescriptionErrorMessage }}</p>
            </div>
            <div class="mb-4 w-90">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentShortDescriptionLabel', 'Agent Short Description') }}:<span class="text-error">*</span></p>
              <textarea
                v-model="agentShortDescription"
                class="textarea textarea-bordered w-100"
                :class="{ 'textarea-error': agentShortDescriptionError === true }"
                style="width: 100%;height: 80px;" :style="getDirStyle('agentShortDescription')"
                :placeholder="t('aiChatAgentSetup.agentInformation.agentShortDescriptionPlaceholder', 'A brief summary (for agent selection lists)')"
                @keydown="handleDirKeydown($event, 'agentShortDescription')" @input="agentShortDescriptionError = false"
                maxlength="250"
              ></textarea>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: agentShortDescription.length, max: 250 }, agentShortDescription.length + '/250') }}</div>
              <p v-if="agentShortDescriptionError === true" class="text-error text-xs mt-1">{{ agentShortDescriptionErrorMessage }}</p>
            </div>
            <div v-if="agentMode !== 'Flow' && agentMode !== 'FlowBased'" class="mb-4 w-90">
              <div class="flex justify-between items-center mb-2">
                <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.startingMessageLabel', 'Starting Message') }}:<span class="text-error">*</span></p>
              <CustomLanguageSelector
                v-model="selectedStartingMessageLang"
                :options="languages"
                :placeholder="t('aiChatAgentSetup.agentInformation.startingMessageLanguageSelectPlaceholder', 'Select Language')"
                widthClass="w-max min-w-[150px]"
              />
              </div>
              <textarea
                v-model="currentStartingMessage"
                class="textarea textarea-bordered w-100"
                :class="{ 'textarea-error': startingMessageError === true }"
                style="width: 100%;height: 100px;" :style="getDirStyle('startingMessage')"
                :placeholder="t('aiChatAgentSetup.agentInformation.startingMessagePlaceholder', 'Initial message when chat starts')"
                @keydown="handleDirKeydown($event, 'startingMessage')" @input="startingMessageError = false"
                maxlength="2000"
              ></textarea>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: currentStartingMessage.length, max: 2000 }, currentStartingMessage.length + '/2000') }}</div>
              <p v-if="startingMessageError === true" class="text-error text-xs mt-1">{{ startingMessageErrorMessage }}</p>
            </div>
  
            <div class="form-control w-90 mb-4" id="divisions-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
                  {{ t('aiChatAgentSetup.agentInformation.divisionsLabel', 'Divisions') }}:
                </span>
                <span class="text-error">*</span>
              </label>
              <DivisionSelect
                v-model="selectedDivisionIds"
                :options="divisions"
                :error="divisionError"
                :errorMessage="divisionErrorMessage"
              />
            </div>
            <div class="hidden">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentLanguageLabel', 'Agent Language') }}</p>
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">Agent Language:</p>
              <CustomLanguageSelector
                v-model="agentLanguage"
                :options="[
                  { code: 'auto', name: t('aiChatAgentSetup.agentInformation.languageOptions.auto', 'Auto') },
                  { code: 'en', name: t('aiChatAgentSetup.agentInformation.languageOptions.en', 'English') },
                  { code: 'he', name: t('aiChatAgentSetup.agentInformation.languageOptions.he', 'עברית') },
                  { code: 'es', name: t('aiChatAgentSetup.agentInformation.languageOptions.es', 'Spanish') },
                  { code: 'fr', name: t('aiChatAgentSetup.agentInformation.languageOptions.fr', 'French') },
                  { code: 'de', name: t('aiChatAgentSetup.agentInformation.languageOptions.de', 'German') },
                  { code: 'zh', name: t('aiChatAgentSetup.agentInformation.languageOptions.zh', 'Chinese') },
                  { code: 'ja', name: t('aiChatAgentSetup.agentInformation.languageOptions.ja', 'Japanese') },
                  { code: 'ar', name: t('aiChatAgentSetup.agentInformation.languageOptions.ar', 'Arabic') },
                  { code: 'ru', name: t('aiChatAgentSetup.agentInformation.languageOptions.ru', 'Russian') },
                  { code: 'pt', name: t('aiChatAgentSetup.agentInformation.languageOptions.pt', 'Portuguese') },
                  { code: 'it', name: t('aiChatAgentSetup.agentInformation.languageOptions.it', 'Italian') },
                  { code: 'ko', name: t('aiChatAgentSetup.agentInformation.languageOptions.ko', 'Korean') },
                  { code: 'hi', name: t('aiChatAgentSetup.agentInformation.languageOptions.hi', 'Hindi') },
                  { code: 'sv', name: t('aiChatAgentSetup.agentInformation.languageOptions.sv', 'Swedish') },
                  { code: 'nl', name: t('aiChatAgentSetup.agentInformation.languageOptions.nl', 'Dutch') },
                  { code: 'da', name: t('aiChatAgentSetup.agentInformation.languageOptions.da', 'Danish') },
                  { code: 'fi', name: t('aiChatAgentSetup.agentInformation.languageOptions.fi', 'Finnish') },
                  { code: 'no', name: t('aiChatAgentSetup.agentInformation.languageOptions.no', 'Norwegian') },
                  { code: 'tr', name: t('aiChatAgentSetup.agentInformation.languageOptions.tr', 'Turkish') },
                  { code: 'pl', name: t('aiChatAgentSetup.agentInformation.languageOptions.pl', 'Polish') }
                ]"
              />
            </div>
            
          </div>
          <div style="width:2px; background-color: dimgray; border-radius: 10px;margin-right: 2%;margin-left: 2%;"></div>
          <div id="section-2" class="flex flex-end flex-col w-90" style="width: 40%;">
            <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.configurationsTitle', 'Configurations') }}:</p>
            
            <div class="form-control w-90 mb-4" id="agent-type-selector-div" v-show="false">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentTypeLabel', 'Agent Type') }}:</span>
              </label>
              <AgentTypeSelector 
                :selected-agent-type="agentType" 
                @update:selected-agent-type="agentType = $event"
              />
            </div>
  
            <div class="form-control w-90 mb-4" id="agent-mode-selector-div" v-show="false">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentModeLabel', 'Agent Mode') }}:</span>
              </label>
              <AgentModeSelector 
                :selected-agent-mode="agentMode" 
                @mode-change-requested="handleAgentModeSelection"
              />
            </div>
  
            <div class="form-control w-90 mt-2" id="llm-selector-div" v-if="llmLockedId === null" v-show="false">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.llmModelLabel', 'LLM Model') }}:</span>
              </label>
              <LLMSelector 
                :availableLLMModels="availableLLMModels" 
                :selectedLLMModel="selectedLLMModel" 
                @update:selectedLLMModel="selectedLLMModel = $event"
              />
            </div>
            <div class="form-control w-90 mt-2" v-else v-show="false">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.llmModelLabel', 'LLM Model') }}:</span>
              </label>
              <div class="p-3 rounded-md bg-base-200 text-sm">{{ selectedLLMModel }}</div>
            </div>
  
            <div class="form-control w-90 mt-2" id="verify-llm-selector-div" v-if="showVerificationSelector === true" v-show="false">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.verifyLlmModelLabel', 'Verification LLM (verify twice)') }}:</span>
              </label>
              <LLMSelector 
                :availableLLMModels="availableLLMModels" 
                :selectedLLMModel="selectedVerificationLLMModel" 
                @update:selectedLLMModel="selectedVerificationLLMModel = $event"
              />
            </div>
            
  
            <div class="form-control w-90 mt-4 mb-4" id="agent-style-selector-div" v-show="false">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentStyleLabel', 'Agent Style') }}:</span>
              </label>
              <AgentStyleSelect
                v-model="selectedStyleGuid"
                :options="agentStyles"
                @create-new-style="showCreateStyleModal = true"
              />
            </div>
            
            
  
            <div class="form-control hidden" id="usage-model-external-knowledge-checkbox-div">
              <label class="label cursor-pointer">
                <input type="checkbox" checked="checked" class="checkbox mr-4" />
                <span class="label-text float-left" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.usageModelExternalKnowledgeLabel', 'Usage Model External Knowledge') }}</span>
              </label>
            </div>
            
       
            <div class="form-control w-90 mt-1 mb-4" id="deployment-channels-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.deploymentChannelsLabel', 'Deployment Channels') }}:</span>
              </label>
              <DeploymentChannelsOptionsSelector 
                v-model:selectedChannels="selectedDeploymentChannels"
                v-model:whatsappSourceId="selectedWhatsappSourceId"
                :agentId="route.query.edit"
                :whatsappNumber="whatsappDisplayNumber"
                v-model:phoneSourceId="selectedPhoneSourceId"
                :phoneNumber="phoneDisplayNumber"
                :allowedChannels="['Phone', 'Web Voice Agent', 'Whatsapp Voice']"
              />
            </div>
            <div class="form-control w-90 mt-2">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.phoneVoiceLabel', 'Phone Voice') }}:</span>
              </label>
              <CustomVoiceSelector
                v-model="voiceSettingsVoiceId"
                :accent="voiceSettingsAccent"
                :accentEnabled="true"
                :agentLanguage="voiceSettingsLanguage"
                @update:accent="voiceSettingsAccent = $event"
              />
            </div>
            <div class="form-control w-90 mt-2">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.language','Language') }}:</span>
              </label>
              <CustomLanguageSelector
                v-model="voiceSettingsLanguage"
                :options="voiceLanguageOptions"
              />
            </div>
            <div class="form-control w-90 mt-2">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.speed','Speed') }}:</span>
              </label>
              <input type="range" min="0.5" max="2" step="0.1" v-model.number="voiceSettingsSpeed" class="range range-primary w-full" />
              <div class="text-sm mt-1" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? { direction: 'rtl' } : { direction: 'ltr' }">{{ typeof voiceSettingsSpeed === 'number' ? voiceSettingsSpeed.toFixed(1) : voiceSettingsSpeed }}</div>
            </div>
            <div class="form-control w-90 mt-2">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.tone','Tone') }}:</span>
              </label>
              <CustomToneSelector
                v-model="voiceSettingsTone"
                :options="voiceToneOptions"
              />
            </div>
            <div class="form-control w-90 mt-2">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.mode', 'Communication mode') }}:</span>
              </label>
              <CustomModeSelector
                v-model="voiceSettingsMode"
                :options="voiceModeOptions"
              />
            </div>
  
            <div class="form-control mt-4 mb-4" id="enable-feedback-buttons-toggle-div" v-show="false">
              <label class="label cursor-pointer">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.enableFeedbackButtonsLabel', 'Enable Feedback Buttons (Like/Dislike)') }}:</span>
                <input type="checkbox" v-model="enableFeedbackButtons" class="toggle toggle-primary" />
              </label>
            </div>
            <div class="form-control mt-1 mb-8" id="enable-suggested-responses-toggle-div" v-show="false">
              <label class="label cursor-pointer">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.enableSuggestedResponsesLabel', 'Enable Suggested Responses') }}:</span>
                <input type="checkbox" v-model="enableSuggestedResponses" class="toggle toggle-primary" />
              </label>
            </div>
            
          </div>
        </div>
        <div class="flex justify-end" style="position: relative;">
          <div class="flex space-x-2">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(1, 1)" class="btn">
              <span v-if="isNavigating === 2" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      <template v-if="agentMode === 'Flow'">
        <input type="radio" data-currentPage="2" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.agentFlow', 'Agent Flow')" />
        <div role="tabpanel" data-currentPage="2" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 70vh;"> 
          <div class="flex justify-between items-center mb-4" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentFlow.title', 'Agent Flow') }}</h1>
          </div>
          <div class="flex"> 
            <div class="flex-grow" style="width: 100%;">
              <LLMFlow ref="llmFlowRef" />
            </div>
            <div class="flex-shrink-0 border-l pl-6 flex flex-col" style="width: 35%;display: none;">
            
              
               <div class="custom-tabs-container flex mb-2 border-b border-base-300">
                   <button 
                       class="custom-tab-button flex-1 px-4 py-2 text-sm font-medium text-center transition-colors duration-150 ease-in-out"
                       :class="{
                           'active-tab': activeFlowPane === 'Preview',
                           'inactive-tab': activeFlowPane !== 'Preview'
                       }"
                       @click="activeFlowPane = 'Preview'"
                   >
                       {{ t('aiChatAgentSetup.agentFlow.previewTab', 'Preview') }}
                   </button>
                   <button 
                       class="custom-tab-button flex-1 px-4 py-2 text-sm font-medium text-center transition-colors duration-150 ease-in-out"
                       :class="{
                           'active-tab': activeFlowPane === 'Flow AI Agent',
                           'inactive-tab': activeFlowPane !== 'Flow AI Agent'
                       }"
                       @click="activeFlowPane = 'Flow AI Agent'"
                   >
                       {{ t('aiChatAgentSetup.agentFlow.flowAIAgentTab', 'Flow AI Agent') }}
                   </button>
               </div>
  
  
               <div class="custom-tab-content flex-grow bg-base-100 rounded-b-box overflow-hidden relative">
       
                  <div 
                      class="tab-pane h-full w-full absolute inset-0 p-1 transition-opacity duration-200 ease-in-out"
                      :class="{'opacity-100': activeFlowPane === 'Preview', 'opacity-0 pointer-events-none': activeFlowPane !== 'Preview'}"
                   >
                      <div class="card bg-base-200 p-4 rounded-md">
                        <FlowPreviewChat 
                            v-if="isEditMode === true && route.query.edit !== undefined" 
                            :agent-id="route.query.edit" 
                            :key="flowPreviewKey" 
                        />
                      </div>
                  </div>
  
                  <div 
                      class="tab-pane h-full w-full absolute inset-0 p-1 transition-opacity duration-200 ease-in-out"
                      :class="{'opacity-100': activeFlowPane === 'Flow AI Agent', 'opacity-0 pointer-events-none': activeFlowPane !== 'Flow AI Agent'}"
                   >
                      <FlowBuilderAgent 
                          v-if="isEditMode === true"
                          :agent-id="route.query.edit"
                          :llm-flow-ref="llmFlowRef" 
                          @flow-updated="handleFlowUpdateFromAgent" 
                          class="h-full w-full"/>
                       <div v-else class="p-4 text-center text-gray-500">
                         <span>Save the agent first to enable the Flow AI Agent.</span>
                       </div>
                   </div>
               </div>
              
            </div>
          </div>
  
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(2, -1)" class="btn">
              <span v-if="isNavigating === 1" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex space-x-2">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                  <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
               </button>
              <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
                  <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
               </button>
              <button @click="goNextStep(2, 1)" class="btn">
                <span v-if="isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <template v-if="agentMode === 'FlowBased'">
        <input type="radio" data-currentPage="2" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.flowBased', 'Flow Based Agent')" />
        <div role="tabpanel" data-currentPage="2" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 70vh;">
          <div class="flex justify-between items-center mb-4" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.flowBased.title', 'Flow Based Agent') }}</h1>
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(2, -1)" class="btn">
              <span v-if="isNavigating === 1" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex space-x-2">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                  <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
               </button>
              <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
                  <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
               </button>
              <button @click="goNextStep(2, 1)" class="btn">
                <span v-if="isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <input type="radio" data-currentPage="3" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.knowledgeBase', 'Knowledge Base')" />
      <div role="tabpanel" data-currentPage="3" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between items-center" id="tabTitle">
          <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.knowledgeBase.title', 'Knowledge Base') }}</h1>
          <div class="flex items-center justify-evenly">
            <button @click="refreshAgentData" class="btn btn-ghost btn-square" :disabled="isRefreshingKB === true" :title="t('aiChatAgentSetup.knowledgeBase.refreshButtonTooltip', 'Refresh Statuses')">
               <Icon icon="heroicons:arrow-path" class="h-5 w-5" :class="{'animate-spin': isRefreshingKB !== undefined ? isRefreshingKB : false}" />
            </button>
            <button @click="openCreateFolderModal" class="btn">
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="none" stroke="#000" stroke-linecap="round" stroke-width="1.5" d="M18 10h-5m9 1.798c0-2.632 0-3.949-.77-4.804a3 3 0 0 0-.224-.225C20.151 6 18.834 6 16.202 6h-.374c-1.153 0-1.73 0-2.268-.153a4 4 0 0 1-.848-.352C12.224 5.224 11.816 4.815 11 4l-.55-.55c-.274-.274-.41-.41-.554-.53a4 4 0 0 0-2.18-.903C7.53 2 7.336 2 6.95 2c-.883 0-1.324 0-1.692.07A4 4 0 0 0 2.07 5.257C2 5.626 2 6.068 2 6.95M21.991 16c-.036 2.48-.22 3.885-1.163 4.828C19.657 22 17.771 22 14 22h-4c-3.771 0-5.657 0-6.828-1.172S2 17.771 2 14v-3"/></svg>
               {{ t('aiChatAgentSetup.knowledgeBase.createFolderButton', 'Create Folder +') }}
            </button>
            <div class="form-control" id="upload-knowledge-dropdown-div">
              <div class="dropdown dropdown-end">
                <div tabindex="0" role="button" class="btn">{{ t('aiChatAgentSetup.knowledgeBase.uploadKnowledgeButton', 'Upload Knowledge +') }}</div>
                <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
                  <li>
                    <label class="btn btn-ghost justify-start">
                      <input type="file" @change="handleFileUpload" multiple class="hidden" accept=".docx,.pdf,.txt,.xlsx" />
                      {{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.uploadFiles', 'Upload Files') }}
                    </label>
                  </li>
                  <li><button @click="showTextInputModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.addText', 'Add Text') }}</button></li>
                  <li><button @click="showWebsiteLinkModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.addWebsiteLink', 'Add Website Link') }}</button></li>
                   <li><button @click="showWebsiteBatchModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.addWebsiteBatch', 'Add Website Batch URLs') }}</button></li>
                  <li><button @click="showWebsiteScrapeModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.scrapeWebsite', 'Scrape Entire Website') }}</button></li>
                  <li><button @click="showSharedKbPicker = true" class="btn btn-ghost justify-start" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiChatAgentSetup.knowledgeBase.linkSharedKnowledgeBases', 'Link Shared Knowledge Bases') }}</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="text-sm breadcrumbs my-4">
           <ul>
             <li v-for="(part, index) in currentKbPath" :key="part.id != undefined ? part.id : 'root'">
               <a @click="navigateToBreadcrumb(index)" class="cursor-pointer hover:underline">
                 <svg v-if="index === 0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-1 inline-block">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" />
                 </svg>
                 <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-1 inline-block">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                 </svg>
                 {{ part.name }}
              </a>
             </li>
           </ul>
         </div>
        <div class="flex justify-between items-center mb-4 mt-4">
          <div class="form-control flex-grow" id="kb-search-input-div">
             <div class="input-group">
              <input type="text" v-model="searchQuery" :placeholder="t('aiChatAgentSetup.knowledgeBase.searchPlaceholder', 'Search knowledge in current folder...')" class="input input-bordered w-full" :style="getDirStyle('searchQuery')" @keydown="handleDirKeydown($event, 'searchQuery')"/>
             </div>
           </div>
          <div class="ml-4 dropdown dropdown-end">
              <label tabindex="0" class="btn m-1">
                  {{ t('aiChatAgentSetup.knowledgeBase.viewMode.label', 'View') }}: {{ kbViewMode === 'rows' ? t('aiChatAgentSetup.knowledgeBase.viewMode.rows', 'Rows') : t('aiChatAgentSetup.knowledgeBase.viewMode.grid', 'Grid') }}
                  <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-1"/>
              </label>
              <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-32">
                  <li><a @click="kbViewMode = 'rows'" :class="{'font-bold text-primary': kbViewMode === 'rows'}">{{ t('aiChatAgentSetup.knowledgeBase.viewMode.rows', 'Rows') }}</a></li>
                  <li><a @click="kbViewMode = 'grid'" :class="{'font-bold text-primary': kbViewMode === 'grid'}">{{ t('aiChatAgentSetup.knowledgeBase.viewMode.grid', 'Grid') }}</a></li>
              </ul>
           </div>
          <div class="ml-4 dropdown dropdown-end" :class="{ 'dropdown-open': isAdvancedOpen === true }" ref="advancedDropdownRef">
              <label tabindex="0" class="btn m-1" @click="isAdvancedOpen = (isAdvancedOpen !== true)">
                  {{ t('aiChatAgentSetup.knowledgeBase.advanced.label', 'Advanced') }}
                  <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-1"/>
              </label>
              <div tabindex="0" class="dropdown-content z-[2] p-4 shadow bg-base-100 rounded-box w-80">
                  <div class="flex items-center mb-3">
                      <span class="mr-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.rowsPerPage', 'Rows per page') }}:</span>
                      <CustomDropdownSelector id="kbItemsPerPageSelect" v-model.number="itemsPerPageKb" class="w-28" buttonClass="btn-sm" :options="[...itemsPerPageKbOptions.map(opt => ({ value: opt, label: String(opt) })), { value: 0, label: t('aiChatAgentSetup.knowledgeBase.advanced.all', 'All') }]" />
                  </div>
                  <div class="flex items-center mb-2">
                      <button class="btn btn-sm mr-2" @click="runAdvancedAction('selectAllFiltered')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.selectAll', 'Select All') }}</button>
                      <button class="btn btn-sm mr-2" @click="runAdvancedAction('clearSelection')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.clearSelection', 'Clear Selection') }}</button>
                      <button class="btn btn-sm btn-error" @click="runAdvancedAction('deleteSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.deleteSelected', 'Delete Selected') }}</button>
                  </div>
                  <div class="divider my-2"></div>
                  <div class="mb-2 text-sm font-semibold">{{ t('aiChatAgentSetup.knowledgeBase.advanced.bulkScheduler', 'Bulk Rescrape Scheduler') }}</div>
                  <div class="flex items-center mb-2">
                      <span class="mr-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.interval', 'Interval') }}:</span>
                      <CustomDropdownSelector id="bulkRescrapeIntervalSelect" v-model.number="bulkRescrapeIntervalDays" class="w-28" buttonClass="btn-sm" :options="[{ value: 1, label: '1 day' }, { value: 7, label: '7 days' }, { value: 14, label: '14 days' }, { value: 30, label: '30 days' }]" />
                  </div>
                  <div class="flex items-center mb-3">
                      <span class="mr-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.time', 'Time (UTC)') }}:</span>
                      <input type="time" v-model="bulkRescrapeAtTime" step="1800" class="input input-bordered input-xs w-28" />
                  </div>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('enableRescrapeSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.enableRescrapeSelected', 'Update Rescrape Info (Selected)') }}</button>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('disableRescrapeSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.disableRescrapeSelected', 'Disable Rescrape (Selected)') }}</button>
                  <div class="divider my-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.immediate', 'Immediate') }}</div>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('rescanSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.rescanSelected', 'Rescrape Selected') }}</button>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('retryProcessingSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.retryProcessingSelected', 'Rescrape Selected Processing') }}</button>
                  <button class="btn btn-sm w-full" @click="runAdvancedAction('rescanFailedSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.rescanFailedSelected', 'Rescrape Selected Failed') }}</button>
              </div>
          </div>
          
        </div>
  
      
        <div v-if="kbViewMode === 'rows'">
          <div class="overflow-x-auto relative">
            <div v-if="isRefreshingKB === true" class="absolute inset-0 bg-base-100 bg-opacity-75 flex items-center justify-center z-10 rounded-box">
               <span class="loading loading-spinner loading-lg"></span>
            </div>
            <table class="table w-full">
              <thead>
                <tr>
                  <th class="w-1/12">
                    <input type="checkbox" :checked="isAllSelectedOnPage === true" @change="toggleSelectAllOnPage" />
                  </th>
                  <th class="w-2/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.name', 'Name') }}</th>
                  <th class="w-1/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.type', 'Type') }}</th>
                  <th class="w-1/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.status', 'Status') }}</th>
                  <th class="w-1/5 text-center">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.actions', 'Actions') }}</th>
                  <th class="w-1/5 text-center">Rescrape</th>
                  <th class="w-1/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.cache', 'Cache') }}</th>
                  <th class="w-1/5 text-center">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.hideSource', 'Hide Source') }}</th>
                </tr>
              </thead>
              <tbody>
                   <tr v-if="currentFolderId === null" v-for="kb in linkedSharedKbItems" :key="'sharedkb-' + kb.kbId" class="hover">
                     <td></td>
                     <td class="cursor-default hover:text-primary flex items-center">
                       <Icon icon="mdi:library" class="w-5 h-5 mr-2 text-purple-500" />
                       {{ kb.name }}
                     </td>
                     <td>Shared KB</td>
                     <td>
                       <span class="badge badge-success">Linked</span>
                     </td>
                     <td class="text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                        <button @click="unlinkSharedKb(kb.kbId)" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.unlinkButton','Unlink') }}</button>
                        <router-link to="/sharedKnowledgeBases" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.openButton','Open') }}</router-link>
                       </div>
                     </td>
                     <td></td>
                     <td></td>
                     <td class="text-center">-</td>
                   </tr>
                   <tr v-for="folder in paginatedFoldersInCurrentView"
                       :key="folder.id"
                       class="hover"
                       :class="{ 'bg-blue-100 dark:bg-blue-800 border-blue-300 dark:border-blue-600': isDragOverFolder === folder.id }"
                       @dragover.prevent="handleFolderDragOver($event, folder.id)"
                       @dragleave.prevent="handleFolderDragLeave($event, folder.id)"
                       @drop.prevent="handleFileDropOnFolder(folder.id)">
                    <td></td>
                    <td @click="navigateToFolder(folder)" class="cursor-pointer hover:text-primary flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2 text-warning">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                      </svg>
        
                      <span v-if="folderBeingRenamedId !== folder.id">{{ folder.name }}</span>
                      <input v-else
                             type="text"
                             v-model="newFolderNameInput"
                             class="input input-sm input-bordered w-full max-w-xs"
                             :data-folder-rename-id="folder.id"
                             @blur="saveFolderName(folder)"
                             @keyup.enter="saveFolderName(folder)"
                             @keyup.esc="cancelRename"/>
                    </td>
                    <td>Folder</td>
                    <td>-</td>
                    <td class="text-center">
                      <div class="inline-flex items-center justify-center whitespace-nowrap">
                      <button v-if="folderBeingRenamedId !== folder.id"
                              @click.stop="startRenamingFolder(folder)"
                              class="btn btn-xs mx-1">Rename</button>
                      <button @click.stop="deleteFolder(folder.id)" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.delete','Delete') }}</button>
                      </div>
                    </td>
                    <td></td>
                    <td>
                        <input type="checkbox" 
                               v-if="folder.fileType === 'text'" 
                               :checked="isCached(folder.name) !=false" 
                               @change.stop="toggleCacheFile(folder)" 
                               class="checkbox checkbox-primary checkbox-xs" />
                    </td>
                    <td class="text-center">
                        <input type="checkbox"
                               v-model="folder.hiddenFromSources"
                               class="checkbox checkbox-secondary checkbox-xs"
                               @change.stop
                               title="Hide this folder from being cited as a source in chat responses" />
                    </td>
                  </tr>
                   <tr v-for="file in paginatedFilteredExistingFiles"
                       :key="'existing-' + file.fileName"
                       class="hover"
                       draggable="true"
                       @dragstart="handleFileDragStart(file, 'existing', $event)"
                       @dragend="handleDragEnd">
                    <td>
                      <input type="checkbox" :checked="isRowSelected(file.fileName) === true" @change="toggleSelectRow(file)" />
                    </td>
                     <td @click="openInspectOrEditTextFile(file)" class="cursor-pointer hover:text-primary flex items-center" :title="decodeURIComponentSafe(file.fileName)">
                        <Icon icon="heroicons:document" class="w-5 h-5 mr-2 text-info" />
                       {{ truncateDisplayFileName(file.fileName) }}
                     </td>
                     <td>{{ file.fileType === 'websiteurl' ? 'Website URL' : file.fileType === 'website' ? 'Website' : file.fileType !== undefined && file.fileType !== null ? file.fileType.toUpperCase() : 'File' }}</td>
                     <td>
                       <span v-if="file.status === 'deleted'" class="badge badge-error">To Delete</span>
                       <template v-else>
                         <span v-if="file.processingStatus === 'Available' || file.processingStatus === 'Completed'" class="badge badge-success">Available</span>
                         <span v-else-if="file.processingStatus === 'Processing'" class="badge badge-warning">Processing</span>
                         <span v-else-if="file.processingStatus === 'Failed' || file.processingStatus === 'Error'" class="badge badge-error">{{ file.processingStatus }}</span>
                         <span v-else class="badge">{{ file.processingStatus }}</span>
                       </template>
                     </td>
                     <td class="text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                       <button v-if="file.status !== 'deleted' && (file.processingStatus === 'Available' || file.processingStatus === 'Completed' || file.processingStatus === 'Failed' || file.processingStatus === 'Error')" @click="removeFileByName(file, 'existing')" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.delete','Delete') }}</button>
                       <button v-else-if="file.status === 'deleted'" @click="existingFiles[existingFiles.findIndex(ef => ef.fileName === file.fileName)].status = 'existing'" class="btn btn-success btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.restore','Restore') }}</button>
                       <button @click="openMoveFileModal(file, 'existing')" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.move','Move') }}</button>
                        <button v-if="['website','websiteurl','websitepage'].includes(file.fileType) === true" @click="openScrapeReport(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.reportButton','Report') }}</button>
                        <button v-if="shouldShowContinue(file) === true" @click="continueWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.continueButton','Continue') }}</button>
                        <button v-if="shouldShowRetryFailed(file) === true" @click="retryFailedWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.retryFailedButton','Retry Failed') }}</button>
                       </div>
                     </td>
                    <td class="min-w-48">
                      <div v-if="['website','websiteurl','websitepage'].includes(file.fileType) !== false" class="form-control w-full">
                        <div class="flex items-center justify-between">
                          <div dir="ltr" class="inline-flex items-center whitespace-nowrap">
                            <input type="number"
                                   v-model.number="file.rescrapeIntervalDays"
                                   class="input input-bordered input-xs w-14 mx-1 shrink-0"
                                   min="1"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''"
                                   :placeholder="t('aiChatAgentSetup.knowledgeBase.daysPlaceholder', 'Days')" />
                            <input type="time"
                                   v-model="file.rescrapeAtTime"
                                   step="1800"
                                   class="input input-bordered input-xs w-20 shrink-0"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''" />
                          </div>
                          <input
                                 type="checkbox"
                                 :checked="file.rescrapeEnabled === true"
                                 @click.stop
                                 @change.stop="file.rescrapeEnabled = $event.target.checked"
                                 class="toggle toggle-primary"
                                 :aria-label="t('aiChatAgentSetup.knowledgeBase.rescrapeLabel', 'Rescrape')" />
                        </div>
                      </div>
                    </td>
                    <td>
                        <input type="checkbox" 
                               v-if="file.fileType === 'text'" 
                               :checked="isCached(file.fileName) != false" 
                               @change.stop="toggleCacheFile(file)" 
                               class="checkbox checkbox-primary checkbox-xs" />
                     </td>
                     <td class="text-center">
                        <input type="checkbox"
                               v-model="file.hiddenFromSources"
                               class="checkbox checkbox-secondary checkbox-xs"
                               @change.stop
                               title="Hide this file from being cited as a source in chat responses" />
                     </td>
                   </tr>
                   <tr v-for="file in paginatedFilteredUploadedFiles"
                       :key="'new-' + file.fileName"
                       class="hover"
                       draggable="true"
                       @dragstart="handleFileDragStart(file, 'new', $event)"
                       @dragend="handleDragEnd">
                    <td>
                      <input type="checkbox" :checked="isRowSelected(file.fileName) === true" @change="toggleSelectRow(file)" />
                    </td>
                     <td @click="openInspectOrEditTextFile(file)" class="cursor-pointer hover:text-primary flex items-center" :title="decodeURIComponentSafe(file.fileName)">
                        <Icon icon="heroicons:document-arrow-up" class="w-5 h-5 mr-2 text-info" />
                       {{ truncateDisplayFileName(file.fileName) }}
                     </td>
                     <td>{{ file.fileType === 'websiteurl' ? 'Website URL' : file.fileType === 'website' ? 'Website' : file.fileType !== undefined && file.fileType !== null ? file.fileType.toUpperCase() : 'File' }}</td>
                     <td>
                       <span v-if="file.isEditedVersion === true" class="badge badge-warning">Waiting To Update</span>
                       <span v-else class="badge badge-info">New</span>
                      </td>
                     <td class="text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                       <button @click="removeFileByName(file, 'new')" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.delete','Delete') }}</button>
                       <button @click="openMoveFileModal(file, 'new')" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.move','Move') }}</button>
                       <button v-if="['website','websiteurl','websitepage'].includes(file.fileType) === true" @click="openScrapeReport(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.reportButton', 'Report') }}</button>
                       <button v-if="shouldShowContinue(file) === true" @click="continueWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.continueButton','Continue') }}</button>
                       <button v-if="shouldShowRetryFailed(file) === true" @click="retryFailedWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.retryFailedButton','Retry Failed') }}</button>
                       </div>
                     </td>
                    <td class="min-w-48">
                      <div v-if="['website','websiteurl','websitepage'].includes(file.fileType) !== false" class="form-control w-full">
                        <div class="flex items-center justify-between">
                          <div dir="ltr" class="inline-flex items-center whitespace-nowrap">
                            <input type="number"
                                   v-model.number="file.rescrapeIntervalDays"
                                   class="input input-bordered input-xs w-14 mx-1 shrink-0"
                                   min="1"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''"
                                   :placeholder="t('aiChatAgentSetup.knowledgeBase.daysPlaceholder', 'Days')" />
                            <input type="time"
                                   v-model="file.rescrapeAtTime"
                                   step="1800"
                                   class="input input-bordered input-xs w-20 shrink-0"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''" />
                          </div>
                          <input
                                 type="checkbox"
                                 :checked="file.rescrapeEnabled === true"
                                 @click.stop
                                 @change.stop="file.rescrapeEnabled = $event.target.checked"
                                 class="toggle toggle-primary"
                                 :aria-label="t('aiChatAgentSetup.knowledgeBase.rescrapeLabel', 'Rescrape')" />
                        </div>
                      </div>
                    </td>
                    <td>
                        <input type="checkbox" 
                               v-if="file.fileType === 'text'" 
                               :checked="isCached(file.fileName) !=false" 
                               @change.stop="toggleCacheFile(file)" 
                               class="checkbox checkbox-primary checkbox-xs" />
                     </td>
                     <td class="text-center">
                       <input type="checkbox"
                              v-model="file.hiddenFromSources"
                              class="checkbox checkbox-secondary checkbox-xs"
                              @change.stop
                              title="Hide this file from being cited as a source in chat responses" />
                     </td>
                   </tr>
                <tr v-if="paginatedFoldersInCurrentView.length === 0 && paginatedFilteredExistingFiles.length === 0 && paginatedFilteredUploadedFiles.length === 0 && (currentFolderId !== null || linkedSharedKbItems.length === 0)">
                    <td colspan="7" class="text-center text-gray-500 py-4">
                         This folder is empty. Use search to find items, or add new knowledge.
                     </td>
                 </tr>
               </tbody>
            </table>
          </div>
        </div>
  
   
         <div v-else-if="kbViewMode === 'grid'">
           <div class="relative">
              <div v-if="isRefreshingKB !== undefined ? isRefreshingKB : false" class="absolute inset-0 bg-base-100 bg-opacity-75 flex items-center justify-center z-10 rounded-box" style="min-height: 200px;">
                  <span class="loading loading-spinner loading-lg"></span>
              </div>
             <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 space-x-4 p-4">
              
              <div v-for="folder in paginatedFoldersInCurrentView"
                   :key="folder.id"
                   class="flex flex-col items-center justify-start p-3 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 dark:hover:bg-gray-700 transition-colors h-32"
                   :class="{ 'bg-blue-100 dark:bg-blue-800 border-blue-500 dark:border-blue-600 ring-2 ring-blue-400': isDragOverFolder === folder.id }"
                   @click="navigateToFolder(folder)"
                   @dragover.prevent="handleFolderDragOver($event, folder.id)"
                   @dragleave.prevent="handleFolderDragLeave($event, folder.id)"
                   @drop.prevent="handleFileDropOnFolder(folder.id)">
                   <Icon icon="heroicons:folder" class="w-12 h-12 text-warning mb-2"/>
                   <span class="text-sm text-center break-all" :title="folder.name">
                       {{ truncateDisplayFileName(folder.name, 25) }}
                   </span>
              </div>
  
               
               <div v-for="file in paginatedFilteredExistingFiles"
                    :key="'grid-existing-' + file.fileName"
                    class="flex flex-col items-center justify-start p-3 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 dark:hover:bg-gray-700 transition-colors h-32 relative"
                    draggable="true"
                    @dragstart="handleFileDragStart(file, 'existing', $event)"
                    @dragend="handleDragEnd"
                    @click="openInspectOrEditTextFile(file)"
                    :title="decodeURIComponentSafe(file.fileName) + ` (Status: ${file.status === 'deleted' ? 'To Delete' : file.processingStatus})`">
                   <Icon :icon="file.fileType === 'pdf' ? 'heroicons:document-text' : file.fileType === 'docx' ? 'simple-icons:microsoftword' : file.fileType === 'xlsx' ? 'simple-icons:microsoftexcel' : file.fileType === 'text' ? 'heroicons:document-text' : file.fileType === 'websiteurl' ? 'heroicons:link' : file.fileType === 'website' ? 'heroicons:globe-alt' : 'heroicons:document'"
                         class="w-12 h-12 mb-2"
                         :class="{
                             'text-error opacity-50': file.status === 'deleted',
                             'text-success': file.status !== 'deleted' && (file.processingStatus === 'Available' || file.processingStatus === 'Completed'),
                             'text-warning': file.status !== 'deleted' && file.processingStatus === 'Processing',
                             'text-error': file.status !== 'deleted' && (file.processingStatus === 'Failed' || file.processingStatus === 'Error'),
                             'text-secondary': file.status !== 'deleted' && (['Available', 'Completed', 'Processing', 'Failed', 'Error'].includes(file.processingStatus) === true)
                         }"/>
                   <span class="text-sm text-center break-all">
                       {{ truncateDisplayFileName(file.fileName, 25) }}
                   </span>
                   <span v-if="file.status === 'deleted'" class="badge badge-xs badge-error mt-1">To Delete</span>
                    <span v-else-if="file.processingStatus && (['Available', 'Completed'].includes(file.processingStatus) === true)"
                         class="badge badge-xs mt-1"
                         :class="{
                           'badge-warning': file.processingStatus === 'Processing',
                           'badge-error': file.processingStatus === 'Failed' || file.processingStatus === 'Error'
                         }">
                      {{ file.processingStatus }}
                   </span>
                    <input type="checkbox" 
                           v-if="file.fileType === 'text'" 
                           :checked="isCached(file.fileName)!=false" 
                           @change.stop="toggleCacheFile(file)" 
                           class="checkbox checkbox-primary checkbox-xs absolute top-1 right-1" 
                           title="Cache this file"/>
                    <input type="checkbox"
                           v-model="file.hiddenFromSources"
                           @click.stop
                           class="checkbox checkbox-secondary checkbox-xs absolute top-1 left-1"
                           title="Hide this file from being cited as a source in chat responses"/>
              </div>
  
            
               <div v-for="file in paginatedFilteredUploadedFiles"
                    :key="'grid-new-' + file.fileName"
                    class="flex flex-col items-center justify-start p-3 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 dark:hover:bg-gray-700 transition-colors h-32 relative"
                    draggable="true"
                    @dragstart="handleFileDragStart(file, 'new', $event)"
                    @dragend="handleDragEnd"
                    @click="openInspectOrEditTextFile(file)"
                    :title="decodeURIComponentSafe(file.fileName) + ' (Status: New)'">
                   <Icon :icon="file.fileType === 'pdf' ? 'heroicons:document-text' : file.fileType === 'docx' ? 'simple-icons:microsoftword' : file.fileType === 'xlsx' ? 'simple-icons:microsoftexcel' : file.fileType === 'text' ? 'heroicons:document-text' : file.fileType === 'websiteurl' ? 'heroicons:link' : file.fileType === 'website' ? 'heroicons:globe-alt' : 'heroicons:document-arrow-up'"
                         class="w-12 h-12 text-info mb-2"/>
                   <span class="text-sm text-center break-all">
                       {{ truncateDisplayFileName(file.fileName, 25) }}
                   </span>
                   <span v-if="file.isEditedVersion === true" class="badge badge-xs badge-warning mt-1">Waiting To Update</span>
                   <span v-else class="badge badge-xs badge-info mt-1">New</span>
                    <input type="checkbox" 
                           v-if="file.fileType === 'text'" 
                           :checked="isCached(file.fileName)!=false" 
                           @change.stop="toggleCacheFile(file)" 
                           class="checkbox checkbox-primary checkbox-xs absolute top-1 right-1" 
                           title="Cache this file"/>
                    <input type="checkbox"
                           v-model="file.hiddenFromSources"
                           @click.stop
                           class="checkbox checkbox-secondary checkbox-xs absolute top-1 left-1"
                           title="Hide this file from being cited as a source in chat responses"/>
              </div>
  
             <div v-if="paginatedFoldersInCurrentView.length === 0 && paginatedFilteredExistingFiles.length === 0 && paginatedFilteredUploadedFiles.length === 0"
                  class="col-span-full text-center text-gray-500 py-8">
                This folder is empty. Use search to find items, or add new knowledge.
             </div>
           </div>
          </div>
        </div>
  
  
        <div class="flex justify-between items-center mt-4">
          <button class="btn btn-sm" :disabled="currentKbPage <= 1" @click="currentKbPage = currentKbPage - 1"><Icon :icon="isRtl === true ? 'tabler:chevron-right' : 'tabler:chevron-left'" class="w-4 h-4" /></button>
          <span :style="getDirStyle('kbPagination')">{{ currentKbPage }}/{{ totalKbPages }}</span>
          <button class="btn btn-sm" :disabled="currentKbPage >= totalKbPages" @click="currentKbPage = currentKbPage + 1"><Icon :icon="isRtl === true ? 'tabler:chevron-left' : 'tabler:chevron-right'" class="w-4 h-4" /></button>
        </div>
        <div class="space-y-2" style="min-height: 100px;"></div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(3, -1)" class="btn">
            <span v-if="isNavigating === 2" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(3, 1)" class="btn">
              <span v-if="isNavigating === 4" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      <template v-if="isEditMode === true">
         <input type="radio" data-currentPage="4" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.fineTuneQA', 'Fine Tune Q&A')" />
        <div role="tabpanel" data-currentPage="4" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" style="margin-bottom: 0%;" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.fineTuneQA.title', 'Fine Tune Q&A') }}</h1>
          </div>
          <div role="tablist" class="tabs tabs-lifted mt-4">
            <input 
              type="radio" 
              name="fine_tune_tabs" 
              role="tab" 
              class="tab" 
              :aria-label="t('aiChatAgentSetup.fineTuneQA.fineTuneTab', 'Fine Tune')" 
              :checked="activeFineTuneTab === 'fineTune'" 
              @click="activeFineTuneTab = 'fineTune'"
            />
            <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
              <div class="mt-4">
                <FineTuneAgent :agentID="route.query.edit" @save-fine-tune="handleFineTuneSave" @approve-generated-rules="approveGeneratedRulesFromAdvanced" />
              </div>
            </div>
            <input 
              type="radio" 
              name="fine_tune_tabs" 
              role="tab" 
              class="tab" 
              :aria-label="t('aiChatAgentSetup.fineTuneQA.fineTunedAnswersTab', 'Fine Tuned Answers')" 
              :checked="activeFineTuneTab === 'fineTunedAnswers'" 
              @click="activeFineTuneTab = 'fineTunedAnswers'"
            />
            <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
              <h2 class="text-xl mb-4">{{ t('aiChatAgentSetup.fineTuneQA.existingAnswersTitle', 'Existing Fine-Tuned Answers (Assigned to Folders)') }}</h2>
              <p class="text-sm text-gray-500 mb-2">
                {{ t('aiChatAgentSetup.fineTuneQA.existingAnswersDescription', 'Fine-tuned answers are treated as files and can be organized into folders. They are primarily managed here but their folder assignment happens in the Knowledge Base tab.') }}
              </p>
              <div class="overflow-x-auto">
                <table class="table w-full">
                  <thead>
                    <tr>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.row', 'Row #') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.originalQuestion', 'Original Question (Preview)') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.fineTunedAnswer', 'Fine-Tuned Answer (Preview)') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.folder', 'Folder') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.actions', 'Actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(answerFile, index) in fineTunedAnswers" :key="'finedtuned-' + index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ answerFile.originalQuery }}</td>
                      <td>{{ answerFile.fineTunedAnswer }}</td>
                      <td>
                        {{ knowledgeBaseFileAssignments[answerFile.fileName] ? (knowledgeBaseFolders.find(f => f.id === knowledgeBaseFileAssignments[answerFile.fileName])?.name || t('aiChatAgentSetup.fineTuneQA.unknownFolder', 'Unknown Folder')) : t('aiChatAgentSetup.fineTuneQA.rootFolder', 'Knowledge Base Root') }}
                      </td>
                      <td>
                        <button @click="removeFineTunedAnswer(index)" class="btn btn-error btn-sm">{{ t('aiChatAgentSetup.fineTuneQA.actions.remove', 'Remove') }}</button>
                      </td>
                    </tr>
                    <tr v-if="fineTunedAnswers.length === 0">
                      <td colspan="5" class="text-center">{{ t('aiChatAgentSetup.fineTuneQA.noAnswers', 'No fine-tuned answers yet.') }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(4, -1)" class="btn">
              <span v-if="isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                  <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
               </button>
              <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
                  <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
               </button>
              <button @click="goNextStep(4, 1)" class="btn">
                <span v-if="isNavigating === 5" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
              </button>
            </div>
          </div>
        </div>
      </template>
       <input type="radio" data-currentPage="5" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.rules', 'Rules')" />
      <div role="tabpanel" data-currentPage="5" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between" id="tabTitle">
          <h1 class="text-2xl mb-0" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.rules.title', 'Agent Rules') }}</h1>
        </div>
        <div class="mt-4">
          <div class="mb-4">
            <RuleLibraryPanel :is-rtl="isRtl" :existing-rules="rules" :selected-language="agentLanguage" :agent-description="agentDescription" @add-rule="addRuleFromLibrary" />
          </div>
          <div class="flex space-x-2 mb-4">
            <textarea v-model="newRule" maxlength="3500" rows="1" class="textarea textarea-bordered flex-1 resize-none overflow-hidden" :placeholder="t('aiChatAgentSetup.rules.newRulePlaceholder','Enter a new rule')" :style="getDirStyle('newRule')" @keydown="handleDirKeydown($event, 'newRule')" @input="($event.target.style.height='auto',$event.target.style.height=$event.target.scrollHeight+'px')" />
            <div class="text-xs text-right pr-1 mt-1 self-center text-gray-500">{{ newRule.length }}/3500</div>
            <button @click="addRule" class="btn">{{ t('aiChatAgentSetup.rules.addRuleButton','Add Rule') }}</button>
          </div>
          <div class="space-y-2" style="min-height: 300px;">
            <div v-for="(rule, index) in rules" :key="index" class="flex items-center space-x-2 p-2 bg-base-200 rounded-lg">
              <span v-if="editingRuleIndex !== index" class="flex-1" @click="startEditingRule(index)">{{ rule }}</span>
              <textarea v-else ref="editingRuleInput" v-model="rules[index]" maxlength="3500" rows="1" class="textarea textarea-bordered flex-1 resize-none overflow-hidden" :style="getDirStyle('editingRule')" @keydown="handleDirKeydown($event, 'editingRule')" @input="($event.target.style.height='auto',$event.target.style.height=$event.target.scrollHeight+'px')" @blur="stopEditingRule" @keyup.enter="stopEditingRule" />
              <button @click="removeRule(index)" class="btn btn-sm btn-error">{{ t('aiChatAgentSetup.rules.removeRuleButton','Remove') }}</button>
            </div>
          </div>
        </div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(5, -1)" class="btn">
            <span v-if="isNavigating === 4" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(5, 1)" class="btn">
              <span v-if="isNavigating === 6" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      <input type="radio" data-currentPage="6" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.avatars', 'Avatars')" v-show="false" />
      <div role="tabpanel" data-currentPage="6" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh; display: none;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ t('aiChatAgentSetup.avatars.title', 'Avatar Configuration') }}
          </h1>
        </div>
        <div class="mt-4">
          <AvatarProvidersPanel
            :selected-provider="selectedAvatarProvider"
            @update:selected-provider="selectedAvatarProvider = $event"
            v-model="avatarProviderConfigs"
          />
        </div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(6, -1)" class="btn">
            <span v-if="isNavigating === 5" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(6, 1)" class="btn">
              <span v-if="isNavigating === 7" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      <template v-if="agentMode !== 'Flow' && agentMode !== 'FlowBased'">
        <input type="radio" data-currentPage="7" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.toolsApi', 'Tools/API')" />
        <div role="tabpanel" data-currentPage="7" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }" style="margin-bottom: 0%;">{{t('aiChatAgentSetup.toolsApi.title', 'Tools & API Configuration')}}</h1>
          </div>
          <div class="mt-4">
            <div class="mb-4">
              <div class="flex justify-between items-center mb-2">
                <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.toolsApi.availableToolsLabel', 'Available Tools:') }}</p>
                <div class="flex space-x-2">
                  <button @click="refreshTools" class="btn btn-sm btn-info">{{ t('aiChatAgentSetup.toolsApi.refreshButton', 'Refresh') }}</button>
                  <router-link to="/aiAgentTools" class="btn btn-sm">{{ t('aiChatAgentSetup.toolsApi.manageToolsButton', 'Manage Tools') }}</router-link>
                </div>
              </div>
              <div class="mb-4">
                <div class="flex space-x-2">
                  <CustomDropdownSelector id="availableToolsSelect" v-model="selectedTool" class="w-full" :options="[{ value: '', label: t('aiChatAgentSetup.toolsApi.selectToolPlaceholder', 'Select a tool to add') }, ...availableTools.map(tool => ({ value: tool.toolid, label: isDefinedAndNotEmpty(tool.toolname) !== false ? tool.toolname : tool.toolName }))]" />
                  <button @click="addSelectedTool" class="btn">{{ t('aiChatAgentSetup.toolsApi.addToolButton','Add Tool') }}</button>
                </div>
              </div>
              <div class="mb-2">
                <span class="text-sm text-gray-500" :style="{ direction: 'ltr', textAlign: '' }">{{ t('aiChatAgentSetup.toolsApi.selectedToolsCount', { count: selectedToolIds.length }, selectedToolIds.length + ' Selected Tools') }}</span>
              </div>
              <div class="space-y-4">
                <div v-for="(tool, index) in tools" :key="tool.toolid" class="card bg-base-200">
                  <div class="card-body">
                    <div class="flex justify-between items-start">
                      <div class="flex-1">
                        <h3 class="card-title font-bold" :style="{ direction: 'rtl', textAlign: 'right' }">{{ t('aiChatAgentSetup.toolsApi.functionNameLabel', 'Function Name:') }} {{ tool.toolname }}</h3>
                        <div class="mt-2">
                          <div v-if="tool.tooldata !== undefined && tool.tooldata !== null" class="space-y-2">
                            <div v-if="tool.tooldata.description !== undefined && tool.tooldata.description !== null && tool.tooldata.description.length > 0" class="text-sm">
                              <span class="font-semibold hidden" :style="{ direction: 'rtl', textAlign: 'right' }">{{ t('aiChatAgentSetup.toolsApi.descriptionLabel', 'Description:') }}</span>
                              <p class="text-gray-600 hidden" :style="{ direction: 'rtl', textAlign: 'right' }">{{ tool.tooldata.description }}</p>
                            </div>
                            <div v-if="tool.tooldata.requiredParams !== undefined && tool.tooldata.requiredParams !== null && tool.tooldata.requiredParams.length > 0" class="text-sm">
                              <span class="font-semibold" :style="{ direction: 'rtl', textAlign: 'right' }">{{ t('aiChatAgentSetup.toolsApi.parametersLabel', 'Parameters:') }}</span>
                              <ul class="list-disc list-inside text-gray-600" :style="{ direction: 'rtl', textAlign: 'right' }">
                                <li v-for="(param, paramIndex) in tool.tooldata.requiredParams" :key="paramIndex" :style="{ direction: 'rtl', textAlign: 'right' }">
                                  {{ param }}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                      <button @click="removeTool(index)" class="btn btn-sm btn-error ml-4">{{ t('aiChatAgentSetup.toolsApi.removeButton','Remove') }}</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="space-y-2" style="min-height: 300px;"></div>
            </div>
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(7, -1)" class="btn">
              <span v-if="isNavigating === 6" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
              </button>
              <button @click="createAgent(true)" class="btn btn-accent">
                <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
              </button>
            </div>
          </div>
        </div>
      </template>
  
      <input type="radio" data-currentPage="8" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.analysis', 'Analysis')" />
      <div role="tabpanel" data-currentPage="8" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between items-center mb-6" id="tabTitle">
          <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{t('aiChatAgentSetup.analysis.title', 'Analysis Configuration')}}</h1>
        </div>
        <div>
          <p class="label cursor-pointer text-lg font-medium" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.analysis.selectTagCollectionsLabel', 'Select Tag Collections') }}</p>
          <p class="text-sm text-gray-500 mb-4" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.analysis.selectTagCollectionsDescription', 'Select relevant tag collections to be used for analysis. Only collections linked to the agent\'s selected divisions are shown.') }}</p>
          
          <div v-if="selectedDivisionIds.length === 0" class="text-center text-gray-500 p-6 border border-dashed rounded-md bg-base-200" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ t('aiChatAgentSetup.analysis.noDivisionsSelected', 'Please select at least one division in the \'Agent Information\' tab to see available tag collections.') }}
          </div>
          <div v-else-if="isLoadingCollections === true" class="text-center py-6">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
           <div v-else-if="availableCollectionsForAgent.length === 0 && selectedDivisionIds.length > 0" class="text-center text-gray-500 p-6 border border-dashed rounded-md bg-base-200" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
             {{ t('aiChatAgentSetup.analysis.noCollectionsFound', 'No tag collections found linked to the selected division(s).') }}
          </div>
          <div v-else class="border rounded-md p-4 bg-base-200 dark:bg-gray-700 max-h-80 overflow-y-auto space-y-1" style="margin-bottom: 60px;">
            <label v-for="collection in availableCollectionsForAgent" :key="collection.collectionId" class="label cursor-pointer hover:bg-base-300 dark:hover:bg-gray-600 rounded-md px-3 py-2">
              <span class="label-text font-medium">{{ collection.collectionName }}</span> 
              <input type="checkbox" 
                     :value="collection.collectionId" 
                     v-model="selectedAnalysisCollectionIds" 
                     class="checkbox checkbox-primary" />
            </label>
          </div>
        </div>
  
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(8, -1)" class="btn">
              <span v-if="isNavigating === 7" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
           <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
              </button>
              <button @click="createAgent(true)" class="btn btn-accent">
                 <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
              </button>
            </div>
        </div>
      </div>
  
      <input type="radio" data-currentPage="9" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.advanced', 'Advanced')" />
      <div role="tabpanel" data-currentPage="9" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <AdvancedTab
          :languages="languages"
          :isRtl="isRtl"
          v-model:retrieveUserLocation="retrieveUserLocation"
          v-model:enableFileUploadInChat="enableFileUploadInChat"
          v-model:enableUserFineTune="enableUserFineTune"
          v-model:inappropriateContentMessage="inappropriateContentMessage"
          v-model:dontKnowMessage="dontKnowMessage"
          v-model:makeLinksClickable="makeLinksClickable"
          v-model:clearSessionOnRefresh="clearSessionOnRefresh"
          v-model:enabledSavedSessionHistory="enabledSavedSessionHistory"
          v-model:showMap="showMap"
          v-model:limitReachedMessage="limitReachedMessage"
          v-model:messageLimitPerConversation="messageLimitPerConversation"
          v-model:messageLimitPerIP="messageLimitPerIP"
          v-model:limitMessagesPerConversationEnabled="limitMessagesPerConversationEnabled"
          v-model:limitMessagesPerIPEnabled="limitMessagesPerIPEnabled"
          v-model:enablePIIRedaction="enablePIIRedaction"
          v-model:redactEmails="redactEmails"
          v-model:redactPhoneNumbers="redactPhoneNumbers"
          v-model:verifyLinks="verifyLinks"
          v-model:customBlockRegex="customBlockRegex"
          v-model:retrieverKValue="retrieverKValue"
          v-model:maxOutputTokens="maxOutputTokens"
          v-model:actionButtons="actionButtons"
          v-model:whatsappClosingMessage="whatsappClosingMessage"
          v-model:verifyAnswerEnabled="verifyAnswerEnabled"
          v-model:selectedVectorDBId="selectedVectorDBId"
          v-model:selectedEmbeddingModelId="selectedEmbeddingModelId"
          v-model:allowCallControl="allowCallControl"
          :hideEmbeddingSelector="hideEmbeddingSelector"
          :showTransferActions="true"
          @export-agent="openTransferModal('export')"
          @import-agent="openTransferModal('import')"
        />
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(9, -1)" class="btn">
            <span v-if="isNavigating === 8" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
            </button>
          </div>
        </div>
      </div>
  
      <input type="radio" data-currentPage="10" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.guardrails', 'Guardrails')" />
      <div role="tabpanel" data-currentPage="10" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <SecuritySettings
          :languages="languages"
          v-model:inappropriateContentMessage="inappropriateContentMessage"
          v-model:enablePIIRedaction="enablePIIRedaction"
          v-model:redactEmails="redactEmails"
          v-model:redactPhoneNumbers="redactPhoneNumbers"
          v-model:verifyLinks="verifyLinks"
          v-model:verifyAnswerEnabled="verifyAnswerEnabled"
          v-model:customBlockRegex="customBlockRegex"
          v-model:limitReachedMessage="limitReachedMessage"
          v-model:messageLimitPerConversation="messageLimitPerConversation"
          v-model:messageLimitPerIP="messageLimitPerIP"
          v-model:limitMessagesPerConversationEnabled="limitMessagesPerConversationEnabled"
          v-model:limitMessagesPerIPEnabled="limitMessagesPerIPEnabled"
          v-model:authenticatorEnabled="authenticatorEnabled"
          v-model:authenticatorProvider="authenticatorProvider"
          v-model:authenticatorWelcomeMessage="authenticatorWelcomeMessage"
          v-model:authenticatorRetryMessage="authenticatorRetryMessage"
          v-model:authenticatorSuccessMessage="authenticatorSuccessMessage"
          v-model:authenticatorRunAiToolOnLogin="authenticatorRunAiToolOnLogin"
          :disable-verify-answer-toggle="isFastOrSuperFastMode === true"
        />
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(10, -1)" class="btn">
            <span v-if="isNavigating === 9" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
            </button>
          </div>
        </div>
      </div>

      <input type="radio" data-currentPage="12" name="my_tabs_2" role="tab" class="tab" aria-label="Question and Answers" />
      <div role="tabpanel" data-currentPage="12" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between">
          <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">Question and Answers</h1>
        </div>
        <div class="mt-4">
          <textarea v-model="agentQnA" class="textarea textarea-bordered w-full" style="height: 300px;" :placeholder="'Add question and answer pairs here to be included in the system prompt.'"></textarea>
        </div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(12, -1)" class="btn">
            <span v-if="isNavigating === 11" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
            </button>
          </div>
        </div>
      </div>

      <template v-if="showVoiceAgentTab === true">
        <input type="radio" data-currentPage="11" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.voiceAgent','Voice Persona')" v-show="false" />
        <div role="tabpanel" data-currentPage="11" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh; display: none;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceAgent.title','Voice Persona') }}</h1>
          </div>
          <div class="mt-4">
            <div class="mb-4 w-90">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceAgent.additionalPrompt','Additional Voice Prompt') }}:</p>
              <textarea v-model="voiceAgentExtraPrompt" class="textarea textarea-bordered w-100" style="width: 100%;height: 120px;" :style="getDirStyle('voiceAgentExtraPrompt')" :placeholder="'Optional additional prompt to append to the agent description for voice calls'" @keydown="handleDirKeydown($event, 'voiceAgentExtraPrompt')"></textarea>
            </div>
            <div class="form-control w-90 mb-4">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceAgent.voice','Voice') }}:</span>
              </label>
              <CustomVoiceSelector
                v-model="voiceSettingsVoiceId"
                :accent="voiceSettingsAccent"
                :accentEnabled="true"
                :agentLanguage="voiceSettingsLanguage"
                @update:accent="voiceSettingsAccent = $event"
              />
            </div>
            <div class="form-control w-90 mb-4">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.language','Language') }}:</span>
              </label>
              <CustomLanguageSelector
                v-model="voiceSettingsLanguage"
                :options="voiceLanguageOptions"
                :placeholder="t('aiChatAgentSetup.voiceSettings.languagePlaceholder', 'Select Language')"
              />
            </div>
            <div class="form-control w-90 mb-4">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.speed','Speed') }}:</span>
              </label>
              <input type="range" min="0.5" max="2" step="0.1" v-model.number="voiceSettingsSpeed" class="range range-primary w-full" />
              <div class="text-sm mt-1" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? { direction: 'rtl' } : { direction: 'ltr' }">{{ typeof voiceSettingsSpeed === 'number' ? voiceSettingsSpeed.toFixed(1) : voiceSettingsSpeed }}</div>
            </div>
            <div class="form-control w-90 mb-4">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.tone','Tone') }}:</span>
              </label>
              <CustomToneSelector
                v-model="voiceSettingsTone"
                :options="voiceToneOptions"
                :placeholder="t('aiChatAgentSetup.voiceSettings.tonePlaceholder', 'Select Tone')"
              />
            </div>
            <div class="form-control w-90 mb-4">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.mode', 'Communication mode') }}:</span>
              </label>
              <CustomDropdownSelector id="voiceSettingsModeSelect" v-model="voiceSettingsMode" class="w-full" :options="voiceModeOptions.map(opt => ({ value: opt.id, label: opt.label }))" />
            </div>
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(11, -1)" class="btn">
              <span v-if="isNavigating === 10" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
              </button>
              <button @click="createAgent(true)" class="btn btn-accent">
                <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
              </button>
            </div>
          </div>
        </div>
      </template>
  
    </div>
   
    <VoiceSettingsOverlayModal
      :visible="showVoiceSettingsModal === true"
      :isRtl="isRtl === true"
      :voiceType="voiceSettingsVoiceType"
      :vadEngine="voiceSettingsVadEngine"
      :selectedVoiceId="voiceSettingsVoiceId"
      :accent="voiceSettingsAccent"
      :language="voiceSettingsLanguage"
      :languageOptions="voiceLanguageOptions"
      :selectedTermsPackages="voiceSettingsTermsPackages"
      :speed="voiceSettingsSpeed"
      :promptToVoiceEnabled="voiceSettingsPromptToVoiceEnabled"
      :promptToVoicePrompt="voiceSettingsPromptToVoicePrompt"
      :tone="voiceSettingsTone"
      :toneOptions="voiceToneOptions"
      :mode="voiceSettingsMode"
      :modeOptions="voiceModeOptions"
      :cancellingMode="voiceSettingsCancellingMode"
      :cancellingModeOptions="voiceCancellingModeOptions"
      :audioProcessorEngine="voiceSettingsAudioProcessorEngine"
      :audioProcessorEngineOptions="voiceAudioProcessorEngineOptions"
      :idleAutoResponseEnabled="voiceSettingsIdleAutoResponseEnabled"
      :startingMessageIdleAutoResponseSeconds="voiceSettingsStartingMessageIdleAutoResponseSeconds"
      :idleAutoResponseSeconds="voiceSettingsIdleAutoResponseSeconds"
      :toolUseAutoResponseSeconds="voiceSettingsToolUseAutoResponseSeconds"
      :transcriptionOptimizerEnabled="voiceSettingsTranscriptionOptimizerEnabled"
      :transcriptionOptimizerTerms="voiceSettingsTranscriptionOptimizerTerms"
      :transcriptionOptimizerSourcePrompt="transcriptionOptimizerSourcePrompt"
      @update:voiceType="voiceSettingsVoiceType = $event"
      @update:vadEngine="voiceSettingsVadEngine = $event"
      @update:selectedVoiceId="voiceSettingsVoiceId = $event"
      @update:accent="voiceSettingsAccent = $event"
      @update:language="voiceSettingsLanguage = $event"
      @update:selectedTermsPackages="voiceSettingsTermsPackages = $event"
      @update:selectedTerms="voiceSettingsSonioxTerms = $event"
      @update:speed="voiceSettingsSpeed = $event"
      @update:promptToVoiceEnabled="voiceSettingsPromptToVoiceEnabled = $event"
      @update:promptToVoicePrompt="voiceSettingsPromptToVoicePrompt = $event"
      @update:tone="voiceSettingsTone = $event"
      @update:mode="voiceSettingsMode = $event"
      @update:cancellingMode="voiceSettingsCancellingMode = $event"
      @update:audioProcessorEngine="voiceSettingsAudioProcessorEngine = $event"
      @update:idleAutoResponseEnabled="voiceSettingsIdleAutoResponseEnabled = $event"
      @update:startingMessageIdleAutoResponseSeconds="voiceSettingsStartingMessageIdleAutoResponseSeconds = $event"
      @update:idleAutoResponseSeconds="voiceSettingsIdleAutoResponseSeconds = $event"
      @update:toolUseAutoResponseSeconds="voiceSettingsToolUseAutoResponseSeconds = $event"
      @update:transcriptionOptimizerEnabled="voiceSettingsTranscriptionOptimizerEnabled = $event"
      @update:transcriptionOptimizerTerms="voiceSettingsTranscriptionOptimizerTerms = $event"
      @close="closeVoiceSettingsModal"
      @confirm="confirmVoiceSettings"
    />
   
    <dialog :open="showCreateFolderModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.createFolder.title', 'Create New Folder') }}</h3>
        <div class="py-4">
          <p class="mb-2" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }" v-html="createFolderPromptText"></p>
          <input type="text" v-model="newFolderName" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.createFolder.folderNamePlaceholder', 'Folder name')" />
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showCreateFolderModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="handleCreateFolder">{{ t('aiChatAgentSetup.modals.createFolder.createButton', 'Create') }}</button>
        </div>
      </div>
       <form method="dialog" class="modal-backdrop">
        <button @click="showCreateFolderModal = false">close</button>
      </form>
    </dialog>
   
    <dialog :open="showMoveFileModal === true" class="modal">
      <div class="modal-box" v-if="fileToMove !== null">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }" v-html="t('aiChatAgentSetup.modals.moveFile.title', 'Move File: <span class=\'font-normal\'>{fileName}</span>', { fileName: decodeURIComponentSafe(fileToMove.fileName) })"></h3>
        <div class="py-4">
          <label class="label" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            <span class="label-text">{{ t('aiChatAgentSetup.modals.moveFile.destinationLabel', 'Select destination folder:') }}</span>
          </label>
          <CustomDropdownSelector id="moveFileTargetFolderSelect" v-model="targetFolderForMove" class="w-full" :options="[{ value: null, label: t('aiChatAgentSetup.modals.moveFile.rootDestination', 'Knowledge Base (Root)') }, ...knowledgeBaseFolders.map(folder => ({ value: folder.id, label: folder.name }))]" />
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showMoveFileModal = false; fileToMove = null;">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="handleMoveFile">{{ t('aiChatAgentSetup.modals.moveFile.moveButton', 'Move File') }}</button>
        </div>
      </div>
       <form method="dialog" class="modal-backdrop">
        <button @click="showMoveFileModal = false; fileToMove = null;">close</button>
      </form>
    </dialog>
   
    <dialog :open="showWebsiteLinkModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addWebsiteLink.title', 'Add Website Link') }}</h3>
        <div class="py-4">
          <input type="url" v-model="websiteLink" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.addWebsiteLink.urlPlaceholder', 'Enter website URL')" :style="getDirStyle('websiteLink')" @keydown="handleDirKeydown($event, 'websiteLink')" />
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showWebsiteLinkModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="addWebsiteLink">{{ t('aiChatAgentSetup.modals.addWebsiteLink.addButton', 'Add') }}</button>
        </div>
      </div>
    </dialog>
    <dialog :open="showWebsiteBatchModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addWebsiteBatch.title', 'Add Website Batch URLs') }}</h3>
        <div class="py-4">
          <textarea v-model="websiteBatchText" class="textarea textarea-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.addWebsiteBatch.placeholder', 'Enter one URL per line')" style="min-height: 200px;" :style="getDirStyle('websiteBatchText')" @keydown="handleDirKeydown($event, 'websiteBatchText')"></textarea>
          <div class="mt-4 md:flex">
            <label class="label cursor-pointer flex items-center md:me-3">
              <input type="checkbox" class="toggle toggle-accent" v-model="rescrapeEnabled" />
              <span class="label-text ms-2">Enable Rescrape</span>
            </label>
            <div class="md:me-3 mt-3 md:mt-0">
              <label class="label"><span class="label-text">Interval (days)</span></label>
              <input type="number" class="input input-bordered w-full" v-model="rescrapeIntervalDays" min="1" :disabled="rescrapeEnabled !== true" />
            </div>
            <div class="mt-3 md:mt-0">
              <label class="label"><span class="label-text">Hour (0-23, UTC)</span></label>
              <input type="number" class="input input-bordered w-full" v-model="rescrapeAtHour" min="0" max="23" :disabled="rescrapeEnabled !== true" />
            </div>
          </div>
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showWebsiteBatchModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="addWebsiteBatchUrls">{{ t('aiChatAgentSetup.modals.addWebsiteBatch.addButton', 'Add URLs') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="showWebsiteBatchModal = false">close</button>
      </form>
    </dialog>
    <dialog :open="showWebsiteScrapeModal === true" class="modal">
      <div class="modal-box text-center">
        <h3 class="font-bold text-lg">{{ t('aiChatAgentSetup.modals.scrapeWebsite.title', 'Scrape Entire Website') }}</h3>
        <div class="py-4">
          <input type="url" v-model="websiteToScrape" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.scrapeWebsite.urlPlaceholder', 'Enter website URL to scrape')" :style="getDirStyle('websiteToScrape')" @keydown="handleDirKeydown($event, 'websiteToScrape')" />
          <div class="mt-4">
            <label class="label" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
              <span class="label-text">{{ t('aiChatAgentSetup.modals.scrapeWebsite.maxPagesLabel', 'Max Pages (optional)') }}</span>
            </label>
            <input type="number" v-model="maxPages" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.scrapeWebsite.maxPagesPlaceholder', 'Leave empty for unlimited')" :style="getDirStyle('maxPages')" @keydown="handleDirKeydown($event, 'maxPages')" />
          </div>
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showWebsiteScrapeModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="scrapeWebsite">{{ t('aiChatAgentSetup.modals.scrapeWebsite.scrapeButton', 'Start Scraping') }}</button>
        </div>
      </div>
    </dialog>
    <dialog :open="showTextInputModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addTextContent.title', 'Add Text Content') }}</h3>
        <div class="py-4">
          <input type="text" v-model="textFileName" maxlength="255" class="input input-bordered w-full mb-4" :placeholder="t('aiChatAgentSetup.modals.addTextContent.fileNamePlaceholder', 'Enter file name')" :style="getDirStyle('textFileName')" @keydown="handleDirKeydown($event, 'textFileName')" />
          <div class="text-xs text-right pr-1 mb-2 text-gray-500" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ textFileName.length }}/255</div>
          <textarea v-model="textContent" maxlength="10000" class="textarea textarea-bordered w-full" style="min-height: 200px;" :style="getDirStyle('textContent')" @keydown="handleDirKeydown($event, 'textContent')" :placeholder="t('aiChatAgentSetup.modals.addTextContent.contentPlaceholder', 'Enter your text content here')"></textarea>
          <div class="text-xs text-right pr-1 mt-1 text-gray-500" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ textContent.length }}/10000</div>
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showTextInputModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="addTextContent">{{ t('aiChatAgentSetup.modals.addTextContent.addButton', 'Add') }}</button>
        </div>
      </div>
    </dialog>
    <AgentLoadingModal
      :open="showLoadingModal !== false"
      :modalState="modalState"
      :currentTip="currentTip"
      @close="closeModal"
    />
    <dialog :open="showMaxPromptModal === true" class="modal">
      <div class="modal-box max-w-4xl shadow-2xl" style="animation: scaleIn 200ms ease-out;">
        <div class="flex items-center justify-between mb-4 pb-3 border-b border-base-300">
          <h3 class="font-bold text-xl text-base-content" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentInstructionsLabel') }}</h3>
          <button class="btn btn-sm btn-circle btn-ghost" @click="showMaxPromptModal = false" :title="t('aiChatAgentSetup.buttons.close','Close')">
            <Icon icon="lucide:x" class="w-5 h-5" />
          </button>
        </div>
        <div class="py-2">
          <textarea
            v-model="agentDescription"
            class="textarea textarea-bordered w-full text-base"
            :class="{ 'textarea-error': agentDescriptionError === true }"
            style="min-height: 500px; resize: vertical;"
            :style="getDirStyle('agentDescription')"
            :placeholder="t('aiChatAgentSetup.agentInformation.agentInstructionsPlaceholder', 'Description Prompt')"
            @keydown="handleDirKeydown($event, 'agentDescription')" @input="handleAgentDescriptionInput()"
          ></textarea>
        </div>
        <div class="modal-action mt-4 pt-3 border-t border-base-300">
          <button class="btn" @click="showMaxPromptModal = false">{{ t('aiChatAgentSetup.buttons.close','Close') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop bg-black bg-opacity-50 backdrop-blur-sm" @click="showMaxPromptModal = false">
        <button>close</button>
      </form>
    </dialog>
    <AppModal
      :visible="showPersonalityReplaceModal === true"
      :title="t('agentPersonalitySelector.replaceTitle', 'Replace Description')"
      boxClass="max-w-md"
      @close="cancelPersonalityReplace"
    >
      <template #actions>
        <button class="btn" @click="cancelPersonalityReplace">{{ t('agentPersonalitySelector.cancel', 'Cancel') }}</button>
        <button class="btn" @click="confirmPersonalityReplace">{{ t('agentPersonalitySelector.confirm', 'Replace') }}</button>
      </template>
    </AppModal>
    <dialog :open="isSwitchConfirmOpen === true" class="modal">
      <div class="modal-box text-center">
        <h3 class="font-bold text-lg">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.title', 'Confirm Mode Switch') }}</h3>
        <p class="py-4">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.prompt', 'Switching away from Flow will discard the current flow configuration. Are you sure you want to continue?') }}</p>
        <div class="modal-action space-x-2">
          <button class="btn" @click="cancelSwitchMode">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.cancelButton', 'Cancel') }}</button>
          <button class="btn btn-warning" @click="confirmSwitchMode">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.confirmButton', 'Discard Flow & Switch') }}</button>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button @click="cancelSwitchMode">close</button>
        </form>
      </div>
    </dialog>
    <InspectFile
      :is-open="isInspectModalOpen === true"
      :file="selectedFileToInspect"
      @close="closeInspectModal"
    />
    
    <CreateAgentStyleModal
      :visible="showCreateStyleModal"
      @close="showCreateStyleModal = false"
      @created="onStyleCreated"
    />
    <it-modal v-model="showScrapeReportModal" width="90%">
      <template v-slot:body>
        <div v-if="scrapeReportSource !== null">
          <WebsiteScrapeReport :agent-id="route.query.edit" :source="scrapeReportSource" :initial-open="true" :hide-toggle="true" />
          <div class="flex justify-end mt-4">
            <button class="btn" @click="closeScrapeReportModal()">{{ t('aiChats.knowledgeStatusModal.closeButton','Close') }}</button>
          </div>
        </div>
      </template>
    </it-modal>
    <dialog :open="showSharedKbPicker === true" class="modal">
      <div class="modal-box max-w-lg">
        <h3 class="font-bold text-lg mb-4">{{ t('aiChatAgentSetup.modals.linkSharedKb.title','Link Shared Knowledge Bases') }}</h3>
        <CustomDropdownSelector id="sharedKnowledgeBasesSelect" v-model="selectedSharedKbIds" :multiple="true" class="w-full" :options="sharedKnowledgeBases.map(kb => ({ value: kb.kbId, label: kb.name }))" dropdownStyle="max-height: 200px; overflow-y: auto;" />
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="confirmAddSharedKbs">{{ t('aiChatAgentSetup.modals.linkSharedKb.addButton','Add') }}</button>
          <button class="btn" @click="showSharedKbPicker = false">{{ t('aiChatAgentSetup.modals.linkSharedKb.closeButton','Close') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="showSharedKbPicker = false">close</button>
      </form>
    </dialog>
    <dialog :open="transferModalOpen === true" class="modal">
      <div class="modal-box max-w-lg rounded-2xl shadow-2xl border border-base-300">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
          {{ transferModalMode === 'import' ? t('aiChatAgentSetup.knowledgeBase.advanced.importAgentZip', 'Import Agent') : t('aiChatAgentSetup.knowledgeBase.advanced.exportAgentZip', 'Export Agent') }}
        </h3>
        <p class="py-2 text-sm opacity-80" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
          {{ transferModalMode === 'import' ? t('aiChatAgentSetup.knowledgeBase.advanced.importAgentModalDescription', 'Pick a zip and import to this agent.') : t('aiChatAgentSetup.knowledgeBase.advanced.exportAgentModalDescription', 'We prepare the export in the background and start download automatically.') }}
        </p>
        <div v-if="transferModalMode === 'import'" class="form-control w-full mt-2">
          <input type="file" accept=".zip,application/zip" class="file-input file-input-bordered w-full" @change="onTransferImportFileChange" :disabled="transferModalBusy === true" />
          <div class="text-xs mt-2 opacity-70" v-if="transferImportFileName.length > 0">{{ transferImportFileName }}</div>
        </div>
        <div class="text-error text-sm mt-3" v-if="transferModalError.length > 0">{{ transferModalError }}</div>
        <div class="text-success text-sm mt-3" v-if="transferModalSuccess.length > 0">{{ transferModalSuccess }}</div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="closeTransferModal" :disabled="transferModalBusy === true">
            {{ transferModalSuccess.length > 0 ? t('aiChatAgentSetup.buttons.close','Close') : t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}
          </button>
          <button class="btn" @click="confirmTransferModalAction" :disabled="transferModalBusy === true || (transferModalMode === 'import' && transferImportFile === null)">
            <span v-if="transferModalBusy === true" class="loading loading-spinner loading-sm mr-2"></span>
            {{ transferModalMode === 'import' ? t('aiChatAgentSetup.knowledgeBase.advanced.importAgentZip', 'Import Agent') : t('aiChatAgentSetup.knowledgeBase.advanced.exportAgentZip', 'Export Agent') }}
          </button>
        </div>
      </div>
    </dialog>
  </ThemeOnly>
  </template>
  
  <script setup>
  import { ref, onMounted, onBeforeUnmount, reactive, computed, nextTick, watch } from 'vue';
  import axios from '../api/axiosInstance';
  import router from '@/router';
  import { useRoute } from 'vue-router';
  import { globalState } from '@/globalState';
  import ThemeOnly from '@/components/ThemeOnly.vue';
  import ModernAIChatAgentSetup from '@/Themes/modern/views/AIChatAgentSetup.vue';
  import { fetchUserData } from '@/api/accountsService';
  import FineTuneAgent from '../components/FineTuneAgent.vue';
  import LLMFlow from '../components/LLMFlow.vue';
  import InspectFile from '@/components/InspectFile.vue';
  import { useToast } from 'vue-toastification';
  import { v4 as uuidv4 } from 'uuid';
  import { Icon } from "@iconify/vue/dist/offline";
  import FlowPreviewChat from '../components/FlowPreviewChat.vue'; 
  import FlowBuilderAgent from '../components/FlowBuilderAgent.vue'; 
  import LLMSelector from '../components/LLMSelector.vue';
  import AgentModeSelector from '../components/AgentModeSelector.vue';
  import AgentTypeSelector from '../components/AgentTypeSelector.vue';
  import AgentToneSelector from '../components/AgentToneSelector.vue';
  import AgentPersonalitySelector from '../components/AgentPersonalitySelector.vue';
  import ToolTip from "../components/Tooltip.vue";
  import { useI18n, applyTranslationsToDOM } from '@/i18n';
  import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
  import AgentLoadingModal from '@/components/AgentLoadingModal.vue';
  import DivisionSelect from '../components/DivisionSelect.vue';
  import AgentStyleSelect from '../components/AgentStyleSelect.vue';
  import WebsiteScrapeReport from '@/components/WebsiteScrapeReport.vue';
  import CreateAgentStyleModal from '../components/CreateAgentStyleModal.vue';
  import AdvancedTab from '../components/AdvancedTab.vue';
  import DeploymentChannelsOptionsSelector from '../components/DeploymentChannelsOptionsSelector.vue';
  import SecuritySettings from '../components/SecuritySettings.vue';
  import CustomVoiceSelector from '../components/CustomVoiceSelector.vue';
  import CustomLanguageSelector from '../components/CustomLanguageSelector.vue';
  import CustomDropdownSelector from '../components/CustomDropdownSelector.vue';
  import CustomToneSelector from '../components/CustomToneSelector.vue';
  import CustomModeSelector from '../components/CustomModeSelector.vue';
  import VoiceSettingsOverlayModal from '../components/VoiceSettingsOverlayModal.vue';
  import AvatarProvidersPanel from '../components/AvatarProvidersPanel.vue';
  import RuleLibraryPanel from '../components/RuleLibraryPanel.vue';
  import { defaultVoiceId, getVoiceOptionById, fetchVoices } from '../utils/voiceCatalog.js';
  import { geminiLanguageNames } from '../utils/geminiLanguages.js';
  
  
  const toast = useToast();
  const route = useRoute();
  const { t, currentLanguage } = useI18n();
  const aiAgentSetupRoot = ref(null);
  let isActive = true;
  onBeforeUnmount(() => {
    isActive = false;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  });
  const agentActive = ref(true);
  const agentName = ref('');
  const agentDescription = ref('');
  const agentQnA = ref('');
  const selectedAgentTone = ref('default');
  const selectedAgentPersonality = ref('');
  const pendingPersonalitySelection = ref(null);
  const showPersonalityReplaceModal = ref(false);
  const agentShortDescription = ref('');
  const startingMessage = ref({});
  const agentLanguage = ref('auto');
  const organizationID = ref(null);
  const knowledgeBaseRef = ref(null);
  const currentPage = ref(1);
  const apiEndpoint = reactive(globalState.apiEndpoint);
  const isSuccessModalOpen = ref(false);
  const successMessage = ref('');
  const isErrorModalOpen = ref(false);
  const errorMessage = ref('');
  const rules = ref([]);
  const newRule = ref('');
  const tools = ref([]);
  const isEditMode = ref(false);
  const uploadedFiles = ref([]);
  const existingFiles = ref([]);
  const autoLlmModelId = 'auto-llm';
  const autoFastModeModelId = ref(null);
  const autoSuperFastModeModelId = ref(null);
  const autoVerificationModelId = ref(null);
  const selectedLLMModel = ref(null);
  const selectedVerificationLLMModel = ref('');
  const availableLLMModels = ref([]);
  const orgPolicies = ref({});
  const llmLockedId = computed(() => {
    const id = orgPolicies.value?.features?.lock?.llmModelId;
    return typeof id === 'string' && id !== '' ? id : null;
  });
  const hideEmbeddingSelector = computed(() => orgPolicies.value?.features?.hide?.embeddingSelector === true);
  const enableFeedbackButtons = ref(true);
  const enableSuggestedResponses = ref(false);
  const enableStreamingText = ref(true);
  const enableUserFineTune = ref(false);
    const whatsappClosingMessage = ref('');
  const selectedDeploymentChannels = ref([]);
  const selectedWhatsappSourceId = ref(null);
  const whatsappDisplayNumber = ref('');
  const selectedPhoneSourceId = ref(null);
  const phoneDisplayNumber = ref('');
  const agentType = ref('PublicAgent');
  const retrieveUserLocation = ref(false);
  const enableFileUploadInChat = ref(false); 
  const makeLinksClickable = ref(true);
  const clearSessionOnRefresh = ref(false);
  const enabledSavedSessionHistory = ref(false);
  const defaultLimitReachedMessageTemplate = 'You have reached the maximum messages for this conversation. Start a new chat to continue.';
  const showMap = ref(true);
  const limitReachedMessage = ref(defaultLimitReachedMessageTemplate);
  const messageLimitPerConversation = ref(0);
  const messageLimitPerIP = ref(0);
  const limitMessagesPerConversationEnabled = ref(false);
  const limitMessagesPerIPEnabled = ref(false);
  const allowCallControl = ref(false);
  const enablePIIRedaction = ref(false);
  const redactEmails = ref(true);
  const redactPhoneNumbers = ref(false);
  const customBlockRegex = ref([]);
  const retrieverKValue = ref(10);
  const maxOutputTokens = ref(2048);
  const actionButtons = ref([]);
  const verifyAnswerEnabled = ref(false);
  const verifyLinks = ref(true);
  const selectedVectorDBId = ref('');
  const selectedEmbeddingModelId = ref('');
  const defaultAuthenticatorWelcomeTemplate = 'Welcome! Please sign in at {{authURL}} to continue.';
  const defaultAuthenticatorRetryTemplate = 'You must login via {{authURL}} to continue.';
  const defaultAuthenticatorSuccessTemplate = 'You are signed in. Lets talk.';
  const authenticatorEnabled = ref(false);
  const authenticatorProvider = ref('google');
  const authenticatorWelcomeMessage = ref(defaultAuthenticatorWelcomeTemplate);
  const authenticatorRetryMessage = ref(defaultAuthenticatorRetryTemplate);
  const authenticatorSuccessMessage = ref(defaultAuthenticatorSuccessTemplate);
  const authenticatorRunAiToolOnLogin = ref(false);
  const voiceAgentExtraPrompt = ref('');
  const voiceAgentProvider = ref('openai-realtime');
  const voiceAgentVoice = ref('alloy');
  const voiceProviders = [
    { id: 'openai-realtime', name: 'OpenAI GPT Real Time' },
    { id: 'openai-mini', name: 'OpenAI GPT Mini' },
    { id: 'gemini-live-flash', name: 'Gemini Live Flash' },
    { id: 'gemini-live-pro', name: 'Gemini Live Pro' }
  ];
  const availableVoicesByProvider = {
    'openai-realtime': ['alloy','verse','aria','sage'],
    'openai-mini': ['alloy','verse','aria','sage'],
    'gemini-live-flash': ['classic','bright','warm'],
    'gemini-live-pro': ['classic','bright','warm']
  };
  const selectedPhoneVoiceId = ref(defaultVoiceId);
  const defaultPromptToVoicePrompt = "# Audio Profile\nA helpful and professional personal assistant.\n\n# Director's note\nStyle: Clear, warm, natural, and phone-friendly. Pace: Natural. Accent: Match the selected language.\n\n## Scene:\nA quiet, professional remote workspace.\n\n## Sample Context:\nSteady, efficient, and unhurried. Tone is empathetic, crisp, and reassuring.";
  const voiceSettingsVoiceType = ref('vtv');
  const voiceSettingsVadEngine = ref('tiger');
  const voiceSettingsVoiceId = ref('');
  const voiceSettingsAccent = ref('Israeli');
  const voiceSettingsLanguage = ref('English');
  const voiceSettingsSpeed = ref(1);
  const voiceSettingsPromptToVoiceEnabled = ref(false);
  const voiceSettingsPromptToVoicePrompt = ref(defaultPromptToVoicePrompt);
  const voiceSettingsTone = ref('Neutral');
  const voiceSettingsMode = ref('interruptible');
  const voiceSettingsCancellingMode = ref('fast');
  const voiceSettingsAudioProcessorEngine = ref('quail');
  const voiceSettingsIdleAutoResponseEnabled = ref(true);
  const voiceSettingsStartingMessageIdleAutoResponseSeconds = ref(6);
  const voiceSettingsIdleAutoResponseSeconds = ref(3);
  const voiceSettingsToolUseAutoResponseSeconds = ref(2);
  const voiceSettingsTermsPackages = ref([]);
  const voiceSettingsSonioxTerms = ref([]);
  const voiceSettingsTranscriptionOptimizerEnabled = ref(false);
  const voiceSettingsTranscriptionOptimizerTerms = ref([]);
  const transcriptionOptimizerSourcePrompt = computed(() => {
    const localizedStartingMessages = startingMessage.value !== null && typeof startingMessage.value === 'object'
      ? Object.values(startingMessage.value).filter((value) => typeof value === 'string' && value.trim() !== '')
      : [];
    return [
      agentDescription.value,
      voiceAgentExtraPrompt.value,
      agentQnA.value,
      ...localizedStartingMessages
    ].filter((value) => typeof value === 'string' && value.trim() !== '').join('\n\n');
  });
  const voiceLanguageOptions = geminiLanguageNames;
  const voiceToneOptions = ['Neutral','Friendly','Formal','Enthusiastic','Calm'];
  const voiceModeOptions = [
    { id: 'interruptible', label: 'Talk and interrupt' },
    { id: 'wait full', label: 'Wait for full answer' }
  ];
  const voiceCancellingModeOptions = [
    { id: 'fast', label: 'Fast Mode' },
    { id: 'quality', label: 'Quality Mode' }
  ];
  const voiceAudioProcessorEngineOptions = [
    { id: 'quail', label: 'Quail VF' },
    { id: 'internal', label: 'Genovise' },
    { id: 'external', label: 'External engine' }
  ];
  const showVoiceSettingsModal = ref(false);
  const forceShowVoiceTab = ref(false);
  const hasInitializedPhoneSelection = ref(false);
  function normalizeVoiceSettingsCancellingMode(value) {
    if (typeof value !== 'string') {
      return 'fast';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'quality') {
      return 'quality';
    }
    if (normalized === 'qualitymode') {
      return 'quality';
    }
    return 'fast';
  }
  function normalizeVoiceSettingsAudioProcessorEngine(value) {
    if (typeof value !== 'string') {
      return 'quail';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'quail' || normalized === 'quailvf' || normalized === 'aic' || normalized === 'aicoustics') {
      return 'quail';
    }
    if (normalized === 'internal' || normalized === 'internalengine' || normalized === 'genovise' || normalized === 'genoviseengine') {
      return 'internal';
    }
    if (normalized === 'external' || normalized === 'externalengine' || normalized === 'krisp' || normalized === 'krispvoicefocus') {
      return 'external';
    }
    return 'quail';
  }
  function normalizeVoiceSettingsVoiceType(value) {
    if (typeof value !== 'string') {
      return 'vtv';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'hybrid') {
      return 'hybrid';
    }
    if (normalized === 'vtvwithtts') {
      return 'hybrid';
    }
    if (normalized === 'realtimewithtts') {
      return 'hybrid';
    }
    if (normalized === 'vllm') {
      return 'vllm';
    }
    if (normalized === 'realtimewithcustomsttopenai' || normalized === 'openairealtimecustomstt') {
      return 'realtimeWithCustomSTTOpenAI';
    }
    if (normalized === 'realtimewithcustomsttdefault') {
      return 'realtimeWithCustomSTTDefault';
    }
    return 'vtv';
  }
  function normalizeVoiceSettingsVadEngine(value) {
    if (typeof value !== 'string') {
      return 'tiger';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'flyingbird') {
      return 'flyingBird';
    }
    return 'tiger';
  }
  function defaultAvatarProviderConfig(providerId) {
    if (providerId === 'did') {
      return {
        presenterId: '',
        presenterName: '',
        previewUrl: ''
      };
    }
    return {};
  }
  const selectedAvatarProvider = ref('did');
  const avatarProviderConfigs = ref({
    did: defaultAvatarProviderConfig('did')
  });
  const showWebsiteLinkModal = ref(false);
  const showWebsiteScrapeModal = ref(false);
  const showScrapeReportModal = ref(false);
  const scrapeReportSource = ref(null);
  const websiteLink = ref('');
  const websiteToScrape = ref('');
  const maxPages = ref('');
  const showWebsiteBatchModal = ref(false);
  const websiteBatchText = ref('');
  const rescrapeEnabled = ref(false);
  const rescrapeIntervalDays = ref('');
  const rescrapeAtHour = ref('');
  const editingRuleIndex = ref(null);
  const editingRuleInput = ref(null);
  const showTextInputModal = ref(false);
  const textFileName = ref('');
  const textContent = ref('');
  const searchQuery = ref('');
  const availableTools = ref([]);
  const selectedToolIds = ref([]);
  const selectedTool = ref('');
  const llmFlowRef = ref(null);
  const isSaving = ref(false);
  const isNavigating = ref(null);
  const showLoadingModal = ref(false);
  const modalState = reactive({
    type: 'loading', 
    title: 'Processing...',
    message: ''
  });
  const currentTip = ref('');
  const tipInterval = ref(null);
  const agentStyles = ref([]);
  const selectedStyleGuid = ref('');
  const sharedKnowledgeBases = ref([]);
  const selectedSharedKbIds = ref([]);
  const showSharedKbPicker = ref(false);
  const linkedSharedKbItems = computed(() => {
    const ids = Array.isArray(selectedSharedKbIds.value) === true ? selectedSharedKbIds.value : [];
    const list = Array.isArray(sharedKnowledgeBases.value) === true ? sharedKnowledgeBases.value : [];
    return list.filter(kb => ids.includes(kb.kbId) === true);
  });
  const showVerificationSelector = computed(() => false);
  function confirmAddSharedKbs() {
    showSharedKbPicker.value = false;
  }
  const divisions = ref([]);
  const selectedDivisionIds = ref([]);
  const inputDirection = ref('ltr');
  const agentMode = ref('Agentic');
  const flowBasedFlow = ref(null);
  const isFastOrSuperFastMode = computed(() => {
    const currentMode = typeof agentMode.value === 'string' ? agentMode.value.toLowerCase() : '';
    return currentMode === 'fast' || currentMode === 'superfast';
  });
  const agenticBehaviour = ref(true);
  const pendingAgenticBehaviour = ref(true);
  const isSwitchConfirmOpen = ref(false);
  const targetAgentMode = ref(null);
  const activeFineTuneTab = ref('fineTune');
  const resolvedSelectedLLMModelId = computed(() => {
      if (selectedLLMModel.value === autoLlmModelId) {
      if (agentMode.value === 'SuperFast') {
        if (typeof autoSuperFastModeModelId.value === 'string' && autoSuperFastModeModelId.value.length > 0) {
          return autoSuperFastModeModelId.value;
        }
      } else if (agentMode.value === 'Fast') {
        if (typeof autoFastModeModelId.value === 'string' && autoFastModeModelId.value.length > 0) {
          return autoFastModeModelId.value;
        }
      }
    }
    return selectedLLMModel.value;
  });
  const isInspectModalOpen = ref(false);
  const selectedFileToInspect = ref(null);
  function unlinkSharedKb(kbId) {
    if (Array.isArray(selectedSharedKbIds.value) === true) {
      selectedSharedKbIds.value = selectedSharedKbIds.value.filter(id => id !== kbId);
    } else {
      selectedSharedKbIds.value = [];
    }
  }
  let loadedAgentData = null;
  let initialFlowLoadAttempted = false;
  const availableCollectionsForAgent = ref([]);
  const selectedAnalysisCollectionIds = ref([]);
  const isLoadingCollections = ref(false);
  const activeFlowPane = ref('Flow AI Agent');
  const showVoiceAgentTab = computed(() => false);
  const isPhoneChannelSelected = computed(() => Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') === true);
  const resolvedVoiceIdForCalls = computed(() => {
    const chosenVoiceId = typeof voiceSettingsVoiceId.value === 'string' && voiceSettingsVoiceId.value.length > 0 ? voiceSettingsVoiceId.value : selectedPhoneVoiceId.value;
    if (typeof chosenVoiceId === 'string' && chosenVoiceId.length > 0) {
      return chosenVoiceId;
    }
    if (typeof voiceAgentVoice.value === 'string' && voiceAgentVoice.value.length > 0) {
      return voiceAgentVoice.value;
    }
    return defaultVoiceId;
  });
  const selectedVoiceOptionForCalls = computed(() => getVoiceOptionById(resolvedVoiceIdForCalls.value));
  watch([selectedVoiceOptionForCalls, voiceSettingsLanguage], () => {
    const accentOptions = Array.isArray(selectedVoiceOptionForCalls.value?.accentOptions) === true ? selectedVoiceOptionForCalls.value.accentOptions.filter((item) => typeof item === 'string' && item.trim() !== '').map((item) => item.trim()) : [];
    if (accentOptions.length <= 0) {
      return;
    }
    const currentAccent = typeof voiceSettingsAccent.value === 'string' ? voiceSettingsAccent.value.trim().toLowerCase() : '';
    if (accentOptions.some((item) => item.toLowerCase() === currentAccent) === true) {
      return;
    }
    const currentLanguage = typeof voiceSettingsLanguage.value === 'string' ? voiceSettingsLanguage.value.trim().toLowerCase() : '';
    const matchingLanguage = accentOptions.find((item) => item.toLowerCase() === currentLanguage);
    voiceSettingsAccent.value = matchingLanguage !== undefined ? matchingLanguage : accentOptions[0];
  }, { immediate: true });
  
  const inappropriateContentMessage = ref({});
  const dontKnowMessage = ref({});
  const languages = [
        { code: 'default', name: 'Default' },
        { code: 'en', name: 'English' },
        { code: 'he', name: 'עברית' },
        { code: 'es', name: 'Spanish' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'zh', name: 'Chinese' },
        { code: 'ja', name: 'Japanese' },
        { code: 'ar', name: 'Arabic' },
        { code: 'ru', name: 'Russian' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'it', name: 'Italian' },
        { code: 'ko', name: 'Korean' },
        { code: 'hi', name: 'Hindi' },
        { code: 'sv', name: 'Swedish' },
        { code: 'nl', name: 'Dutch' },
        { code: 'da', name: 'Danish' },
        { code: 'fi', name: 'Finnish' },
        { code: 'no', name: 'Norwegian' },
        { code: 'tr', name: 'Turkish' },
        { code: 'pl', name: 'Polish' }
  ];
  watch(selectedPhoneSourceId, (value, previous) => {
    const hasValue = Number.isFinite(Number(value)) === true && value !== null;
    const valuesDiffer = value !== previous;
    if (hasInitializedPhoneSelection.value === false) {
      return;
    }
    if (hasValue === true && valuesDiffer === true) {
      showVoiceSettingsModal.value = true;
    }
  });
  watch(selectedPhoneVoiceId, (value) => {
    if (typeof value === 'string' && value.length > 0) {
      if (value !== voiceSettingsVoiceId.value) {
        voiceSettingsVoiceId.value = value;
      }
      if (value !== voiceAgentVoice.value) {
        voiceAgentVoice.value = value;
      }
    }
  });
  watch(voiceSettingsVoiceId, (value) => {
    if (typeof value === 'string' && value.length > 0) {
      if (value !== selectedPhoneVoiceId.value) {
        selectedPhoneVoiceId.value = value;
      }
      if (value !== voiceAgentVoice.value) {
        voiceAgentVoice.value = value;
      }
    }
  });
  function openVoiceSettingsModal() {
    showVoiceSettingsModal.value = true;
  }
  function closeVoiceSettingsModal() {
    showVoiceSettingsModal.value = false;
  }
  function confirmVoiceSettings() {
    if (isPhoneChannelSelected.value === true) {
      forceShowVoiceTab.value = false;
    } else {
      forceShowVoiceTab.value = true;
    }
    showVoiceSettingsModal.value = false;
  }
  const flowPreviewKey = ref(uuidv4()); 
   
   function decodeBase64UTF8(base64String) {
    if (base64String === null || base64String === undefined) return '';
    try {
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
      console.error("Error decoding base64 UTF-8 string:", error);
      toast.error("Failed to decode text content.");
      return ''; 
    }
  }
   
  function escapeHtml(value) {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  
  function sanitizeWindowOpenUrl(value) {
    if (value === undefined || value === null) {
      return null;
    }
    const raw = String(value).trim();
    if (raw.length === 0) {
      return null;
    }
    let urlObj;
    try {
      urlObj = new URL(raw, window.location.origin);
    } catch (e) {
      return null;
    }
    const protocol = typeof urlObj.protocol === 'string' ? urlObj.protocol.toLowerCase() : '';
    const isProd = process.env.environment === 'production';
    if (protocol === 'https:') {
      return urlObj.href;
    }
    if (protocol === 'http:' && isProd === false) {
      return urlObj.href;
    }
    return null;
  }
  const controlCharPattern = /[\u0000-\u0008\u000B-\u001F\u007F]/;
  const dangerousTextPattern = /(javascript:|data:|vbscript:|on\w+\s*=|<\s*\/?\s*script)/i;
  function secureUserText(raw, options = {}) {
    const allowNewLines = options.allowNewLines === true;
    const allowEmpty = options.allowEmpty === true;
    const maxLength = typeof options.maxLength === 'number' ? options.maxLength : null;
    const fieldName = typeof options.fieldName === 'string' ? options.fieldName : 'Field';
    const normalized = typeof raw === 'string' ? raw.normalize('NFKC') : '';
    const prepared = allowNewLines === true ? normalized.replace(/\r\n/g, '\n') : normalized.replace(/\s+/g, ' ');
    const trimmed = prepared.trim();
    if (trimmed.length === 0) {
      if (allowEmpty === true) {
        return { value: '', error: null };
      }
      return { value: '', error: `${fieldName} is required.` };
    }
    if (maxLength !== null && trimmed.length > maxLength) {
      return { value: '', error: `${fieldName} exceeds ${maxLength} characters.` };
    }
    if (controlCharPattern.test(trimmed) === true) {
      return { value: '', error: `${fieldName} contains disallowed control characters.` };
    }
    if (dangerousTextPattern.test(trimmed) === true) {
      return { value: '', error: `${fieldName} contains disallowed expressions.` };
    }
    if (options.disallowAngleBrackets === true && /[<>]/.test(trimmed) === true) {
      return { value: '', error: `${fieldName} cannot contain angle brackets.` };
    }
    return { value: allowNewLines === true ? trimmed : trimmed.replace(/\s+/g, ' ') , error: null };
  }
  function sanitizeLocalizationObject(map, options = {}) {
    const sanitized = {};
    if (map !== null && typeof map === 'object') {
      Object.keys(map).forEach((key) => {
        const normalizedKey = typeof key === 'string' ? key.trim() : '';
        if (normalizedKey.length === 0) {
          return;
        }
        const result = secureUserText(map[key], {
          fieldName: `${options.fieldName !== undefined ? options.fieldName : 'Value'} (${normalizedKey})`,
          maxLength: options.maxLength,
          allowNewLines: options.allowNewLines === true,
          disallowAngleBrackets: options.disallowAngleBrackets === true,
          allowEmpty: options.allowEmpty === true
        });
        if (result.error === null) {
          if (result.value.length === 0 && options.allowEmpty === true) {
            return;
          }
          sanitized[normalizedKey] = result.value;
        } else {
          sanitized.__error = result.error;
        }
      });
    }
    if (sanitized.__error !== undefined) {
      const err = sanitized.__error;
      delete sanitized.__error;
      return { sanitized: {}, error: err };
    }
    return { sanitized, error: null };
  }
  function sanitizeRuleEntries(rawRules) {
    if (Array.isArray(rawRules) !== true) {
      return { sanitized: [], error: null };
    }
    const sanitized = [];
    for (let i = 0; i < rawRules.length; i += 1) {
      const rule = rawRules[i];
      if (typeof rule !== 'string') {
        continue;
      }
      const validation = secureUserText(rule, {
        fieldName: `Rule ${i + 1}`,
        maxLength: 1000,
        allowNewLines: true,
        disallowAngleBrackets: true
      });
      if (validation.error !== null) {
        return { sanitized: [], error: validation.error };
      }
      sanitized.push(validation.value);
    }
    return { sanitized, error: null };
  }

  function sanitizeOptionalText(raw, fieldName, options = {}) {
    const validation = secureUserText(raw, {
      fieldName,
      allowNewLines: options.allowNewLines === true,
      disallowAngleBrackets: options.disallowAngleBrackets === true,
      maxLength: options.maxLength,
      allowEmpty: true
    });
    if (validation.error !== null) {
      return { value: '', error: validation.error };
    }
    return { value: validation.value, error: null };
  }

  function sanitizeActionButtonsList(buttons) {
    if (Array.isArray(buttons) !== true) {
      return { sanitized: [], error: null };
    }
    const sanitized = [];
    for (let i = 0; i < buttons.length && sanitized.length < 4; i += 1) {
      const rawButton = buttons[i] !== undefined && buttons[i] !== null ? buttons[i] : {};
      const labelCheck = secureUserText(rawButton.label, {
        fieldName: `Button ${i + 1} label`,
        maxLength: 80,
        disallowAngleBrackets: true
      });
      if (labelCheck.error !== null) {
        return { sanitized: [], error: labelCheck.error };
      }
      const type = rawButton.type === 'URL' ? 'URL' : 'Text';
      let actionValue = '';
      if (type === 'URL') {
        const actionCheck = secureUserText(rawButton.action, {
          fieldName: `Button ${i + 1} URL`,
          maxLength: 500
        });
        if (actionCheck.error !== null) {
          return { sanitized: [], error: actionCheck.error };
        }
        const normalizedUrl = actionCheck.value.toLowerCase().startsWith('http://') === true || actionCheck.value.toLowerCase().startsWith('https://') === true
          ? actionCheck.value
          : null;
        if (normalizedUrl === null) {
          return { sanitized: [], error: `Button ${i + 1} URL must start with http:// or https://.` };
        }
        actionValue = normalizedUrl;
      }
      sanitized.push({
        label: labelCheck.value,
        type,
        action: actionValue
      });
    }
    return { sanitized, error: null };
  }

  function sanitizeRegexEntries(entries) {
    if (Array.isArray(entries) !== true) {
      return { sanitized: [], error: null };
    }
    const sanitized = [];
    for (let i = 0; i < entries.length; i += 1) {
      const rawValue = typeof entries[i] === 'string' ? entries[i].normalize('NFKC') : '';
      const trimmed = rawValue.trim();
      if (trimmed.length === 0) {
        continue;
      }
      if (controlCharPattern.test(trimmed) === true) {
        return { sanitized: [], error: `Custom block regex ${i + 1} contains disallowed control characters.` };
      }
      if (trimmed.length > 500) {
        return { sanitized: [], error: `Custom block regex ${i + 1} exceeds 500 characters.` };
      }
      sanitized.push(trimmed);
    }
    return { sanitized, error: null };
  }
  
  const knowledgeBaseFolders = ref([]); 
  const knowledgeBaseFileAssignments = ref({}); 
  const currentKbPath = ref([{ id: null, name: 'Knowledge Base' }]);
  const createFolderPromptText = computed(() => {
    const pathValue = currentKbPath.value;
    let folderName = '';
    if (Array.isArray(pathValue) === true && pathValue.length > 0) {
      const lastIndex = pathValue.length - 1;
      const lastEntry = pathValue[lastIndex];
      if (lastEntry !== undefined && lastEntry !== null && typeof lastEntry.name === 'string') {
        folderName = lastEntry.name;
      }
    }
    if (folderName === 'Knowledge Base') {
      folderName = t('aiChatAgentSetup.knowledgeBase.title', 'Knowledge Base');
    }
    return t('aiChatAgentSetup.modals.createFolder.prompt', 'Enter name for the new folder in <span class=\'font-semibold\'>{folderName}</span>', { folderName: folderName });
  });
   const showCreateFolderModal = ref(false);
   const newFolderName = ref('');
   const showMoveFileModal = ref(false);
   const fileToMove = ref(null); 
   const targetFolderForMove = ref(null); 
   const draggedItem = ref(null); 
   const isDragOverFolder = ref(null); 
  
  
   const folderBeingRenamedId = ref(null);
   const newFolderNameInput = ref('');
  
  const cachedFileNames = ref([]);
  
  const isEditingTextFile = ref(false);
  const originalTextFileToEdit = ref(null);
  
  const kbViewMode = ref('rows');
  
  
  const agentNameError = ref(false);
  const agentNameErrorMessage = ref('');
  const agentDescriptionError = ref(false);
  const agentDescriptionErrorMessage = ref('');
  const agentShortDescriptionError = ref(false); 
  const agentShortDescriptionErrorMessage = ref(''); 
  const startingMessageError = ref(false);
  const startingMessageErrorMessage = ref('');
  const divisionError = ref(false);
  const divisionErrorMessage = ref('');
  
  const isSavingAndRedirecting = ref(false);
  const isRefreshingKB = ref(false);
  const showMaxPromptModal = ref(false);
  watch(showMaxPromptModal, (value) => {
    if (typeof document !== 'undefined') {
      if (value === true) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  });
  
  const loadingTips = [
    t('aiChatAgentSetup.loadingTips.tip1'),
    t('aiChatAgentSetup.loadingTips.tip2'),
    t('aiChatAgentSetup.loadingTips.tip3'),
    t('aiChatAgentSetup.loadingTips.tip4'),
    t('aiChatAgentSetup.loadingTips.tip5'),
    t('aiChatAgentSetup.loadingTips.tip6'),
    t('aiChatAgentSetup.loadingTips.tip7'),
    t('aiChatAgentSetup.loadingTips.tip8'),
    t('aiChatAgentSetup.loadingTips.tip9'),
    t('aiChatAgentSetup.loadingTips.tip10')
  ];
   
  const currentFolderId = computed(() => {
    if (currentKbPath.value.length === 0) return null;
    return currentKbPath.value[currentKbPath.value.length - 1].id;
  });
  
  function processLoadedAgentData(agentData) {
      loadedAgentData = agentData;
      console.log('AIChatAgentSetup: Processing agent data:', loadedAgentData);
      agentName.value = loadedAgentData.agentname;
      agentDescription.value = loadedAgentData.agentdescription;
      agentQnA.value = loadedAgentData.agentdata?.qna !== undefined ? loadedAgentData.agentdata.qna : '';
      selectedAgentPersonality.value = '';
      agentShortDescription.value = isDefinedAndNotEmpty(loadedAgentData.agentShortDescription) != false ? loadedAgentData.agentShortDescription : '';
      agentActive.value = loadedAgentData.agentstatus.toLowerCase() === 'active';
      agentType.value = isDefinedAndNotEmpty(loadedAgentData.agenttype) != false ? loadedAgentData.agenttype : 'PublicAgent';
      agentMode.value = isDefinedAndNotEmpty(loadedAgentData.agentMode) != false ? loadedAgentData.agentMode : 'Agentic';
      agenticBehaviour.value = loadedAgentData.agentdata !== undefined && loadedAgentData.agentdata.agenticBehaviour === false ? false : true;
      if (agentMode.value === 'Flow' || agentMode.value === 'FlowBased') {
        agenticBehaviour.value = true;
      } else if (agenticBehaviour.value === false) {
        agentMode.value = 'SuperFast';
      }
      pendingAgenticBehaviour.value = agenticBehaviour.value;
      selectedDivisionIds.value = isDefinedAndNotEmpty(loadedAgentData.divisionIds) != false ? loadedAgentData.divisionIds : [];
  
      if (loadedAgentData.agentdata !== undefined && loadedAgentData.agentdata !== null) {
          const data = loadedAgentData.agentdata;
          const realTimeFileStatuses = isDefinedAndNotEmpty(loadedAgentData.fileStatuses) != false ? loadedAgentData.fileStatuses : [];
          selectedToolIds.value = isDefinedAndNotEmpty(data.selectedToolIds) != false ? data.selectedToolIds : [];
  
          if (typeof data.startingMessage === 'string') {
              startingMessage.value = { default: data.startingMessage };
          } else {
              startingMessage.value = isDefinedAndNotEmpty(data.startingMessage) != false ? data.startingMessage : {};
              if (startingMessage.value.en !== undefined && startingMessage.value.default === undefined) {
                  startingMessage.value.default = startingMessage.value.en;
                  delete startingMessage.value.en;
              }
          }
          
          agentLanguage.value = isDefinedAndNotEmpty(data.language) != false ? data.language : 'en';
          rules.value = isDefinedAndNotEmpty(data.rules) != false ? data.rules : [];
          selectedStyleGuid.value = isDefinedAndNotEmpty(data.styleGuid) != false ? data.styleGuid : '';
  
          knowledgeBaseFolders.value = isDefinedAndNotEmpty(data.knowledgeBaseFolders) != false ? data.knowledgeBaseFolders : [];
          knowledgeBaseFileAssignments.value = isDefinedAndNotEmpty(data.knowledgeBaseFileAssignments) != false ? data.knowledgeBaseFileAssignments : {};
  
          const allAgentFiles = isDefinedAndNotEmpty(data.files) != false ? data.files : [];
          const statusMap = new Map((isDefinedAndNotEmpty(realTimeFileStatuses) != false ? realTimeFileStatuses : []).map(s => [s.name, s]));
  
          existingFiles.value = allAgentFiles.map(file => {
              const statusInfo = statusMap.get(file.fileName);
              let computedStatus = isDefinedAndNotEmpty(statusInfo) != false ? statusInfo.status : (file.fileType === 'website' || file.fileType === 'websiteurl' ? 'Processing' : 'Available');
              const ft = file.fileType;
              if (ft === 'website' || ft === 'websiteurl' || ft === 'websitepage') {
                  const prog = statusInfo !== undefined && statusInfo !== null ? statusInfo.progress : null;
                  if (prog !== undefined && prog !== null) {
                      const totalVal = Number(prog.total);
                      const currentVal = Number(prog.current);
                      const totalIsNum = Number.isFinite(totalVal) === true;
                      const currentIsNum = Number.isFinite(currentVal) === true;
                      if (totalIsNum === true && currentIsNum === true) {
                          if (totalVal <= 0 || currentVal >= totalVal) {
                              computedStatus = 'Completed';
                          }
                      }
                  } else {
                      const detailsLower = (statusInfo !== undefined && statusInfo !== null && statusInfo.details !== undefined && statusInfo.details !== null) ? String(statusInfo.details).toLowerCase() : '';
                      if (detailsLower.includes('no pages') === true || detailsLower.includes('no urls') === true || detailsLower.includes('no links') === true) {
                          computedStatus = 'Completed';
                      }
                  }
              }
              const hasUtcHour = Number.isFinite(Number(file.rescrapeAtHour)) === true;
              const hasUtcMinute = Number.isFinite(Number(file.rescrapeAtMinute)) === true;
              const derivedLocalTime = (hasUtcHour === true || hasUtcMinute === true) ? toLocalHHMMFromUtc(Number(file.rescrapeAtHour), Number(file.rescrapeAtMinute)) : (typeof file.rescrapeAtTime === 'string' ? file.rescrapeAtTime : '');
              return {
                  ...file,
                  hiddenFromSources: file.hiddenFromSources === true,
                  content: (file.fileType === 'text' && file.fileData) ? decodeBase64UTF8(file.fileData) : (isDefinedAndNotEmpty(file.content) != false ? file.content : null),
                  status: 'existing',
                  processingStatus: computedStatus,
                processingDetails: isDefinedAndNotEmpty(statusInfo) != false ? statusInfo.details : null,
                rescrapeAtTime: derivedLocalTime
              };
          });
  
          if (data.selectedLlmModelId!=false) {
              selectedLLMModel.value = data.selectedLlmModelId;
          } else if (availableLLMModels.value.length > 0) {
              selectedLLMModel.value = availableLLMModels.value[0].id;
          }
  
          if (data.configurations!=false) {
             enableFeedbackButtons.value = isDefinedAndNotEmpty(data.configurations.enableFeedbackButtons) != false ? data.configurations.enableFeedbackButtons : true;
             enableSuggestedResponses.value = isDefinedAndNotEmpty(data.configurations.enableSuggestedResponses) != false ? data.configurations.enableSuggestedResponses : false;
             enableStreamingText.value = data.configurations.enableStreamingText !== false;
             enableUserFineTune.value = data.configurations.enableUserFineTune === true;
             retrieveUserLocation.value = isDefinedAndNotEmpty(data.configurations.retrieveUserLocation) != false ? data.configurations.retrieveUserLocation : false;
             makeLinksClickable.value = isDefinedAndNotEmpty(data.configurations.makeLinksClickable) != false ? data.configurations.makeLinksClickable : true;
            clearSessionOnRefresh.value = isDefinedAndNotEmpty(data.configurations.clearSessionOnRefresh) != false ? data.configurations.clearSessionOnRefresh : false;
            enabledSavedSessionHistory.value = data.configurations.enabledSavedSessionHistory === true;
             showMap.value = data.configurations.showMap !== false;
             enableFileUploadInChat.value = isDefinedAndNotEmpty(data.configurations.enableFileUploadInChat) != false ? data.configurations.enableFileUploadInChat : (agentType.value === 'PrivateAgent');
             allowCallControl.value = data.configurations.allowCallControl === true;
             inappropriateContentMessage.value = isDefinedAndNotEmpty(data.configurations.inappropriateContentMessage) != false ? data.configurations.inappropriateContentMessage : {};
             dontKnowMessage.value = isDefinedAndNotEmpty(data.configurations.dontKnowMessage) != false ? data.configurations.dontKnowMessage : {};
              selectedDeploymentChannels.value = isDefinedAndNotEmpty(data.configurations.deploymentChannels) != false ? data.configurations.deploymentChannels : ['Web Voice Agent'];
             limitReachedMessage.value = isDefinedAndNotEmpty(data.configurations.limitReachedMessage) != false ? data.configurations.limitReachedMessage : defaultLimitReachedMessageTemplate;
             messageLimitPerConversation.value = Number.isFinite(Number(data.configurations.messageLimitPerConversation)) != false && Number(data.configurations.messageLimitPerConversation) >= 0 ? Number(data.configurations.messageLimitPerConversation) : 0;
             messageLimitPerIP.value = Number.isFinite(Number(data.configurations.messageLimitPerIP)) != false && Number(data.configurations.messageLimitPerIP) >= 0 ? Number(data.configurations.messageLimitPerIP) : 0;
             if (data.configurations.limitMessagesPerConversationEnabled !== undefined && data.configurations.limitMessagesPerConversationEnabled !== null) {
               limitMessagesPerConversationEnabled.value = data.configurations.limitMessagesPerConversationEnabled === true;
             } else {
               limitMessagesPerConversationEnabled.value = false;
             }
             if (data.configurations.limitMessagesPerIPEnabled !== undefined && data.configurations.limitMessagesPerIPEnabled !== null) {
               limitMessagesPerIPEnabled.value = data.configurations.limitMessagesPerIPEnabled === true;
             } else {
               limitMessagesPerIPEnabled.value = false;
             }
             enablePIIRedaction.value = data.configurations.enablePIIRedaction === true;
             redactEmails.value = data.configurations.redactEmails !== false;
             redactPhoneNumbers.value = data.configurations.redactPhoneNumbers === true;
             customBlockRegex.value = Array.isArray(data.configurations.customBlockRegex) != false ? data.configurations.customBlockRegex : [];
             if (isDefinedAndNotEmpty(data.configurations.agentToneId) != false) {
               selectedAgentTone.value = data.configurations.agentToneId;
             }
              whatsappClosingMessage.value = isDefinedAndNotEmpty(data.configurations.whatsappClosingMessage) != false ? data.configurations.whatsappClosingMessage : '';
              if (Number.isFinite(Number(data.configurations.retrieverKValue)) === true && Number(data.configurations.retrieverKValue) >= 1) {
                retrieverKValue.value = Number(data.configurations.retrieverKValue);
              } else {
                retrieverKValue.value = 10;
              }
              if (Number.isFinite(Number(data.configurations.maxOutputTokens)) === true && Number(data.configurations.maxOutputTokens) >= 1) {
                maxOutputTokens.value = Number(data.configurations.maxOutputTokens);
              } else {
                maxOutputTokens.value = 2048;
              }
              if (Array.isArray(data.actionButtons) === true) {
                actionButtons.value = data.actionButtons;
              } else if (Array.isArray(data.configurations?.actionButtons) === true) {
                actionButtons.value = data.configurations.actionButtons;
              } else {
                actionButtons.value = [];
              }
              if (data.configurations?.verifyAnswerEnabled !== undefined && data.configurations?.verifyAnswerEnabled !== null) {
                verifyAnswerEnabled.value = isDefinedAndNotEmpty(data.configurations.verifyAnswerEnabled) != false ? data.configurations.verifyAnswerEnabled : false;
              } else {
                verifyAnswerEnabled.value = false;
              }
              if (data.configurations?.verifyLinks !== undefined && data.configurations?.verifyLinks !== null) {
                verifyLinks.value = data.configurations.verifyLinks === true;
              } else {
                verifyLinks.value = true;
              }
              const authCfg = typeof data.configurations?.authenticator === 'object' && data.configurations.authenticator !== null ? data.configurations.authenticator : {};
              const providerValue = typeof authCfg.provider === 'string' && authCfg.provider.trim().length > 0 ? authCfg.provider : 'google';
              const welcomeValue = typeof authCfg.welcomeMessageTemplate === 'string' && authCfg.welcomeMessageTemplate.trim().length > 0 ? authCfg.welcomeMessageTemplate : defaultAuthenticatorWelcomeTemplate;
              const retryValue = typeof authCfg.retryMessageTemplate === 'string' && authCfg.retryMessageTemplate.trim().length > 0 ? authCfg.retryMessageTemplate : defaultAuthenticatorRetryTemplate;
              const successValue = typeof authCfg.successMessageTemplate === 'string' && authCfg.successMessageTemplate.trim().length > 0 ? authCfg.successMessageTemplate : defaultAuthenticatorSuccessTemplate;
              authenticatorEnabled.value = authCfg.enabled === true;
              authenticatorProvider.value = providerValue;
              authenticatorWelcomeMessage.value = welcomeValue;
              authenticatorRetryMessage.value = retryValue;
              authenticatorSuccessMessage.value = successValue;
              authenticatorRunAiToolOnLogin.value = authCfg.runAiToolOnLogin === true;
              if (isDefinedAndNotEmpty(data.configurations?.verificationModelId) != false) {
                selectedVerificationLLMModel.value = data.configurations.verificationModelId;
              } else {
                selectedVerificationLLMModel.value = '';
              }
              if (isDefinedAndNotEmpty(data.configurations?.voiceAgent) != false) {
                const v = data.configurations.voiceAgent;
                voiceAgentExtraPrompt.value = isDefinedAndNotEmpty(v.extraPrompt) != false ? v.extraPrompt : '';
                voiceAgentProvider.value = isDefinedAndNotEmpty(v.provider) != false ? v.provider : 'openai-realtime';
                voiceAgentVoice.value = isDefinedAndNotEmpty(v.voice) != false ? v.voice : (availableVoicesByProvider[voiceAgentProvider.value]?.[0] || 'alloy');
              }
              if (isDefinedAndNotEmpty(data.configurations?.phoneVoiceId) != false) {
                selectedPhoneVoiceId.value = data.configurations.phoneVoiceId;
              } else if (isDefinedAndNotEmpty(data.configurations?.voiceAgent?.voice) != false) {
                selectedPhoneVoiceId.value = data.configurations.voiceAgent.voice;
              }
              if (Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') === true) {
                forceShowVoiceTab.value = false;
              }
              if (isDefinedAndNotEmpty(data.voiceSettings) != false) {
                const vs = data.voiceSettings;
                voiceSettingsVoiceType.value = normalizeVoiceSettingsVoiceType(vs.voiceType);
                voiceSettingsVadEngine.value = normalizeVoiceSettingsVadEngine(vs.vadEngine !== undefined ? vs.vadEngine : vs.vadType);
                const incomingVoiceId = isDefinedAndNotEmpty(vs.voiceId) != false ? vs.voiceId : selectedPhoneVoiceId.value;
                voiceSettingsVoiceId.value = typeof incomingVoiceId === 'string' && incomingVoiceId.length > 0 ? incomingVoiceId : selectedPhoneVoiceId.value;
                voiceSettingsAccent.value = isDefinedAndNotEmpty(vs.accent) != false ? vs.accent : voiceSettingsAccent.value;
                voiceSettingsLanguage.value = isDefinedAndNotEmpty(vs.language) != false ? vs.language : voiceSettingsLanguage.value;
                voiceSettingsSpeed.value = Number.isFinite(Number(vs.speed)) === true ? Number(vs.speed) : voiceSettingsSpeed.value;
                voiceSettingsPromptToVoiceEnabled.value = vs.promptToVoiceEnabled === true;
                voiceSettingsPromptToVoicePrompt.value = typeof vs.promptToVoicePrompt === 'string' && vs.promptToVoicePrompt.trim() !== '' ? vs.promptToVoicePrompt : voiceSettingsPromptToVoicePrompt.value;
                voiceSettingsTone.value = isDefinedAndNotEmpty(vs.tone) != false ? vs.tone : voiceSettingsTone.value;
                voiceSettingsMode.value = isDefinedAndNotEmpty(vs.mode) != false ? ((vs.mode === 'wait full' || vs.mode === 'preventuserinterruptions') ? 'wait full' : ((vs.mode === 'interruptible' || vs.mode === 'allowuserinterruptions') ? 'interruptible' : 'interruptible')) : 'interruptible';
                voiceSettingsCancellingMode.value = normalizeVoiceSettingsCancellingMode(vs.cancellingMode !== undefined ? vs.cancellingMode : vs.cancelingMode);
                voiceSettingsAudioProcessorEngine.value = normalizeVoiceSettingsAudioProcessorEngine(vs.audioProcessorEngine !== undefined ? vs.audioProcessorEngine : vs.audioProcessor);
                voiceSettingsIdleAutoResponseEnabled.value = vs.idleAutoResponseEnabled !== false;
                voiceSettingsStartingMessageIdleAutoResponseSeconds.value = Number.isFinite(Number(vs.startingMessageIdleAutoResponseSeconds)) === true && Number(vs.startingMessageIdleAutoResponseSeconds) >= 1 ? Math.min(300, Number(vs.startingMessageIdleAutoResponseSeconds)) : 6;
                voiceSettingsIdleAutoResponseSeconds.value = Number.isFinite(Number(vs.idleAutoResponseSeconds)) === true && Number(vs.idleAutoResponseSeconds) >= 1 ? Math.min(300, Number(vs.idleAutoResponseSeconds)) : 3;
                voiceSettingsToolUseAutoResponseSeconds.value = Number.isFinite(Number(vs.toolUseAutoResponseSeconds)) === true && Number(vs.toolUseAutoResponseSeconds) >= 0 ? Math.min(300, Number(vs.toolUseAutoResponseSeconds)) : 2;
                voiceSettingsTermsPackages.value = Array.isArray(vs.termsPackages) === true ? vs.termsPackages.filter((item) => typeof item === 'string' && item.trim() !== '') : (Array.isArray(vs.termsPackageIds) === true ? vs.termsPackageIds.filter((item) => typeof item === 'string' && item.trim() !== '') : []);
                voiceSettingsSonioxTerms.value = Array.isArray(vs.sonioxContext?.terms) === true ? vs.sonioxContext.terms.filter((item) => typeof item === 'string' && item.trim() !== '') : [];
                voiceSettingsTranscriptionOptimizerEnabled.value = vs.transcriptionOptimizer?.enabled === true;
                voiceSettingsTranscriptionOptimizerTerms.value = Array.isArray(vs.transcriptionOptimizer?.terms) === true ? vs.transcriptionOptimizer.terms.filter((item) => typeof item === 'string' && item.trim() !== '') : [];
                if (Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') !== true) {
                  forceShowVoiceTab.value = true;
                }
                if (typeof voiceSettingsVoiceId.value === 'string' && voiceSettingsVoiceId.value.length > 0 && voiceSettingsVoiceId.value !== selectedPhoneVoiceId.value) {
                  selectedPhoneVoiceId.value = voiceSettingsVoiceId.value;
                }
              }
              if (isDefinedAndNotEmpty(data.selectedVectorDBId) != false) {
                selectedVectorDBId.value = data.selectedVectorDBId;
              }
              if (isDefinedAndNotEmpty(data.selectedEmbeddingModelId) != false) {
                selectedEmbeddingModelId.value = data.selectedEmbeddingModelId;
              }
          }
  
          cachedFileNames.value = isDefinedAndNotEmpty(data.cachedFileNames) != false ? data.cachedFileNames : [];
  
          tools.value = [];
          if (Array.isArray(selectedToolIds.value) !=false && Array.isArray(availableTools.value) !=false) {
              for (let i = 0; i < selectedToolIds.value.length; i = i + 1) {
                  const toolId = selectedToolIds.value[i];
                  const foundTool = availableTools.value.find(t => isDefinedAndNotEmpty(t.toolid) != false ? t.toolid === toolId : false);
                  if (isDefinedAndNotEmpty(foundTool) != false) {
                      tools.value.push(foundTool);
                  }
              }
          }
  
          selectedAnalysisCollectionIds.value = isDefinedAndNotEmpty(data.selectedAnalysisCollectionIds) != false ? data.selectedAnalysisCollectionIds : [];
          if (isDefinedAndNotEmpty(data.avatarProvider) != false) {
            selectedAvatarProvider.value = data.avatarProvider;
          } else {
            selectedAvatarProvider.value = 'did';
          }
          if (isDefinedAndNotEmpty(data.avatarProviderConfigs) != false) {
            avatarProviderConfigs.value = {
              ...avatarProviderConfigs.value,
              ...data.avatarProviderConfigs
            };
          } else {
            avatarProviderConfigs.value = {
              did: defaultAvatarProviderConfig('did')
            };
          }
          if (avatarProviderConfigs.value[selectedAvatarProvider.value] === undefined) {
            avatarProviderConfigs.value = {
              ...avatarProviderConfigs.value,
              [selectedAvatarProvider.value]: defaultAvatarProviderConfig(selectedAvatarProvider.value)
            };
          }
  
          if (agentMode.value === 'FlowBased' && data.flow) {
              flowBasedFlow.value = data.flow;
          }
          if (agentMode.value === 'Flow' && data.flow) {
              nextTick(() => {
                  if (isDefinedAndNotEmpty(llmFlowRef.value) != false) {
                      llmFlowRef.value.loadFlowData(data.flow);
                  }
              });
          }
      }
  }
  
  async function refreshAgentData() {
      if (isEditMode.value === false) return;
      isRefreshingKB.value = true;
      try {
          const agentIdRaw = route.query.edit;
          const agentId = agentIdRaw !== undefined && agentIdRaw !== null ? String(agentIdRaw) : '';
          if (agentId.length === 0) {
            toast.error("Failed to load agent data.");
            return;
          }
          const cfg = getSecuredAxiosConfig(null);
          if (cfg === null) {
            return;
          }
          const response = await axios.get(`${apiEndpoint}/getAIChat/${encodePathSegment(agentId)}`, cfg);
          processLoadedAgentData(response.data);
          if (selectedDivisionIds.value.length > 0) {
              await fetchFilteredCollections(selectedDivisionIds.value);
          }
      } catch (error) {
          handleApiError(error, 'loading agent data');
      } finally {
          isRefreshingKB.value = false;
      }
  }
  
  function startLoadingTips() {
    let index = 0;
    currentTip.value = loadingTips[0];
    
    tipInterval.value = setInterval(() => {
      index = (index + 1) % loadingTips.length;
      currentTip.value = loadingTips[index];
    }, 30000);
  }
  
  function stopLoadingTips() {
    if (tipInterval.value !== null) {
      clearInterval(tipInterval.value);
      tipInterval.value = null;
    }
  }
  
  const filteredExistingFiles = computed(() => {
    const query = searchQuery.value.toLowerCase();
    const folderId = currentFolderId.value;
  
    return existingFiles.value.filter(file => {
      const fileAssignment = knowledgeBaseFileAssignments.value[file.fileName];
      const isInCurrentFolder = (folderId === null && (fileAssignment === undefined || fileAssignment === null)) || fileAssignment === folderId;
  
      if (isInCurrentFolder === false) {
        return false;
      }
  
      if (file.status === 'deleted') {
        const isReplacedByEdit = uploadedFiles.value.some(upFile => upFile.replacesFile === file.fileName && upFile.isEditedVersion === true);
        if (isReplacedByEdit === true) {
          return false; 
        }
      }
  
      return file.fileType !== 'fineTuneAnswer' &&
        (query === '' || 
          (file.fileName.toLowerCase().includes(query) === true) ||
          ((file.fileType === 'websiteurl' ? 'Website URL' : file.fileType === 'website' ? 'Website' : file.fileType !== undefined && file.fileType !== null ? file.fileType : 'File').toLowerCase().includes(query) === true) ||
          ((file.status === 'existing' ? 'Available' : file.status === 'deleted' ? 'To Delete' : 'New').toLowerCase().includes(query) === true)
        );
    });
  });
  
  const filteredUploadedFiles = computed(() => {
    const query = searchQuery.value.toLowerCase();
    const folderId = currentFolderId.value;
  
    return uploadedFiles.value.filter(file => {
      const fileAssignment = knowledgeBaseFileAssignments.value[file.fileName];
      const isInCurrentFolder = (folderId === null && (fileAssignment === undefined || fileAssignment === null)) || fileAssignment === folderId;
  
      if (isInCurrentFolder === false) {
        return false;
      }
      
      return file.fileType !== 'fineTuneAnswer' &&
        (query === '' ||
          (file.fileName.toLowerCase().includes(query) === true) ||
          ((file.fileType === 'websiteurl' ? 'Website URL' : file.fileType === 'website' ? 'Website' : file.fileType !== undefined && file.fileType !== null ? file.fileType : 'File').toLowerCase().includes(query) === true) ||
          ('New'.toLowerCase().includes(query) === true)
        );
    });
  });
   
  const displayedFoldersInCurrentView = computed(() => {
    const folderId = currentFolderId.value;
     const query = searchQuery.value.toLowerCase();
    return knowledgeBaseFolders.value.filter(folder => 
      (folder.parentId === folderId) &&
      (query === '' || (folder.name.toLowerCase().includes(query) === true))
    );
  });
  
  
  const fineTunedAnswers = computed(() => {
    return existingFiles.value.filter(file => file.fileType === 'fineTuneAnswer' && file.status !== 'deleted');
  });
  
  const maxFileSizeMb = 6000;
  const maxFileSizeBytes = maxFileSizeMb * 1024 * 1024;
  
  function handleFileUpload(event) {
    const files = Array.from(event.target.files);
    for (let i = 0; i < files.length; i = i + 1) {
      const file = files[i];
      if (file.size > maxFileSizeBytes) {
          toast.error(`File ${file.name} exceeds the ${maxFileSizeMb}MB size limit.`);
          continue;
      }
      const isDuplicateInUploaded = uploadedFiles.value.some(existing => existing.fileName === file.name);
      const isDuplicateInExisting = existingFiles.value.some(existing => existing.fileName === file.name && existing.status !== 'deleted');
      if (isDuplicateInUploaded === false && isDuplicateInExisting === false) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Data = e.target.result.split(',')[1];
          const fileType = file.name.split('.').pop().toLowerCase();
          const allowedTypes = ['pdf', 'docx', 'txt', 'xlsx'];
          if (allowedTypes.includes(fileType) === true) {
            const newFileEntry = {
              fileName: file.name,
              fileType: fileType === 'txt' ? 'text' : fileType,
              fileData: base64Data,
              content: (fileType === 'txt' || fileType === 'text') ? decodeBase64UTF8(base64Data) : null, 
               status: 'new',
               hiddenFromSources: false,
               rescrapeEnabled: false,
               rescrapeIntervalDays: '',
               rescrapeAtTime: ''
            };
            uploadedFiles.value.push(newFileEntry);
            if (knowledgeBaseFileAssignments.value[newFileEntry.fileName] === undefined) {
              knowledgeBaseFileAssignments.value[newFileEntry.fileName] = currentFolderId.value;
            }
          } else {
            toast.error(`Unsupported file type: ${fileType}`);
          }
        };
        reader.readAsDataURL(file);
      } else {
        toast.warning(`File ${file.name} already exists or is queued for upload, skipping.`);
      }
    }
     event.target.value = '';
  }
  
  async function downloadFile(file) {
    const agentId = route.query.edit;
    if (agentId === undefined || agentId === null) {
      toast.error("Cannot download file: Agent ID is missing.");
      return;
    }
  
    const cfg = getSecuredAxiosConfig({ responseType: 'blob' });
    if (cfg === null) {
      return;
    }
    toast.info(`Downloading ${decodeURIComponentSafe(file.fileName)}...`);
    try {
      const response = await axios.get(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/files/${encodeURIComponent(file.fileName)}`, cfg);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const decodedFileName = decodeURIComponentSafe(file.fileName);
      link.setAttribute('download', decodedFileName);
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`'${decodedFileName}' downloaded.`);
    } catch (error) {
      toast.dismiss();
      handleApiError(error, `downloading '${decodeURIComponentSafe(file.fileName)}'`);
    }
  }
  
  function removeFile(index, type) { 
    let fileNameToRemove;
    if (type === 'existing') {
       const file = filteredExistingFiles.value[index]; 
      if (file !== undefined) {
          const originalIndex = existingFiles.value.findIndex(ef => ef.fileName === file.fileName);
          if (originalIndex !== -1) {
              existingFiles.value[originalIndex].status = 'deleted';
              fileNameToRemove = existingFiles.value[originalIndex].fileName;
             
          }
      }
     } else { 
       const file = filteredUploadedFiles.value[index];
      if (file !== undefined) {
          const originalIndex = uploadedFiles.value.findIndex(uf => uf.fileName === file.fileName);
          if (originalIndex !== -1) {
              fileNameToRemove = uploadedFiles.value[originalIndex].fileName;
              uploadedFiles.value.splice(originalIndex, 1);
              delete knowledgeBaseFileAssignments.value[fileNameToRemove];
          }
       }
    }
  }
  
  function removeFileByName(file, type) {
    if (type === 'existing') {
      const originalIndex = existingFiles.value.findIndex(ef => ef.fileName === file.fileName);
      if (originalIndex > -1) {
        existingFiles.value[originalIndex].status = 'deleted';
      }
    } else {
      const originalIndex = uploadedFiles.value.findIndex(uf => uf.fileName === file.fileName);
      if (originalIndex > -1) {
        uploadedFiles.value.splice(originalIndex, 1);
        delete knowledgeBaseFileAssignments.value[file.fileName];
      }
    }
  }
  
  
  function removeFineTunedAnswer(index) {
   
    const originalIndex = existingFiles.value.findIndex(file => file.fileName === fineTunedAnswers.value[index].fileName);
    if (originalIndex !== -1) {
      existingFiles.value[originalIndex].status = 'deleted';
      toast.info('Fine-tuned answer marked for deletion.');
    } else {
      toast.error('Could not find the fine-tuned answer to remove.');
    }
  }
  
  function updateAutoModelMappings(models) {
    const list = Array.isArray(models) === true ? models : [];
    const detectedAuto = list.find(model => (typeof model.id === 'string' && model.id === autoLlmModelId) || model.isAuto === true);
    if (detectedAuto !== undefined && detectedAuto !== null) {
      const baseAnswerRaw = typeof detectedAuto.autoAnswerModelId === 'string' ? detectedAuto.autoAnswerModelId.trim() : '';
      const baseAnswer = baseAnswerRaw.length > 0 ? baseAnswerRaw : autoLlmModelId;
      const fastCandidateRaw = typeof detectedAuto.fastModeModelId === 'string' ? detectedAuto.fastModeModelId.trim() : '';
      autoFastModeModelId.value = fastCandidateRaw.length > 0 ? fastCandidateRaw : baseAnswer;
      const superFastCandidateRaw = typeof detectedAuto.superFastModeModelId === 'string' ? detectedAuto.superFastModeModelId.trim() : '';
      autoSuperFastModeModelId.value = superFastCandidateRaw.length > 0 ? superFastCandidateRaw : autoFastModeModelId.value;
      const verifyCandidateRaw = typeof detectedAuto.autoVerificationModelId === 'string' ? detectedAuto.autoVerificationModelId.trim() : '';
      autoVerificationModelId.value = verifyCandidateRaw.length > 0 ? verifyCandidateRaw : baseAnswer;
    } else {
      autoFastModeModelId.value = null;
      autoSuperFastModeModelId.value = null;
      autoVerificationModelId.value = null;
    }
    syncAutoVerificationModel();
  }
  
  function syncAutoVerificationModel() {
    if (selectedLLMModel.value !== autoLlmModelId) {
        return;
      }
    const isFastMode = agentMode.value === 'Fast';
    const isSuperFastMode = agentMode.value === 'SuperFast';
    if (isFastMode === true || isSuperFastMode === true) {
      if (verifyAnswerEnabled.value !== false) {
        verifyAnswerEnabled.value = false;
      }
      if (selectedVerificationLLMModel.value !== '') {
        selectedVerificationLLMModel.value = '';
      }
      return;
    }
    if (verifyAnswerEnabled.value !== true) {
      verifyAnswerEnabled.value = true;
    }
    const targetVerificationModel = typeof autoVerificationModelId.value === 'string' && autoVerificationModelId.value.length > 0 ? autoVerificationModelId.value : '';
    if (selectedVerificationLLMModel.value !== targetVerificationModel) {
      selectedVerificationLLMModel.value = targetVerificationModel;
    }
  }
  
  const fetchLLMModels = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        availableLLMModels.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/config/llmModels`, cfg);
      let models = Array.isArray(response.data) === true ? response.data : [];
      const allowed = Array.isArray(orgPolicies.value?.features?.allowedLlmModelIds) === true ? orgPolicies.value.features.allowedLlmModelIds : null;
      if (Array.isArray(allowed) === true && allowed.length > 0) {
        models = models.map(m => ({ ...m, hasAccess: allowed.includes(m.id) }));
      }
      availableLLMModels.value = models;
      updateAutoModelMappings(availableLLMModels.value);
      const autoModelExists = availableLLMModels.value.some(model => model.id === autoLlmModelId);
      if (llmLockedId.value !== null) {
        selectedLLMModel.value = llmLockedId.value;
      } else if (isEditMode.value === false && availableLLMModels.value.length > 0 && selectedLLMModel.value === null) {
        selectedLLMModel.value = autoModelExists === true ? autoLlmModelId : availableLLMModels.value[0].id;
      }
    } catch (error) {
      handleApiError(error, 'loading LLM models');
      availableLLMModels.value = []; 
    }
  };
  
  async function loadOrgPolicies() {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        orgPolicies.value = {};
        return;
      }
      const userRes = await axios.get(`${apiEndpoint}/api/users/me`, cfg);
      const orgId = userRes?.data?.organizationID;
      if (Number.isFinite(Number(orgId)) === true) {
          const polRes = await axios.get(`${apiEndpoint}/api/agents/policies/${encodePathSegment(orgId)}`, cfg);
          orgPolicies.value = polRes.data || {};
          if (llmLockedId.value !== null) {
            selectedLLMModel.value = llmLockedId.value;
          }
        }
    } catch (e) {
      orgPolicies.value = {};
    }
  }
  
  function getModelDisplayName(model) {
    if (model === undefined || model === null) return 'Unknown Model';
    const providerName = isDefinedAndNotEmpty(model.provider) != false ? model.provider : 'N/A';
    const variant = isDefinedAndNotEmpty(model.productVariant) != false ? model.productVariant : 'N/A';
    const version = isDefinedAndNotEmpty(model.productVersion) != false ? model.productVersion : '';
    return `${providerName} - ${variant} ${version}`.trim();
  }
  
  
  onMounted(async () => {
    fetchVoices();
    const existingUser = globalState.userInfo;
    if (existingUser !== null && existingUser !== undefined && existingUser.organizationID !== undefined && existingUser.organizationID !== null) {
      organizationID.value = existingUser.organizationID;
    } else {
      try {
        const userData = await fetchUserData();
        if (userData !== null && userData !== undefined && userData.organizationID !== undefined && userData.organizationID !== null) {
          organizationID.value = userData.organizationID;
        }
      } catch (e) {}
    }
    await loadOrgPolicies();
    await fetchLLMModels(); 
    await refreshTools();
    await loadSharedKnowledgeBases();
    await loadStyles();
    await loadDivisions();
    
     currentKbPath.value = [{ id: null, name: 'Knowledge Base' }];
  
    const editId = route.query.edit;
    if (editId !== undefined && editId !== null) {
      isEditMode.value = true;
      await refreshAgentData();
      if (Array.isArray(loadedAgentData?.agentdata?.sharedKnowledgeBaseIds) === true) {
        selectedSharedKbIds.value = loadedAgentData.agentdata.sharedKnowledgeBaseIds;
      } else if (isDefinedAndNotEmpty(loadedAgentData?.agentdata?.sharedKnowledgeBaseId) != false) {
        selectedSharedKbIds.value = [loadedAgentData.agentdata.sharedKnowledgeBaseId];
      }
      try {
        const cfg = getSecuredAxiosConfig(null);
        if (cfg !== null) {
          const bindRes = await axios.get(`${apiEndpoint}/api/whatsapp/binding/${encodePathSegment(route.query.edit)}`, cfg);
        if (bindRes.data !== null && bindRes.data !== undefined) {
          selectedWhatsappSourceId.value = bindRes.data.sourceId !== undefined ? bindRes.data.sourceId : null;
          const num = typeof bindRes.data.number === 'string' ? bindRes.data.number : (typeof bindRes.data.externalNumber === 'string' ? bindRes.data.externalNumber : (typeof bindRes.data.display === 'string' ? bindRes.data.display : ''));
          whatsappDisplayNumber.value = num;
          if (selectedDeploymentChannels.value.includes('Whatsapp Voice') === false) {
            selectedDeploymentChannels.value.push('Whatsapp Voice');
          }
        } else {
          selectedWhatsappSourceId.value = null;
          whatsappDisplayNumber.value = '';
        }
        }
      } catch (error) {}
      try {
        const cfg = getSecuredAxiosConfig(null);
        if (cfg !== null) {
          const phoneBindRes = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(route.query.edit)}`, cfg);
        if (phoneBindRes.data !== null && phoneBindRes.data !== undefined) {
          selectedPhoneSourceId.value = phoneBindRes.data.sourceId !== undefined ? phoneBindRes.data.sourceId : null;
          const pnum = typeof phoneBindRes.data.number === 'string' ? phoneBindRes.data.number : (typeof phoneBindRes.data.externalNumber === 'string' ? phoneBindRes.data.externalNumber : (typeof phoneBindRes.data.display === 'string' ? phoneBindRes.data.display : ''));
          phoneDisplayNumber.value = pnum;
          if (selectedDeploymentChannels.value.includes('Phone') === false) {
            selectedDeploymentChannels.value.push('Phone');
          }
        } else {
          selectedPhoneSourceId.value = null;
          phoneDisplayNumber.value = '';
        }
        }
      } catch (error) {}
      if (loadedAgentData?.agentdata?.setupType === 'light') {
        const agentId = route.query.edit;
        router.push({ path: '/aiChatAgentSetupLight', query: { edit: agentId } });
        return;
      }
    } else {
      
      if (availableLLMModels.value.length > 0 && selectedLLMModel.value === null) {
          const autoModelExists = availableLLMModels.value.some(model => model.id === autoLlmModelId);
          selectedLLMModel.value = autoModelExists === true ? autoLlmModelId : availableLLMModels.value[0].id;
      }
      
      enableFileUploadInChat.value = agentType.value === 'PrivateAgent';
      if (Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.length === 0) {
        selectedDeploymentChannels.value = ['Web Voice Agent'];
      }
      selectedAvatarProvider.value = 'did';
      avatarProviderConfigs.value = {
        did: defaultAvatarProviderConfig('did')
      };
    }
    
    if (isEditMode.value === true && loadedAgentData?.agentdata?.files !== undefined) {
  
        selectedAnalysisCollectionIds.value = isDefinedAndNotEmpty(loadedAgentData?.agentdata?.selectedAnalysisCollectionIds) != false ? loadedAgentData?.agentdata?.selectedAnalysisCollectionIds : [];
        
        if (selectedDivisionIds.value.length > 0) {
            await fetchFilteredCollections(selectedDivisionIds.value);
        }
  
        const fineTuneFiles = loadedAgentData.agentdata.files.filter(f => f.fileType === 'fineTuneAnswer');
        for (let i = 0; i < fineTuneFiles.length; i = i + 1) {
            const ftFile = fineTuneFiles[i];
            if (existingFiles.value.some(ef => ef.fileName === ftFile.fileName) === false) {
                existingFiles.value.push({ ...ftFile, status: 'existing' });
            }
        }
    }
   
    if (currentKbPath.value.length === 0 || currentKbPath.value[0].id !== null || currentKbPath.value[0].name !== 'Knowledge Base') {
       currentKbPath.value = [{ id: null, name: 'Knowledge Base' }];
    }
  
    await nextTick(async () => {
      await nextTick();
      if (isActive === true) {
        if (aiAgentSetupRoot.value !== null) {
          applyTranslationsToDOM(aiAgentSetupRoot.value);
        } else {
          console.error('AIChatAgentSetup.vue: aiAgentSetupRoot is null on mount. Initial translations cannot be applied to component scope.');
        }
      }
    });
  
    watch(currentLanguage, async () => {
      if (isActive === false) {
        return;
      }
      await nextTick();
      if (aiAgentSetupRoot.value !== null) {
        applyTranslationsToDOM(aiAgentSetupRoot.value);
      } else {
        console.error('AIChatAgentSetup.vue: aiAgentSetupRoot is null in language watcher. Translations cannot be applied to component scope.');
      }
    });
  
    const lightMode = route.query.lightMode;
    if (lightMode === 'true') {
      const agentId = route.query.edit;
      router.push({ path: '/aiChatAgentSetupLight', query: { edit: agentId } });
    }
    initializing.value = false;
  
    if (route.query.preview === 'left') {
      try {
        const existing = document.getElementById('setup-live-preview-container');
        if (existing !== null && isEditMode.value === true && route.query.edit !== undefined) {
          const endpointRaw = String(apiEndpoint);
          const isHttpsPage = typeof window !== 'undefined' && window.location !== undefined && window.location !== null && window.location.protocol === 'https:';
          const isHttpEndpoint = endpointRaw.startsWith('http://') === true;
          if (isHttpsPage === true && isHttpEndpoint === true) {
            toast.error('Cannot load preview with an insecure API endpoint.');
            return;
          }
          const script = document.createElement('script');
          script.src = '/publicAgentSnippet/publicChatAgent.js';
          script.async = true;
          script.setAttribute('data-agent-id', String(route.query.edit));
          script.setAttribute('data-api-endpoint', endpointRaw);
          script.setAttribute('data-target-element', '#setup-live-preview-container');
          script.setAttribute('data-open-on-load', 'true');
          while (existing.firstChild !== null) {
            existing.removeChild(existing.firstChild);
          }
          existing.appendChild(script);
        }
      } catch (e) {
        console.error('Failed to load live preview snippet:', e);
      }
    }
    hasInitializedPhoneSelection.value = true;
  });
  
  function goNextStep(currentPageNumber, direction) {
      const maxPage = 11; 
      const minPage = 1;
      let targetPage = currentPageNumber + direction;
  
      console.log(`goNextStep called. Current: ${currentPageNumber}, Direction: ${direction}, Initial Target: ${targetPage}`);
  
      
      if (currentPageNumber === 1 && direction === 1) {
          if (validateTab1() === false) {
              return; 
          }
      }
  
      while (targetPage >= minPage && targetPage <= maxPage) {
          let isVisible = true;
          if (targetPage === 2 && agentMode.value !== 'Flow') {
              isVisible = false; 
          }
          if (targetPage === 4 && isEditMode.value !== true) {
              isVisible = false; 
          }
          if (targetPage === 7 && agentMode.value === 'Flow') {
              isVisible = false;
          }
          if (targetPage === 8 && agentMode.value === 'Flow') {
              isVisible = false;
          }
          if (targetPage === 11 && showVoiceAgentTab.value !== true) {
              isVisible = false;
          }
  
          if (isVisible === true) {
              console.log(`Found visible target page: ${targetPage}`);
              break;
          }
          
          console.log(`Page ${targetPage} is hidden. Checking next page in direction ${direction}.`);
          targetPage += direction; 
      }
  
      
      if (targetPage < minPage || targetPage > maxPage) {
          console.log(`Navigation bounded. Target ${targetPage} is out of range [${minPage}-${maxPage}].`);
          return; 
      }
  
      isNavigating.value = targetPage;
      try {
          const radio = document.querySelector(`input[data-currentPage='${targetPage}']`);
          if (radio !== undefined && radio !== null) {
              console.log(`Clicking radio for page ${targetPage}`);
              radio.click(); 
          } else {
               console.error(`Radio button for page ${targetPage} not found.`);
          }
      } finally {
          setTimeout(() => { 
              isNavigating.value = null; 
              const currentTabPanel = document.querySelector(`.tab-content[data-currentPage='${targetPage}']`);
              const tabContents = document.querySelectorAll('.tab-content');
              for (let i = 0; i < tabContents.length; i = i + 1) {
                  const el = tabContents[i];
                  el.classList.remove('active');
              }
              if(currentTabPanel !== null) currentTabPanel.classList.add('active');
          }, 100); 
      }
  }
  
  function pushValidatedRule(rawRule, fieldName = 'Rule') {
    const validation = secureUserText(rawRule, {
      fieldName: fieldName,
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: true
    });
    if (validation.error !== null) {
      toast.error(validation.error);
      return false;
    }
    rules.value.push(validation.value);
    return true;
  }
  
  function addRule() {
    if (newRule.value.trim().length === 0) {
      return;
    }
    const added = pushValidatedRule(newRule.value, 'Rule');
    if (added === true) {
      newRule.value = '';
    }
  }
  
  function addRuleFromLibrary(ruleText) {
    if (typeof ruleText !== 'string' || ruleText.trim().length === 0) {
      return;
    }
    pushValidatedRule(ruleText, 'Library Rule');
  }

  function approveGeneratedRulesFromAdvanced(generatedRules) {
    const source = Array.isArray(generatedRules) === true ? generatedRules : [];
    let addedCount = 0;
    for (let index = 0; index < source.length; index = index + 1) {
      const ruleText = typeof source[index] === 'string' ? source[index].trim() : '';
      if (ruleText.length === 0) {
        continue;
      }
      if (rules.value.includes(ruleText) === true) {
        continue;
      }
      const added = pushValidatedRule(ruleText, 'Generated Rule');
      if (added === true) {
        addedCount = addedCount + 1;
      }
    }
    if (addedCount > 0) {
      toast.success(`${addedCount} generated rules approved. Click Save or Save & Close to persist them.`);
    }
  }
  
  function removeRule(index) {
    rules.value.splice(index, 1);
  }
  
  function removeTool(index) {
    const toolId = tools.value[index].toolid;
    tools.value.splice(index, 1);
    const toolIdIndex = selectedToolIds.value.indexOf(toolId);
    if (toolIdIndex > -1) {
      selectedToolIds.value.splice(toolIdIndex, 1);
    }
  }
  
  function addToolEndpoint(toolIndex) {
    tools.value[toolIndex].apiEndpoints.push({
      url: '',
      method: 'GET'
    });
  }
  
  function removeToolEndpoint(toolIndex, endpointIndex) {
    tools.value[toolIndex].apiEndpoints.splice(endpointIndex, 1);
  }
  
  function startEditingRule(index) {
    editingRuleIndex.value = index;
    console.log(`Started editing rule ${index}, initial direction set to: ${inputDirection.value}`);
    nextTick(() => {
      let el = editingRuleInput.value;
      if (Array.isArray(el) === true) {
        el = el.length > 0 ? el[0] : null;
      }
      if (el !== null && el !== undefined) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
        el.focus();
      }
    });
  }
  
  function stopEditingRule() {
    editingRuleIndex.value = null;
    inputDirection.value = 'ltr';
  }
  
  function addRequiredParam(toolIndex) {
    if (tools.value[toolIndex].requiredParams === undefined || tools.value[toolIndex].requiredParams.length === 0) {
      tools.value[toolIndex].requiredParams = [];
    }
    tools.value[toolIndex].requiredParams.push('');
  }
  
  function removeRequiredParam(toolIndex, paramIndex) {
    tools.value[toolIndex].requiredParams.splice(paramIndex, 1);
  }
  
  async function validateFunction(index) {
    const tool = tools.value[index];
    tool.validationError = null;
    const cfg = getSecuredAxiosConfig(null);
    if (cfg === null) {
      tool.validationError = 'Authentication required';
      toast.error('Function validation failed');
      return;
    }
    const functionConfig = typeof tool.functionConfig === 'string' ? tool.functionConfig : '';
    try {
      const res = await axios.post(`${apiEndpoint}/api/tools/validateFunctionConfig`, { functionConfig: functionConfig }, cfg);
      const valid = res !== undefined && res.data !== undefined && res.data.valid === true;
      if (valid === true) {
        tool.validationError = null;
        toast.success('Function validated successfully');
        return;
      }
      const serverError = res !== undefined && res.data !== undefined && typeof res.data.error === 'string' ? res.data.error : 'Function validation failed';
      tool.validationError = serverError;
      toast.error('Function validation failed');
    } catch (error) {
      const serverErr = error !== undefined && error !== null && error.response !== undefined && error.response !== null && error.response.data !== undefined && error.response.data !== null && typeof error.response.data.error === 'string' ? error.response.data.error : null;
      const msg = serverErr !== null ? serverErr : (error !== undefined && error !== null && typeof error.message === 'string' ? error.message : 'Function validation failed');
      tool.validationError = msg;
      toast.error('Function validation failed');
    }
  }
  
  function validateTab1() {
    let isValid = true;
  
    agentNameError.value = false;
    agentNameErrorMessage.value = '';
    agentDescriptionError.value = false;
    agentDescriptionErrorMessage.value = '';
    agentShortDescriptionError.value = false;
    agentShortDescriptionErrorMessage.value = '';
    startingMessageError.value = false;
    startingMessageErrorMessage.value = '';
    divisionError.value = false;
    divisionErrorMessage.value = '';
  
    const sanitizedName = secureUserText(agentName.value, {
      fieldName: 'Agent Name',
      maxLength: 100,
      disallowAngleBrackets: true
    });
    if (sanitizedName.error !== null) {
      agentNameError.value = true;
      agentNameErrorMessage.value = sanitizedName.error;
      isValid = false;
    } else {
      agentName.value = sanitizedName.value;
    }
    const sanitizedDescription = secureUserText(agentDescription.value, {
      fieldName: 'Agent Description',
      allowNewLines: true,
      maxLength: 200000
    });
    if (sanitizedDescription.error !== null) {
      agentDescriptionError.value = true;
      agentDescriptionErrorMessage.value = sanitizedDescription.error;
      isValid = false;
    } else {
      agentDescription.value = sanitizedDescription.value;
    }
    const shortDescriptionValidation = secureUserText(agentShortDescription.value, {
      fieldName: 'Agent Short Description',
      allowNewLines: true,
      maxLength: 250,
      disallowAngleBrackets: true,
      allowEmpty: agentType.value !== 'PrivateAgent'
    });
    if (shortDescriptionValidation.error !== null) {
      agentShortDescriptionError.value = true;
      agentShortDescriptionErrorMessage.value = shortDescriptionValidation.error;
      isValid = false;
    } else {
      agentShortDescription.value = shortDescriptionValidation.value;
    }

    if (agentMode.value !== 'Flow') {
      const sanitizedStarting = sanitizeLocalizationObject(startingMessage.value, {
        fieldName: 'Starting Message',
        allowNewLines: true,
        maxLength: 2000,
        disallowAngleBrackets: false
      });
      if (sanitizedStarting.error !== null) {
        startingMessageError.value = true;
        startingMessageErrorMessage.value = sanitizedStarting.error;
        isValid = false;
      } else if (typeof sanitizedStarting.sanitized.default !== 'string' || sanitizedStarting.sanitized.default.length === 0) {
        startingMessageError.value = true;
        startingMessageErrorMessage.value = 'Starting Message (in Default) is required.';
        isValid = false;
      } else {
        startingMessage.value = sanitizedStarting.sanitized;
      }
    } else {
      const sanitizedFlowStarting = sanitizeLocalizationObject(startingMessage.value, {
        fieldName: 'Starting Message',
        allowNewLines: true,
        maxLength: 2000,
        disallowAngleBrackets: false,
        allowEmpty: true
      });
      if (sanitizedFlowStarting.error === null) {
        startingMessage.value = sanitizedFlowStarting.sanitized;
      }
    }

    if (selectedDivisionIds.value.length === 0) {
      divisionError.value = true;
      divisionErrorMessage.value = 'At least one Division must be selected.';
      isValid = false;
    }
    if (selectedLLMModel.value === null || selectedLLMModel.value === '') {
          toast.error('LLM Model must be selected.');
          isValid = false;
      }
    const sanitizedRules = sanitizeRuleEntries(rules.value);
    if (sanitizedRules.error !== null) {
      toast.error(sanitizedRules.error);
      isValid = false;
    } else {
      rules.value = sanitizedRules.sanitized;
    }
    return isValid;
  }
  
  const createAgent = async (shouldRedirect = false) => {
    
    if (validateTab1() === false) {
  
      const firstTabRadio = document.querySelector('input[data-currentPage="1"]');
      const firstTabDiv = document.querySelector('div[data-currentPage="1"]');
      if (firstTabRadio !== null && firstTabRadio !== undefined) {
        firstTabRadio.checked = true;
        if(firstTabDiv !== null && firstTabDiv !== undefined) firstTabDiv.classList.add('active');
      }
      return;
    }
  
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg !== null) {
        const userRes = await axios.get(`${apiEndpoint}/api/users/me`, cfg);
        const orgId = userRes?.data?.organizationID;
        if (Number.isFinite(Number(orgId)) === true) {
          const polRes = await axios.get(`${apiEndpoint}/api/agents/policies/${encodePathSegment(orgId)}`, cfg);
        const policies = polRes.data;
        if (Array.isArray(policies?.features?.allowedLlmModelIds) === true && policies.features.allowedLlmModelIds.length > 0) {
          if (policies.features.allowedLlmModelIds.includes(selectedLLMModel.value) === false) {
            const firstAllowed = availableLLMModels.value.find(m => policies.features.allowedLlmModelIds.includes(m.id) === true);
            if (firstAllowed !== undefined) {
              selectedLLMModel.value = firstAllowed.id;
              toast.info('Selected LLM is not allowed for your organization. Switched to the first allowed model.');
            } else {
              toast.error('No allowed LLM models configured for your organization.');
              isSaving.value = false;
              showLoadingModal.value = false;
              return;
            }
          }
        }
        const limits = policies?.limits || {};
        if (Number.isFinite(Number(limits.maxInputTokensForSetup)) === true && Number(limits.maxInputTokensForSetup) > 0) {
          if (agentDescription.value.length > limits.maxInputTokensForSetup) {
            agentDescription.value = agentDescription.value.substring(0, limits.maxInputTokensForSetup);
            toast.info('System prompt was truncated to fit your organization limit.');
          }
        }
      }
      }
    } catch (e) {}

    const sanitizedInappropriateContent = sanitizeLocalizationObject(inappropriateContentMessage.value, {
      fieldName: 'Inappropriate Content Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: true,
      allowEmpty: true
    });
    if (sanitizedInappropriateContent.error !== null) {
      toast.error(sanitizedInappropriateContent.error);
      return;
    }
    inappropriateContentMessage.value = sanitizedInappropriateContent.sanitized;

    const sanitizedDontKnowMessage = sanitizeLocalizationObject(dontKnowMessage.value, {
      fieldName: 'Fallback Answer Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: true,
      allowEmpty: true
    });
    if (sanitizedDontKnowMessage.error !== null) {
      toast.error(sanitizedDontKnowMessage.error);
      return;
    }
    dontKnowMessage.value = sanitizedDontKnowMessage.sanitized;

    const sanitizedLimitReached = sanitizeOptionalText(limitReachedMessage.value, 'Limit Reached Message', {
      allowNewLines: true,
      maxLength: 500,
      disallowAngleBrackets: true
    });
    if (sanitizedLimitReached.error !== null) {
      toast.error(sanitizedLimitReached.error);
      return;
    }
    limitReachedMessage.value = sanitizedLimitReached.value;

    const sanitizedWhatsappClosing = sanitizeOptionalText(whatsappClosingMessage.value, 'WhatsApp closing message', {
      maxLength: 300,
      disallowAngleBrackets: true
    });
    if (sanitizedWhatsappClosing.error !== null) {
      toast.error(sanitizedWhatsappClosing.error);
      return;
    }
    whatsappClosingMessage.value = sanitizedWhatsappClosing.value;

    const sanitizedVoicePrompt = sanitizeOptionalText(voiceAgentExtraPrompt.value, 'Voice Agent prompt', {
      allowNewLines: true,
      maxLength: 4000
    });
    if (sanitizedVoicePrompt.error !== null) {
      toast.error(sanitizedVoicePrompt.error);
      return;
    }
    voiceAgentExtraPrompt.value = sanitizedVoicePrompt.value;

    const sanitizedAuthWelcome = sanitizeOptionalText(authenticatorWelcomeMessage.value, 'Authenticator welcome message', {
      allowNewLines: true,
      maxLength: 1000
    });
    if (sanitizedAuthWelcome.error !== null) {
      toast.error(sanitizedAuthWelcome.error);
      return;
    }
    authenticatorWelcomeMessage.value = sanitizedAuthWelcome.value;

    const sanitizedAuthRetry = sanitizeOptionalText(authenticatorRetryMessage.value, 'Authenticator retry message', {
      allowNewLines: true,
      maxLength: 1000
    });
    if (sanitizedAuthRetry.error !== null) {
      toast.error(sanitizedAuthRetry.error);
      return;
    }
    authenticatorRetryMessage.value = sanitizedAuthRetry.value;

    const sanitizedAuthSuccess = sanitizeOptionalText(authenticatorSuccessMessage.value, 'Authenticator success message', {
      allowNewLines: true,
      maxLength: 1000
    });
    if (sanitizedAuthSuccess.error !== null) {
      toast.error(sanitizedAuthSuccess.error);
      return;
    }
    authenticatorSuccessMessage.value = sanitizedAuthSuccess.value;

    const sanitizedActionButtons = sanitizeActionButtonsList(actionButtons.value);
    if (sanitizedActionButtons.error !== null) {
      toast.error(sanitizedActionButtons.error);
      return;
    }
    actionButtons.value = sanitizedActionButtons.sanitized;

    const sanitizedCustomRegex = sanitizeRegexEntries(customBlockRegex.value);
    if (sanitizedCustomRegex.error !== null) {
      toast.error(sanitizedCustomRegex.error);
      return;
    }
    customBlockRegex.value = sanitizedCustomRegex.sanitized;
  
    const baseCfg = getSecuredAxiosConfig({ timeout: 0 });
    if (baseCfg === null) {
      return;
    }
    modalState.type = 'loading';
    modalState.title = isEditMode.value ? 'Updating Agent...' : 'Creating Agent...';
    modalState.message = '';
    showLoadingModal.value = true;
    startLoadingTips();
    isSaving.value = true;
    isSavingAndRedirecting.value = shouldRedirect;
    
    let flowData = null;
    if (agentMode.value === 'Flow' && llmFlowRef.value !== null) {
        try {
          flowData = llmFlowRef.value.getFlowData();
          console.log("Retrieved Flow Data:", flowData);
        } catch (err) {
            console.error("Error getting flow data:", err);
        }
    } else if (agentMode.value === 'FlowBased') {
        flowData = flowBasedFlow.value;
    }
  
    try {
      const hasNewFiles = uploadedFiles.value.length > 0;
      const agentPayload = {
        aiAgentName: agentName.value,
        divisionIds: selectedDivisionIds.value,
        aiAgentStatus: agentActive.value === true ? 'active' : 'inactive',
        agentType: agentType.value,
        agentMode: agentMode.value,
        agentShortDescription: agentShortDescription.value,
        agentData: {
          description: agentDescription.value,
          qna: agentQnA.value,
          startingMessage: startingMessage.value,
          language: agentLanguage.value,
          rules: rules.value,
          styleGuid: selectedStyleGuid.value,
          avatarProvider: selectedAvatarProvider.value,
          avatarProviderConfigs: avatarProviderConfigs.value,
          agenticBehaviour: agenticBehaviour.value,
          voiceSettings: {
            voiceType: normalizeVoiceSettingsVoiceType(voiceSettingsVoiceType.value),
            vadEngine: normalizeVoiceSettingsVadEngine(voiceSettingsVadEngine.value),
            voiceId: resolvedVoiceIdForCalls.value,
            voiceSupplier: '',
            voiceModel: '',
            ttsId: '',
            accent: voiceSettingsAccent.value,
            language: voiceSettingsLanguage.value,
            speed: Number.isFinite(Number(voiceSettingsSpeed.value)) === true ? Number(voiceSettingsSpeed.value) : 1,
            promptToVoiceEnabled: voiceSettingsPromptToVoiceEnabled.value === true,
            promptToVoicePrompt: typeof voiceSettingsPromptToVoicePrompt.value === 'string' ? voiceSettingsPromptToVoicePrompt.value : '',
            tone: voiceSettingsTone.value,
            mode: voiceSettingsMode.value,
            cancellingMode: normalizeVoiceSettingsCancellingMode(voiceSettingsCancellingMode.value),
            audioProcessorEngine: normalizeVoiceSettingsAudioProcessorEngine(voiceSettingsAudioProcessorEngine.value),
            idleAutoResponseEnabled: voiceSettingsIdleAutoResponseEnabled.value === true,
            startingMessageIdleAutoResponseSeconds: Number.isFinite(Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value)) === true && Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value) >= 1 ? Math.min(300, Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value)) : 6,
            idleAutoResponseSeconds: Number.isFinite(Number(voiceSettingsIdleAutoResponseSeconds.value)) === true && Number(voiceSettingsIdleAutoResponseSeconds.value) >= 1 ? Math.min(300, Number(voiceSettingsIdleAutoResponseSeconds.value)) : 3,
            toolUseAutoResponseSeconds: Number.isFinite(Number(voiceSettingsToolUseAutoResponseSeconds.value)) === true && Number(voiceSettingsToolUseAutoResponseSeconds.value) >= 0 ? Math.min(300, Number(voiceSettingsToolUseAutoResponseSeconds.value)) : 2,
            termsPackages: Array.isArray(voiceSettingsTermsPackages.value) === true ? voiceSettingsTermsPackages.value : [],
            sonioxContext: {
              terms: Array.isArray(voiceSettingsSonioxTerms.value) === true ? voiceSettingsSonioxTerms.value : []
            },
            transcriptionOptimizer: {
              enabled: voiceSettingsTranscriptionOptimizerEnabled.value === true,
              terms: Array.isArray(voiceSettingsTranscriptionOptimizerTerms.value) === true ? voiceSettingsTranscriptionOptimizerTerms.value : []
            }
          },
          flow: flowData,
          cachedFileNames: cachedFileNames.value, 
          files: [ 
            ...existingFiles.value.map(file => ({ 
              fileName: file.fileName,
              fileType: file.fileType,
              status: file.status,
              url: file.url,
              maxPages: file.maxPages,
              rescrapeEnabled: file.rescrapeEnabled === true ? true : false,
              rescrapeIntervalDays: (Number.isFinite(Number(file.rescrapeIntervalDays)) === true && Number(file.rescrapeIntervalDays) >= 1) ? Number(file.rescrapeIntervalDays) : undefined,
              rescrapeAtHour: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcHourFromLocalTime(file.rescrapeAtTime) : undefined,
              rescrapeAtMinute: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcMinuteFromLocalTime(file.rescrapeAtTime) : undefined,
              originalQuery: file.originalQuery,
              originalResponse: file.originalResponse,
              fineTunedAnswer: file.fineTunedAnswer,
              hiddenFromSources: file.hiddenFromSources === true ? true : false
            })),
            ...uploadedFiles.value.map(file => ({ 
              fileName: file.fileName,
              fileType: file.fileType,
              fileData: file.fileData,
              status: 'new',
              url: file.url,
              content: file.content,
              maxPages: file.maxPages,
              rescrapeEnabled: file.rescrapeEnabled === true ? true : false,
              rescrapeIntervalDays: (Number.isFinite(Number(file.rescrapeIntervalDays)) === true && Number(file.rescrapeIntervalDays) >= 1) ? Number(file.rescrapeIntervalDays) : undefined,
              rescrapeAtHour: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcHourFromLocalTime(file.rescrapeAtTime) : undefined,
              rescrapeAtMinute: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcMinuteFromLocalTime(file.rescrapeAtTime) : undefined,
              hiddenFromSources: file.hiddenFromSources === true ? true : false
            }))
          ],
          selectedToolIds: selectedToolIds.value,
          selectedLlmModelId: resolvedSelectedLLMModelId.value, 
          configurations: {
            enableFeedbackButtons: enableFeedbackButtons.value,
            enableSuggestedResponses: enableSuggestedResponses.value,
            enableStreamingText: enableStreamingText.value !== false,
           enableUserFineTune: enableUserFineTune.value === true,
           retrieveUserLocation: retrieveUserLocation.value,
           enableFileUploadInChat: enableFileUploadInChat.value,
           makeLinksClickable: makeLinksClickable.value,
          clearSessionOnRefresh: clearSessionOnRefresh.value,
          enabledSavedSessionHistory: enabledSavedSessionHistory.value === true,
           showMap: showMap.value !== false,
           allowCallControl: allowCallControl.value === true,
           inappropriateContentMessage: inappropriateContentMessage.value,
           dontKnowMessage: dontKnowMessage.value,
           limitReachedMessage: limitReachedMessage.value,
           messageLimitPerConversation: (limitMessagesPerConversationEnabled.value === true) && (Number.isFinite(Number(messageLimitPerConversation.value)) != false) && (Number(messageLimitPerConversation.value) >= 0) ? Number(messageLimitPerConversation.value) : 0,
           messageLimitPerIP: (limitMessagesPerIPEnabled.value === true) && (Number.isFinite(Number(messageLimitPerIP.value)) != false) && (Number(messageLimitPerIP.value) >= 0) ? Number(messageLimitPerIP.value) : 0,
           enablePIIRedaction: enablePIIRedaction.value === true,
           redactEmails: redactEmails.value === true,
           redactPhoneNumbers: redactPhoneNumbers.value === true,
          customBlockRegex: customBlockRegex.value,
            deploymentChannels: selectedDeploymentChannels.value,
            agentToneId: selectedAgentTone.value,
            whatsappClosingMessage: whatsappClosingMessage.value,
            retrieverKValue: Number.isFinite(Number(retrieverKValue.value)) === true && Number(retrieverKValue.value) >= 1 ? Number(retrieverKValue.value) : 30,
            maxOutputTokens: Number.isFinite(Number(maxOutputTokens.value)) === true && Number(maxOutputTokens.value) >= 1 ? Number(maxOutputTokens.value) : 2048,
            actionButtons: actionButtons.value,
            verifyAnswerEnabled: verifyAnswerEnabled.value === true,
            verifyLinks: verifyLinks.value === true,
            verificationModelId: typeof selectedVerificationLLMModel.value === 'string' && selectedVerificationLLMModel.value.length > 0 ? selectedVerificationLLMModel.value : undefined,
            authenticator: {
              enabled: authenticatorEnabled.value === true,
              provider: typeof authenticatorProvider.value === 'string' && authenticatorProvider.value.trim().length > 0 ? authenticatorProvider.value : 'google',
              welcomeMessageTemplate: typeof authenticatorWelcomeMessage.value === 'string' && authenticatorWelcomeMessage.value.trim().length > 0 ? authenticatorWelcomeMessage.value : defaultAuthenticatorWelcomeTemplate,
              retryMessageTemplate: typeof authenticatorRetryMessage.value === 'string' && authenticatorRetryMessage.value.trim().length > 0 ? authenticatorRetryMessage.value : defaultAuthenticatorRetryTemplate,
              successMessageTemplate: typeof authenticatorSuccessMessage.value === 'string' && authenticatorSuccessMessage.value.trim().length > 0 ? authenticatorSuccessMessage.value : defaultAuthenticatorSuccessTemplate,
              runAiToolOnLogin: authenticatorRunAiToolOnLogin.value === true
            },
            limitMessagesPerConversationEnabled: limitMessagesPerConversationEnabled.value === true,
            limitMessagesPerIPEnabled: limitMessagesPerIPEnabled.value === true,
            phoneVoiceId: resolvedVoiceIdForCalls.value,
            voiceAgent: (isPhoneChannelSelected.value === true || showVoiceAgentTab.value === true) ? {
              provider: voiceAgentProvider.value,
              voice: resolvedVoiceIdForCalls.value,
              extraPrompt: voiceAgentExtraPrompt.value
            } : undefined
         },
          selectedVectorDBId: typeof selectedVectorDBId.value === 'string' && selectedVectorDBId.value.length > 0 ? selectedVectorDBId.value : undefined,
          selectedEmbeddingModelId: typeof selectedEmbeddingModelId.value === 'string' && selectedEmbeddingModelId.value.length > 0 ? selectedEmbeddingModelId.value : undefined,
          selectedAnalysisCollectionIds: selectedAnalysisCollectionIds.value,
          sharedKnowledgeBaseIds: Array.isArray(selectedSharedKbIds.value) === true ? selectedSharedKbIds.value : [],
        
          knowledgeBaseFolders: knowledgeBaseFolders.value,
          knowledgeBaseFileAssignments: knowledgeBaseFileAssignments.value,
        }
      };
      
      const headers = baseCfg.headers;
  
      let response;
      if (isEditMode.value === true) {
        const agentIdRaw = route.query.edit;
        const agentId = agentIdRaw !== undefined && agentIdRaw !== null ? String(agentIdRaw) : '';
        if (agentId.length === 0) {
          throw new Error('Missing agent id');
        }
        response = await axios.put(`${apiEndpoint}/modifyAIChat/${encodePathSegment(agentId)}`, agentPayload, baseCfg);
        try {
          const sidVal = selectedWhatsappSourceId.value;
          if (selectedDeploymentChannels.value.includes('Whatsapp Voice') === true) {
            const coerced = Number.isFinite(Number(sidVal)) === true ? Number(sidVal) : null;
            if (coerced !== null) {
              await axios.post(`${apiEndpoint}/api/whatsapp/bind`, { agentId: agentId, sourceId: coerced }, { ...baseCfg, timeout: undefined });
            }
          } else {
            const sidNum = Number(sidVal);
            if (Number.isFinite(sidNum) === true && sidNum > 0) {
              await axios.delete(`${apiEndpoint}/api/whatsapp/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: sidNum } });
            } else {
              const currentBind = await axios.get(`${apiEndpoint}/api/whatsapp/binding/${encodePathSegment(agentId)}`, { ...baseCfg, timeout: undefined });
              const boundId = Number(currentBind?.data?.sourceId);
              if (Number.isFinite(boundId) === true && boundId > 0) {
                await axios.delete(`${apiEndpoint}/api/whatsapp/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: boundId } });
              }
            }
          }
        } catch (error) {}
        try {
          const phoneSidVal = (selectedPhoneSourceId !== undefined && selectedPhoneSourceId !== null && typeof selectedPhoneSourceId === 'object') ? selectedPhoneSourceId.value : selectedPhoneSourceId;
          if (selectedDeploymentChannels.value.includes('Phone') === true) {
            const coercedPhone = Number.isFinite(Number(phoneSidVal)) === true ? Number(phoneSidVal) : null;
            if (coercedPhone !== null) {
              await axios.post(`${apiEndpoint}/api/phone/bind`, { agentId: agentId, sourceId: coercedPhone }, { ...baseCfg, timeout: undefined });
            }
          } else {
            const phoneSidNum = Number(phoneSidVal);
            if (Number.isFinite(phoneSidNum) === true && phoneSidNum > 0) {
              await axios.delete(`${apiEndpoint}/api/phone/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: phoneSidNum } });
            } else {
              const currentPhoneBind = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(agentId)}`, { ...baseCfg, timeout: undefined });
              const phoneBoundId = Number(currentPhoneBind?.data?.sourceId);
              if (Number.isFinite(phoneBoundId) === true && phoneBoundId > 0) {
                await axios.delete(`${apiEndpoint}/api/phone/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: phoneBoundId } });
              }
            }
          }
        } catch (error) {}
        try {
          if (immediateRescrapeRequested.value === true && Array.isArray(immediateRescrapeQueue.value) === true && immediateRescrapeQueue.value.length > 0) {
            await runImmediateRescrape(agentId);
          }
        } catch (error) {}
        toast.success('Agent updated successfully'); 
      } else {
        response = await axios.post(`${apiEndpoint}/createNewAIChat`, agentPayload, baseCfg);
        try {
          const rawNewId = isDefinedAndNotEmpty(response?.data?.agentid) != false ? response.data.agentid : (isDefinedAndNotEmpty(response?.data?.agentId) != false ? response.data.agentId : null);
          const newIdStr = rawNewId !== null && rawNewId !== undefined ? String(rawNewId) : '';
          if (newIdStr.length > 0 && selectedDeploymentChannels.value.includes('Whatsapp Voice') === true) {
            const sidVal = selectedWhatsappSourceId.value;
            const coerced = Number.isFinite(Number(sidVal)) === true ? Number(sidVal) : null;
            if (coerced !== null) {
              await axios.post(`${apiEndpoint}/api/whatsapp/bind`, { agentId: newIdStr, sourceId: coerced }, { ...baseCfg, timeout: undefined });
            }
          }
          if (newIdStr.length > 0 && selectedDeploymentChannels.value.includes('Phone') === true) {
            const phoneSidVal = (selectedPhoneSourceId !== undefined && selectedPhoneSourceId !== null && typeof selectedPhoneSourceId === 'object') ? selectedPhoneSourceId.value : selectedPhoneSourceId;
            const coercedPhone = Number.isFinite(Number(phoneSidVal)) === true ? Number(phoneSidVal) : null;
            if (coercedPhone !== null) {
              await axios.post(`${apiEndpoint}/api/phone/bind`, { agentId: newIdStr, sourceId: coercedPhone }, { ...baseCfg, timeout: undefined });
            }
          }
          if (newIdStr.length > 0 && immediateRescrapeRequested.value === true && Array.isArray(immediateRescrapeQueue.value) === true && immediateRescrapeQueue.value.length > 0) {
            await runImmediateRescrape(newIdStr);
          }
        } catch (error) {}
        toast.success('Agent created successfully'); 
      }
      console.log("Agent save/create response:", response.data);
  
      stopLoadingTips();
      showLoadingModal.value = false; 
      isSaving.value = false;
  
       uploadedFiles.value = []; 
      
      if (shouldRedirect === true) {
          await new Promise(resolve => setTimeout(resolve, 500)); 
          
          const newAgentId = isDefinedAndNotEmpty(response.data.agentid) != false ? response.data.agentid : isDefinedAndNotEmpty(response.data.agentId) != false ? response.data.agentId : response.data;
          const query = { agentId: newAgentId };
          if (hasNewFiles === true) {
            query.processing = 'true';
          }
          router.push({ path: '/aiChatAgents', query }); 
      } else {
          toast.info('Agent saved. Refreshing statuses...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          await refreshAgentData();
          toast.success('Agent saved and statuses refreshed!');
      }
  
    } catch (error) {
      stopLoadingTips(); 
      isSaving.value = false;
      isSavingAndRedirecting.value = false;
      showLoadingModal.value = true; 
      
      const statusCode = isDefinedAndNotEmpty(error.response?.status) != false ? error.response?.status : 0;
      const errorData = isDefinedAndNotEmpty(error.response?.data) != false ? error.response?.data : {};
      const errorMessageText = isDefinedAndNotEmpty(errorData?.message) != false ? errorData?.message : isDefinedAndNotEmpty(errorData?.error) != false ? errorData?.error : 'An unexpected error occurred.';
      console.error("Error saving agent:", isDefinedAndNotEmpty(error.response) != false ? error.response : error);
  
      modalState.type = 'error';
      modalState.title = 'Error Saving Agent';
      if (statusCode === 400 && typeof error.response?.data?.error === 'object') {
          modalState.message = "Validation errors occurred. Please check your input.";
      } else if (statusCode === 400 || statusCode === 409) {
         modalState.message = errorMessageText;
      } else {
        modalState.message = errorMessageText;
        if (statusCode >= 500) {
            modalState.message += " Please try again later or contact support.";
        }
      }
    } 
  };
  
  const saveAgentWithoutRedirect = async () => {
      if (isEditMode.value === true) {
          await createAgent(false);
      } else {
          toast.info("Please save the agent first to enable this action.");
      }
  };
  
  async function addWebsiteLink() {
    if (websiteLink.value.trim().length > 0) {
      const newFile = {
        fileName: websiteLink.value,
        fileType: 'websiteurl',
         status: 'new',
        url: websiteLink.value,
        rescrapeEnabled: false,
        rescrapeIntervalDays: undefined,
        rescrapeAtTime: '',
        hiddenFromSources: false
      };
    
      const isDuplicate = uploadedFiles.value.some(f => f.fileName === newFile.fileName) != false || 
                          existingFiles.value.some(f => f.fileName === newFile.fileName && f.status !== 'deleted') != false;
      if (isDuplicate === false) {
           uploadedFiles.value.push(newFile); 
          knowledgeBaseFileAssignments.value[newFile.fileName] = currentFolderId.value;
          showWebsiteLinkModal.value = false;
          websiteLink.value = '';
      } else {
          toast.warning('Website link already exists.');
      }
    }
  }
  
  function addWebsiteBatchUrls() {
    const text = websiteBatchText.value;
    if (text.trim().length === 0) {
      toast.error('Please enter at least one URL.');
      return;
    }
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    let added = 0;
    let skipped = 0;
    for (let i = 0; i < lines.length; i = i + 1) {
      const url = lines[i];
      const isHttp = (url.startsWith('http://') === true) || (url.startsWith('https://') === true);
      if (isHttp === false) {
        skipped = skipped + 1;
        continue;
      }
      const isDuplicate = (uploadedFiles.value.some(f => f.fileName === url) === true) || (existingFiles.value.some(f => f.fileName === url && f.status !== 'deleted') === true);
      if (isDuplicate === false) {
        const newFile = { fileName: url, fileType: 'websiteurl', status: 'new', url: url, rescrapeEnabled: rescrapeEnabled.value === true, rescrapeIntervalDays: rescrapeIntervalDays.value, rescrapeAtTime: typeof rescrapeAtHour.value === 'number' ? String(rescrapeAtHour.value).padStart(2,'0') + ':00' : '' , hiddenFromSources: false };
        uploadedFiles.value.push(newFile);
        knowledgeBaseFileAssignments.value[newFile.fileName] = currentFolderId.value;
        added = added + 1;
      } else {
        skipped = skipped + 1;
      }
    }
    showWebsiteBatchModal.value = false;
    websiteBatchText.value = '';
    if (added > 0) {
      toast.success(added + ' URL(s) added.');
    }
    if (skipped > 0) {
      toast.info(skipped + ' URL(s) skipped.');
    }
  }
  
  async function scrapeWebsite() {
    if (websiteToScrape.value.trim().length > 0) {
       const newFile = {
          fileName: websiteToScrape.value,
          fileType: 'website',
          status: 'new',
          url: websiteToScrape.value,
          maxPages: isDefinedAndNotEmpty(maxPages.value) != false ? maxPages.value : undefined,
          rescrapeEnabled: false,
          rescrapeIntervalDays: undefined,
          rescrapeAtTime: '',
          hiddenFromSources: false
        };
      const isDuplicate = uploadedFiles.value.some(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === newFile.fileName) != false || 
                          existingFiles.value.some(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === newFile.fileName && f.status !== 'deleted') != false;
      if (isDuplicate === false) {
          uploadedFiles.value.push(newFile);
          knowledgeBaseFileAssignments.value[newFile.fileName] = currentFolderId.value;
          showWebsiteScrapeModal.value = false;
          websiteToScrape.value = '';
          maxPages.value = '';
          
      } else {
          toast.warning('Website scrape target already exists.');
      }
    }
  }
  
  async function addTextContent() {
    if (textFileName.value.trim().length === 0 || textContent.value.trim().length === 0) {
      toast.error('Please enter both file name and content for text input.');
      return;
    }
  
      const fileName = textFileName.value.endsWith('.txt') === true ? textFileName.value : `${textFileName.value}.txt`;
      const isDuplicateInUploaded = uploadedFiles.value.some(f => f.fileName === fileName) != false;
      const isDuplicateInExisting = existingFiles.value.some(f => f.fileName === fileName && f.status !== 'deleted');
  
    if (isEditingTextFile.value === false && (isDuplicateInUploaded === true || isDuplicateInExisting === true)) {
      toast.error('A file or text input with this name already exists.');
      return;
    }
  
        const textBlob = new Blob([textContent.value], { type: 'text/plain' });
        if (textBlob.size > maxFileSizeBytes) {
            toast.error(`Text content for "${fileName}" exceeds the ${maxFileSizeMb}MB size limit.`);
            return;
        }
  
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Data = e.target.result.split(',')[1];
          const newFileEntry = {
            fileName: fileName,
            fileType: 'text',
             status: 'new',
             content: textContent.value,
        fileData: base64Data,
        isEditedVersion: false, 
        replacesFile: null,
        hiddenFromSources: false
          };
          uploadedFiles.value.push(newFileEntry);
          knowledgeBaseFileAssignments.value[newFileEntry.fileName] = currentFolderId.value;
  
      if (isEditingTextFile.value === true && originalTextFileToEdit.value !== null) {
        const originalFile = originalTextFileToEdit.value;
        newFileEntry.isEditedVersion = true;
        newFileEntry.replacesFile = originalFile.fileName;
  
        if (originalFile.status === 'new') {
          const idx = uploadedFiles.value.findIndex(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === originalFile.fileName);
          if (idx > -1) uploadedFiles.value.splice(idx, 1);
        } else if (originalFile.status === 'existing') {
          const existingIdx = existingFiles.value.findIndex(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === originalFile.fileName);
          if (existingIdx > -1) existingFiles.value[existingIdx].status = 'deleted';
        }
        
        if (isCached(originalFile.fileName) != false) {
          toggleCacheFile(originalFile); 
          toggleCacheFile(newFileEntry); 
        }
        toast.success(`File "${originalFile.fileName}" updated to "${fileName}". Old version marked for deletion.`);
      } else {
        toast.success('Text content added successfully.');
      }
          
          showTextInputModal.value = false;
          textFileName.value = '';
          textContent.value = '';
      isEditingTextFile.value = false;
      originalTextFileToEdit.value = null;
        };
        reader.readAsDataURL(textBlob);
  }
  
  
  const refreshTools = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        availableTools.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/tools/getAllAITools`, {
        ...cfg
      });
      console.log('Available tools:', response.data);
      availableTools.value = response.data;
    } catch (error) {
      handleApiError(error, 'loading tools');
    }
  };
  
  const isToolEnabled = (toolId) => {
    return selectedToolIds.value.includes(toolId) === true;
  };
  
  function addSelectedTool() {
    if (selectedTool.value !== '') {
      const toolToAdd = availableTools.value.find(tool => isDefinedAndNotEmpty(tool.toolid) != false && tool.toolid === selectedTool.value);
      if (isDefinedAndNotEmpty(toolToAdd) != false && selectedToolIds.value.includes(selectedTool.value) === false) {
        tools.value.push(toolToAdd);
        selectedToolIds.value.push(selectedTool.value);
        selectedTool.value = '';
      }
    }
  }
  
  const removeToolFromAgent = (toolId) => {
    const index = selectedToolIds.value.indexOf(toolId);
    if (index > -1) {
      selectedToolIds.value.splice(index, 1);
    }
  };
  
  const loadStyles = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        agentStyles.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/styles/getAllAgentStyles`, cfg);
      console.log('Loaded styles:', response.data);
      
      agentStyles.value = response.data.map(style => {
        const normalizedId = style.styleguid !== undefined && style.styleguid !== null && style.styleguid !== '' ? style.styleguid : (style.styleGuid !== undefined && style.styleGuid !== null && style.styleGuid !== '' ? style.styleGuid : (style.styleid !== undefined && style.styleid !== null && style.styleid !== '' ? style.styleid : (style.styleId !== undefined && style.styleId !== null && style.styleId !== '' ? style.styleId : (style.id !== undefined && style.id !== null && style.id !== '' ? style.id : ''))));
        return { ...style, styleid: normalizedId };
      });
      if (isEditMode.value === true && selectedStyleGuid.value !== '') {
        const selectedStyle = agentStyles.value.find(style => style.styleid === selectedStyleGuid.value);
        if (selectedStyle !== undefined) {
          console.log('Found selected style:', selectedStyle);
        }
      }
    } catch (error) {
      handleApiError(error, 'loading styles');
    }
  };
  
  const handleStyleChange = () => {
    console.log('Style changed to:', selectedStyleGuid.value);
  };
  
  const loadDivisions = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        divisions.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/divisions`, cfg);
      divisions.value = response.data; 
    } catch (error) {
      handleApiError(error, 'loading divisions');
    }
  };
  
  function toggleDivisionSelection(divisionId) {
    const index = selectedDivisionIds.value.indexOf(divisionId);
    if (index === -1) {
      selectedDivisionIds.value.push(divisionId);
    } else {
      selectedDivisionIds.value.splice(index, 1);
    }
  }
  
  function getDivisionName(divisionId) {
    const division = divisions.value.find(d => d.divisionid === divisionId);
    return isDefinedAndNotEmpty(division) != false ? division.divisionname : 'Unknown';
  }
  
  function handleToggleChange(event) {
      const isChecked = event.target.checked;
      const newMode = isChecked !=false ? 'Flow' : 'Agentic';
      console.log(`Toggle changed. Checked: ${isChecked}, Requesting mode: ${newMode}`);
      requestModeChange(newMode, true);
  }
  
  function handleAgentModeSelection(selection) {
    if (selection !== undefined && selection !== null && typeof selection === 'object') {
      const modeId = typeof selection.modeId === 'string' ? selection.modeId : agentMode.value;
      const desiredAgenticBehaviour = selection.agenticBehaviour === false ? false : true;
      requestModeChange(modeId, desiredAgenticBehaviour);
      return;
    }
    const fallbackMode = typeof selection === 'string' ? selection : agentMode.value;
    const fallbackAgentic = fallbackMode === 'SuperFast' ? false : true;
    requestModeChange(fallbackMode, fallbackAgentic);
  }
  
  function applyAgentModeChange(mode, desiredAgenticBehaviour) {
    agentMode.value = mode;
    if (mode === 'Flow') {
      agenticBehaviour.value = true;
    } else if (mode === 'SuperFast') {
      agenticBehaviour.value = false;
    } else {
      agenticBehaviour.value = desiredAgenticBehaviour === false ? false : true;
    }
    pendingAgenticBehaviour.value = agenticBehaviour.value;
  }
  
  function requestModeChange(newMode, desiredAgenticBehaviour) {
    const nextAgenticValue = desiredAgenticBehaviour === false ? false : true;
    if (agentMode.value === newMode) {
      applyAgentModeChange(newMode, nextAgenticValue);
      return;
    }
    if ((agentMode.value === 'Flow' || agentMode.value === 'FlowBased') && newMode !== agentMode.value) {
      let flowExists = false;
      if (agentMode.value === 'Flow') {
        if (isDefinedAndNotEmpty(llmFlowRef.value) != false) {
            const currentFlow = llmFlowRef.value.getFlowData();
            flowExists = Array.isArray(currentFlow) != false && currentFlow.length > 1;
        } else if (isDefinedAndNotEmpty(loadedAgentData?.agentdata?.flow) != false) {
            flowExists = Array.isArray(loadedAgentData.agentdata.flow) != false && loadedAgentData.agentdata.flow.length > 1;
        }
      } else if (agentMode.value === 'FlowBased') {
        flowExists = Array.isArray(flowBasedFlow.value) != false && flowBasedFlow.value.length > 1;
      }
  
      if (flowExists === true) {
          targetAgentMode.value = newMode;
          pendingAgenticBehaviour.value = nextAgenticValue;
          isSwitchConfirmOpen.value = true;
      } else {
          applyAgentModeChange(newMode, nextAgenticValue);
      }
    } else {
      applyAgentModeChange(newMode, nextAgenticValue);
    }
  }
  
  function confirmSwitchMode() {
    if (targetAgentMode.value !== null) {
        const confirmedAgentic = pendingAgenticBehaviour.value === false ? false : true;
        applyAgentModeChange(targetAgentMode.value, confirmedAgentic);
    }
    cancelSwitchMode(); 
  }
  
  function cancelSwitchMode() {
    console.log("Switch cancelled.");
    isSwitchConfirmOpen.value = false;
    targetAgentMode.value = null;
    pendingAgenticBehaviour.value = agenticBehaviour.value;
  }
  
  watch(agentMode, (newMode, oldMode) => {
    console.log(`AIChatAgentSetup: Watcher triggered. Mode changed from ${oldMode} to ${newMode}. Edit mode: ${isEditMode.value}`);
    if (newMode === 'Flow') {
      
    } else {
      
    }
  
    if (newMode === 'Flow' && isEditMode.value === true && loadedAgentData?.agentdata?.flow !== null && initialFlowLoadAttempted === false) {
      console.log('AIChatAgentSetup: Watcher conditions met for loading flow.');
      const flowToLoad = loadedAgentData.agentdata.flow; 
      nextTick(() => {
        console.log('AIChatAgentSetup: Watcher nextTick. CHECKING llmFlowRef JUST BEFORE CALL: ', llmFlowRef.value);
        if (llmFlowRef.value !== null) {
          try {
              console.log('AIChatAgentSetup: Watcher calling loadFlowData...');
              llmFlowRef.value.loadFlowData(flowToLoad);
              initialFlowLoadAttempted = true;
          } catch(error) {
              console.error('AIChatAgentSetup: Error calling loadFlowData from watcher:', e);
          }
        } else {
          console.error('AIChatAgentSetup: LLMFlow component ref still not available in watch nextTick (FINAL CHECK).');
        }
      });
    }
  });
  
  watch(isFastOrSuperFastMode, (isFastModeActive) => {
    if (isFastModeActive === true && verifyAnswerEnabled.value !== false) {
      verifyAnswerEnabled.value = false;
    }
  }, { immediate: true });
  
  watch([selectedLLMModel, agentMode], () => {
    syncAutoVerificationModel();
  });
  
  watch(verifyAnswerEnabled, () => {
    if (selectedLLMModel.value === autoLlmModelId) {
      syncAutoVerificationModel();
    }
  });
  
  watch(selectedAvatarProvider, value => {
    if (typeof value !== 'string' || value.length === 0) {
      selectedAvatarProvider.value = 'did';
      return;
    }
    if (avatarProviderConfigs.value[value] === undefined) {
      avatarProviderConfigs.value = {
        ...avatarProviderConfigs.value,
        [value]: defaultAvatarProviderConfig(value)
      };
    }
  });
  
  watch(selectedDivisionIds, async (newDivisionIds, oldDivisionIds) => {
    if (JSON.stringify(newDivisionIds) !== JSON.stringify(oldDivisionIds)) {
      if (Array.isArray(newDivisionIds) === true && newDivisionIds?.length > 0) {
        await fetchFilteredCollections(newDivisionIds);
      } else {
        availableCollectionsForAgent.value = [];
        selectedAnalysisCollectionIds.value = [];
      }
    }
  }, { deep: true });
  
  async function fetchFilteredCollections(divisionIds) {
    if (Array.isArray(divisionIds) === false || divisionIds.length === 0) {
      availableCollectionsForAgent.value = [];
      selectedAnalysisCollectionIds.value = [];
      return;
    }
    isLoadingCollections.value = true;
    const cfg = getSecuredAxiosConfig(null);
    if (cfg === null) {
      isLoadingCollections.value = false;
      return;
    }
  
    try {
      const params = { divisionIds: divisionIds.join(',') };
      const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections`, { ...cfg, params });
      availableCollectionsForAgent.value = response.data;
      selectedAnalysisCollectionIds.value = selectedAnalysisCollectionIds.value.filter(id => 
          availableCollectionsForAgent.value.some(coll => coll.collectionId === id)
      );
    } catch (error) {
      handleApiError(error, 'fetching filtered collections');
      availableCollectionsForAgent.value = [];
      selectedAnalysisCollectionIds.value = [];
    } finally {
      isLoadingCollections.value = false;
    }
  }
  
  function handleFineTuneSave(data) {
    console.log('Received fine-tune data:', data);
    if (data.originalQuery === undefined || data.originalResponse === undefined || data.fineTunedAnswer === undefined) {
        toast.error('Invalid fine-tuning data received.');
        return;
    }
  
    const fineTuneContent = `Original Question: ${data.originalQuery}
  Original Answer: ${data.originalResponse}
  Fine-tuned Answer: ${data.fineTunedAnswer}`;
    const fineTuneFileName = `fine-tune-${uuidv4()}.txt`; 
    
    const textBlob = new Blob([fineTuneContent], { type: 'text/plain' });
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Data = e.target.result.split(',')[1];
      
   
      const newFineTuneFile = {
        fileName: fineTuneFileName,
         fileType: 'fineTuneAnswer', 
         status: 'new', 
        content: fineTuneContent,
         fileData: base64Data, 
        originalQuery: data.originalQuery, 
        originalResponse: data.originalResponse,
        fineTunedAnswer: data.fineTunedAnswer,
        hiddenFromSources: true
      };
      
       existingFiles.value.push(newFineTuneFile); 
       knowledgeBaseFileAssignments.value[newFineTuneFile.fileName] = currentFolderId.value; 
   
      if (data.suppressToast !== true) {
        toast.dismiss(); 
        toast.success('Fine-tuned answer added and assigned to current folder.');
      }
    };
    reader.onerror = (error) => {
      console.error('Error converting fine-tune content to base64:', error);
      toast.error('Failed to process fine-tuned answer.');
    };
    reader.readAsDataURL(textBlob);
  }
  
  function openInspectModal(file) {
    let fileToInspect = existingFiles.value.find(f => f.fileName === file.fileName);
    if (fileToInspect === undefined) {
        fileToInspect = uploadedFiles.value.find(f => f.fileName === file.fileName);
    }
    
    if (fileToInspect === undefined) {
      console.error("Could not find file object for inspection:", file.fileName);
      toast.error("Could not load file details.");
      return;
    }
  
    const needsFetching = (fileToInspect.content === undefined || fileToInspect.content === null) && 
                          (fileToInspect.fileData === undefined || fileToInspect.fileData === null) && 
                          fileToInspect.status === 'existing';
  
    if (needsFetching === true) {
        const agentId = route.query.edit;
        if (agentId === undefined || agentId === null) {
            toast.error("Cannot fetch file content: Agent ID is missing.");
            return;
        }
        const cfg = getSecuredAxiosConfig({ responseType: 'blob' });
        if (cfg === null) {
          toast.error("Could not load file details.");
          return;
        }
        
        fileToInspect.isLoading = true;
        selectedFileToInspect.value = { ...fileToInspect };
        isInspectModalOpen.value = true;
        
        axios.get(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/files/${encodeURIComponent(fileToInspect.fileName)}`, cfg).then(response => {
            const reader = new FileReader();
            reader.onload = (e) => {
                fileToInspect.fileData = e.target.result.split(',')[1];
                fileToInspect.isLoading = false;
                selectedFileToInspect.value = { ...fileToInspect };
            };
            reader.onerror = () => {
                toast.error("Failed to read file content.");
                fileToInspect.isLoading = false;
                selectedFileToInspect.value = { ...fileToInspect };
            }
            reader.readAsDataURL(response.data);
        }).catch(error => {
            handleApiError(error, `loading content for ${fileToInspect.fileName}`);
            fileToInspect.isLoading = false;
            selectedFileToInspect.value = { ...fileToInspect, error: 'Failed to load content.' };
            isInspectModalOpen.value = false;
            closeInspectModal();
        });
    } else {
      selectedFileToInspect.value = { ...fileToInspect };
      isInspectModalOpen.value = true;
    }
  }
  
  function closeInspectModal() {
    isInspectModalOpen.value = false;
    selectedFileToInspect.value = null;
  }
  
  function closeModal() {
      showLoadingModal.value = false;
      modalState.type = 'loading';
      modalState.title = 'Processing...';
      modalState.message = '';
      stopLoadingTips(); 
      isEditingTextFile.value = false; 
      originalTextFileToEdit.value = null;
  }
  
  function closeModalOnErrorState(event) {
  
      console.log('Backdrop click ignored.');
    
  }
   
  const getAuthToken = () => {
    if (globalState.authUsesHttpOnlyCookies === true) {
      return '';
    }
    const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true)?.split('=')[1];
    const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') === true)?.split('=')[1];
    const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
    if (token === undefined || token === null) {
      return '';
    }
    return token !== undefined && token !== null ? token : '';
  };
  
  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token !== '' && token !== undefined && token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };
  
  const getSecuredAxiosConfig = (extraConfig) => {
    const headers = getAuthHeaders();
    const cfg = extraConfig !== undefined && extraConfig !== null && typeof extraConfig === 'object' ? { ...extraConfig } : {};
    cfg.headers = headers;
    const authHeader = headers['Authorization'];
    const hasAuthHeader = typeof authHeader === 'string' && authHeader.trim().length > 0;
    if (hasAuthHeader === false) {
      cfg.withCredentials = true;
    }
    return cfg;
  };

  const transferModalOpen = ref(false);
  const transferModalMode = ref('export');
  const transferModalBusy = ref(false);
  const transferModalError = ref('');
  const transferModalSuccess = ref('');
  const transferImportFile = ref(null);
  const transferImportFileName = ref('');
  const transferExportStream = ref(null);

  function closeTransferExportStream() {
    if (transferExportStream.value !== null) {
      transferExportStream.value.close();
      transferExportStream.value = null;
    }
  }

  function readFileAsDataUrl(fileObject) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsDataURL(fileObject);
    });
  }

  function openTransferModal(mode) {
    transferModalMode.value = mode === 'import' ? 'import' : 'export';
    transferModalBusy.value = false;
    transferModalError.value = '';
    transferModalSuccess.value = '';
    transferImportFile.value = null;
    transferImportFileName.value = '';
    closeTransferExportStream();
    transferModalOpen.value = true;
  }

  function closeTransferModal() {
    if (transferModalBusy.value === true) {
      return;
    }
    closeTransferExportStream();
    transferModalOpen.value = false;
  }

  function onTransferImportFileChange(event) {
    const target = event !== undefined && event !== null ? event.target : null;
    const files = target !== null && target !== undefined ? target.files : null;
    if (files === null || files === undefined || files.length === 0) {
      transferImportFile.value = null;
      transferImportFileName.value = '';
      return;
    }
    transferImportFile.value = files[0];
    transferImportFileName.value = typeof files[0].name === 'string' ? files[0].name : '';
  }

  async function exportAgentZip() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        transferModalError.value = 'Save the agent first.';
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      const startResponse = await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/export/start`, {}, cfg);
      const streamPath = typeof startResponse?.data?.streamPath === 'string' && startResponse.data.streamPath.length > 0
        ? startResponse.data.streamPath
        : `/api/agents/${encodePathSegment(agentId)}/export/jobs/${startResponse?.data?.jobId}/stream`;
      const streamUrl = streamPath.startsWith('http://') === true || streamPath.startsWith('https://') === true
        ? streamPath
        : `${apiEndpoint}${streamPath}`;
      closeTransferExportStream();
      transferModalSuccess.value = 'Preparing export...';
      const stream = new EventSource(streamUrl, { withCredentials: true });
      transferExportStream.value = stream;
      stream.addEventListener('status', (event) => {
        let payload = null;
        try {
          payload = JSON.parse(event.data);
        } catch (error) {
          payload = null;
        }
        if (payload === null || payload === undefined) {
          return;
        }
        if (typeof payload.progressMessage === 'string' && payload.progressMessage.length > 0) {
          transferModalSuccess.value = payload.progressMessage;
        }
        if (payload.status === 'failed') {
          transferModalError.value = typeof payload.errorMessage === 'string' && payload.errorMessage.length > 0 ? payload.errorMessage : 'Export failed.';
          closeTransferExportStream();
          return;
        }
        if (payload.status === 'completed') {
          if (typeof payload.downloadUrl === 'string' && payload.downloadUrl.length > 0) {
            window.open(payload.downloadUrl, '_blank', 'noopener,noreferrer');
            transferModalSuccess.value = 'Export ready. Download started.';
            closeTransferExportStream();
            return;
          }
          transferModalError.value = 'Export finished but no download URL was returned.';
          closeTransferExportStream();
        }
      });
      stream.addEventListener('ping', () => {});
      stream.onerror = () => {
        if (transferModalSuccess.value !== 'Export ready. Download started.' && transferModalError.value.length === 0) {
          transferModalError.value = 'Lost connection while exporting.';
        }
        closeTransferExportStream();
      };
    } catch (error) {
      const message = typeof error?.response?.data?.message === 'string' ? error.response.data.message : 'Failed exporting agent.';
      transferModalError.value = message;
    }
  }

  async function importAgentZip() {
    try {
      if (transferImportFile.value === null) {
        transferModalError.value = 'Please select a zip file.';
        return;
      }
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        transferModalError.value = 'Save the agent first.';
        return;
      }
      const fileDataUrl = await readFileAsDataUrl(transferImportFile.value);
      const cfg = getSecuredAxiosConfig(null);
      await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/import`, { zipBase64: fileDataUrl }, cfg);
      await refreshAgentData();
      transferModalSuccess.value = 'Agent imported successfully.';
    } catch (error) {
      const message = typeof error?.response?.data?.message === 'string' ? error.response.data.message : 'Failed importing agent.';
      transferModalError.value = message;
    }
  }

  async function confirmTransferModalAction() {
    if (transferModalBusy.value === true) {
      return;
    }
    transferModalBusy.value = true;
    transferModalError.value = '';
    transferModalSuccess.value = '';
    try {
      if (transferModalMode.value === 'import') {
        await importAgentZip();
      } else {
        await exportAgentZip();
      }
    } finally {
      transferModalBusy.value = false;
    }
  }
  
  const encodePathSegment = (value) => {
    const raw = value !== undefined && value !== null ? String(value) : '';
    return encodeURIComponent(raw);
  };
  
  const handleApiError = (error, context = 'operation') => {
      console.error(`Error during ${context}:`, error);
      const errorMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message : (typeof error?.response?.data?.error === 'string' ? error.response.data.error : '');
      const isExpired = errorMessage.toLowerCase().includes('jwt expired');
      if (error?.response?.status === 401 || error?.response?.status === 403 || isExpired === true) {
           toast.error('Authentication error. Redirecting to login.'); 
          router.push('/login');
      } else if (errorMessage !== '') {
          toast.error(`Error: ${String(errorMessage)}`);
      } else {
          toast.error(`An unexpected error occurred during ${context}.`);
      }
  };
   
   
  function openCreateFolderModal() {
    newFolderName.value = '';
    showCreateFolderModal.value = true;
  }
  
  function handleCreateFolder() {
    if (newFolderName.value.trim().length === 0) {
      toast.error('Folder name cannot be empty.');
      return;
    }
   
    const parentId = currentFolderId.value;
    const isDuplicate = knowledgeBaseFolders.value.some(
      folder => folder.parentId === parentId && folder.name.toLowerCase() === newFolderName.value.trim().toLowerCase()
    );
    if (isDuplicate === true) {
      toast.error(`A folder named \"${newFolderName.value.trim()}\" already exists here.`);
      return;
    }
  
    knowledgeBaseFolders.value.push({
      id: uuidv4(),
      name: newFolderName.value.trim(),
      parentId: parentId,
    });
    showCreateFolderModal.value = false;
    newFolderName.value = '';
    toast.success('Folder created.');
  }
  
  function navigateToFolder(folder) {
    currentKbPath.value.push({ id: folder.id, name: folder.name });
     searchQuery.value = ''; 
    currentKbPage.value = 1;
  }
  
  function navigateToBreadcrumb(index) {
    currentKbPath.value = currentKbPath.value.slice(0, index + 1);
     searchQuery.value = ''; 
    currentKbPage.value = 1;
  }
  
  function deleteFolder(folderIdToDelete) {
      const folderToDelete = knowledgeBaseFolders.value.find(f => f.id === folderIdToDelete);
      if (folderToDelete === undefined) return;
  
       
      const hasSubFolders = knowledgeBaseFolders.value.some(f => f.parentId === folderIdToDelete);
   
      const hasFiles = Object.values(knowledgeBaseFileAssignments.value).includes(folderIdToDelete);
  
      if (hasSubFolders === true || hasFiles === true) {
          toast.error(`Folder "${folderToDelete.name}" is not empty. Please remove its contents first.`);
          return;
      }
  
      if (confirm(`Are you sure you want to delete folder "${folderToDelete.name}"? This cannot be undone.`) === true) {
          knowledgeBaseFolders.value = knowledgeBaseFolders.value.filter(f => f.id !== folderIdToDelete);
          toast.success(`Folder "${folderToDelete.name}" deleted.`);
      }
  }
  
  function openMoveFileModal(file, type) {
     fileToMove.value = { ...file, type: type }; 
     targetFolderForMove.value = isDefinedAndNotEmpty(knowledgeBaseFileAssignments.value[file.fileName]) != false ? knowledgeBaseFileAssignments.value[file.fileName] : null; 
    showMoveFileModal.value = true;
  }
  
  function handleMoveFile() {
    if (fileToMove.value === null) return;
    const fileName = fileToMove.value.fileName;
    
    if (targetFolderForMove.value === 'root') { 
        knowledgeBaseFileAssignments.value[fileName] = null;
    } else {
        knowledgeBaseFileAssignments.value[fileName] = targetFolderForMove.value;
    }
    
    toast.success(`File "${fileName}" moved.`);
    showMoveFileModal.value = false;
    fileToMove.value = null;
  }
  
  function estimateTokens(text) {
    if (text === null || text === undefined) {
      return 0;
    }
    return Math.ceil(text.length / 4);
  }
  
  function isCached(fileName) {
    return cachedFileNames.value.includes(fileName) === true;
  }
  
  function toggleCacheFile(file) {
    const fileName = file.fileName;
    const isCurrentlyCached = isCached(fileName);
  
    if (isCurrentlyCached === false) { 
      let fileContentToEstimate = null;
      if (file.status === 'new') {
        const uploadedFile = uploadedFiles.value.find(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === fileName);
        if (isDefinedAndNotEmpty(uploadedFile) != false && uploadedFile.fileType === 'text') {
          fileContentToEstimate = uploadedFile.content; 
        }
      } else if (file.status === 'existing') {
        const existingFile = existingFiles.value.find(f => f.fileName === fileName);
        if (existingFile !== undefined && existingFile.fileType === 'text') {
          if (existingFile.content === undefined || existingFile.content === null) {
              if (existingFile.fileData !== undefined && existingFile.fileData !== null) {
                   try {
                      existingFile.content = atob(existingFile.fileData);
                      fileContentToEstimate = existingFile.content;
                   } catch (error) {
                      console.error("Error decoding base64 for existing file content:", e);
                      toast.error("Could not read existing file content for token estimation.");
                      return;
                   }
              } else {
                  toast.error("Existing file content is not available for token estimation.");
                  return;
              }
          } else {
              fileContentToEstimate = existingFile.content;
          }
        }
      }
  
      if (fileContentToEstimate === null) {
        toast.error('Cannot cache: File content not available or not a TXT file.');
        return;
      }
  
      const tokens = estimateTokens(fileContentToEstimate);
      if (tokens > 10000) {
        toast.error(`Cannot cache "${fileName}": Exceeds 10,000 tokens (estimated: ${tokens}).`);
        return;
      } else {
          toast.info(`Estimated tokens for "${fileName}": ${tokens}`);
      }
      cachedFileNames.value.push(fileName);
    } else {
      const index = cachedFileNames.value.indexOf(fileName);
      if (index > -1) {
        cachedFileNames.value.splice(index, 1);
      }
    }
  }
  
  function truncateDisplayFileName(name, maxLength = 60) {
    const decodedName = decodeURIComponentSafe(name);
    if (decodedName === undefined || decodedName === null) return '';
  
    const gridMaxLength = kbViewMode.value === 'grid' ? 25 : maxLength;
     if (decodedName.length <= gridMaxLength) {
      return decodedName;
    }
    return decodedName.substring(0, gridMaxLength) + '...';
  }
  
  function handleFileDragStart(file, fileOriginalType, event) {
  
    const decodedFileName = decodeURIComponentSafe(file.fileName);
    draggedItem.value = { fileName: file.fileName, decodedFileName: decodedFileName, type: fileOriginalType, originalTargetId: knowledgeBaseFileAssignments.value[file.fileName] || null };
    if (event.dataTransfer !== null && event.dataTransfer !== undefined) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', decodedFileName); 
    }
    isDragOverFolder.value = null;
  }
  
  function handleDragEnd() {
  
    draggedItem.value = null;
    isDragOverFolder.value = null;
  }
  
  function handleFolderDragOver(event, folderId) {
  
   event.preventDefault();
   if (draggedItem.value !== null && draggedItem.value !== undefined) {
     if (draggedItem.value.originalTargetId !== folderId) {
         isDragOverFolder.value = folderId;
     }
   }
  }
  
  function handleFolderDragLeave(event, folderId) {
  
   if (isDragOverFolder.value === folderId) {
     isDragOverFolder.value = null;
   }
  }
  
  
  function handleFileDropOnFolder(targetFolderId) {
  
    if (draggedItem.value === null || draggedItem.value === undefined) {
      isDragOverFolder.value = null;
      return;
    }
  
    const currentAssignment = isDefinedAndNotEmpty(knowledgeBaseFileAssignments.value[draggedItem.value.fileName]) != false ? knowledgeBaseFileAssignments.value[draggedItem.value.fileName] : null;
    if (currentAssignment === targetFolderId) {
      toast.info('File is already in this folder.');
      isDragOverFolder.value = null;
      draggedItem.value = null;
      return;
    }
  
    knowledgeBaseFileAssignments.value[draggedItem.value.fileName] = targetFolderId;
   
    toast.success(`File \"${truncateDisplayFileName(draggedItem.value.decodedFileName, 30)}\" moved.`);
    isDragOverFolder.value = null;
    draggedItem.value = null;
  }
  
  
  
  function decodeURIComponentSafe(uri) {
    try {
      return decodeURIComponent(uri);
    } catch (error) {
      console.warn(`Failed to decode URI component: ${uri}`, error);
      return uri; 
    }
  }
  
  
  function startRenamingFolder(folder) {
    folderBeingRenamedId.value = folder.id;
    newFolderNameInput.value = folder.name;
    nextTick(() => {
      const inputElement = document.querySelector(`input[data-folder-rename-id="${folder.id}"]`);
      inputElement?.focus();
    });
  }
  
  function saveFolderName(folder) {
    const newName = newFolderNameInput.value.trim();
    folderBeingRenamedId.value = null; 
  
    if (newName.length === 0) {
      toast.error('Folder name cannot be empty.');
      return; 
    }
  
    if (newName === folder.name) {
      return; 
    }
  
    const isDuplicate = knowledgeBaseFolders.value.some(
      f => isDefinedAndNotEmpty(f.id) != false && f.id !== folder.id && f.parentId === folder.parentId && f.name.toLowerCase() === newName.toLowerCase()
    );
  
    if (isDuplicate === true) {
      toast.error(`Another folder named \"${newName}\" already exists here.`);
      return; 
    }
  
    
    const folderIndex = knowledgeBaseFolders.value.findIndex(f => f.id === folder.id);
    if (folderIndex !== -1) {
      knowledgeBaseFolders.value[folderIndex].name = newName;
      toast.success('Folder renamed successfully.');
    } else {
      toast.error('Error renaming folder: Folder not found.');
    }
  }
  
  function cancelRename() {
      folderBeingRenamedId.value = null;
      newFolderNameInput.value = '';
  }
  
  
  function handleFlowUpdateFromAgent(newFlowData) {
    console.log("[AIChatAgentSetup] Received flow-updated event:", newFlowData);
    if (llmFlowRef.value !== null && llmFlowRef.value !== undefined) {
      try {
        llmFlowRef.value.loadFlowData(newFlowData);
        toast.info("Flow updated by AI Agent.");
      } catch (error) {
        console.error("[AIChatAgentSetup] Error loading flow data into LLMFlow:", error);
        toast.error("Error applying flow updates.");
      }
    } else {
      console.error("[AIChatAgentSetup] llmFlowRef is not available to load new data.");
      toast.error("Internal Error: Could not update flow diagram.");
    }
  }
  
  function getNextVersionFileName(originalName) {
    const baseNameMatch = originalName.match(/^(.*?)(?:\s*\(v(\d+)\))?\.txt$/i);
    let base = originalName.replace(/\.txt$/i, '');
    let nextVersionNumber = 1;
  
    if (baseNameMatch !== null) {
      base = baseNameMatch[1]; 
      if (baseNameMatch[2] !== undefined) { 
        nextVersionNumber = parseInt(baseNameMatch[2], 10) + 1;
      } else {
        nextVersionNumber = 2;
      }
    } else {
  
      nextVersionNumber = 2; 
    }
  
    let nextFileName = '';
   
    while (true) {
      nextFileName = `${base} (v${nextVersionNumber}).txt`;
      const existsInExisting = existingFiles.value.some(f => f.fileName === nextFileName && f.status !== 'deleted');
      const existsInUploaded = uploadedFiles.value.some(f => f.fileName === nextFileName);
      
      if (existsInExisting === false && existsInUploaded === false) {
        break;
      }
      nextVersionNumber++;
    }
    return nextFileName;
  }
  
  function openEditTextFileModal(file) {
    if (file.fileType !== 'text') return;
  
    originalTextFileToEdit.value = JSON.parse(JSON.stringify(file)); 
    isEditingTextFile.value = true;
  
    textFileName.value = getNextVersionFileName(file.fileName);
    
    let content = '';
    if (file.status === 'new') {
      const uploadedFile = uploadedFiles.value.find(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === file.fileName);
      if (isDefinedAndNotEmpty(uploadedFile) != false) {
        content = isDefinedAndNotEmpty(uploadedFile.content) != false ? uploadedFile.content : (isDefinedAndNotEmpty(uploadedFile.fileData) != false ? decodeBase64UTF8(uploadedFile.fileData) : '');
      }
    } else { 
      const existingFile = existingFiles.value.find(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === file.fileName);
      if (isDefinedAndNotEmpty(existingFile) != false) {
          content = isDefinedAndNotEmpty(existingFile.content) != false ? existingFile.content : (isDefinedAndNotEmpty(existingFile.fileData) != false ? decodeBase64UTF8(existingFile.fileData) : '');
      }
    }
    textContent.value = content;
    showTextInputModal.value = true;
  }
  
  function openInspectOrEditTextFile(file) {
    if (file.fileType === 'websiteurl' || file.fileType === 'website') {
      if (file.status === 'existing') {
        const raw = isDefinedAndNotEmpty(file.url) != false ? file.url : file.fileName;
        const safe = sanitizeWindowOpenUrl(raw);
        if (safe !== null) {
          window.open(safe, '_blank', 'noopener,noreferrer');
        } else {
          toast.error(t('aiChatAgentSetup.errors.invalidUrl', 'Invalid URL'));
        }
      } else {
        toast.info('Save the agent to make this link clickable.');
      }
      return;
    }
  
    if (file.fileType !== 'text') {
      openInspectModal(file);
      return;
    }
  
    const openEditor = (fileWithContent) => {
      originalTextFileToEdit.value = JSON.parse(JSON.stringify(fileWithContent)); 
      isEditingTextFile.value = true;
      textFileName.value = getNextVersionFileName(fileWithContent.fileName);
      textContent.value = isDefinedAndNotEmpty(fileWithContent.content) != false ? fileWithContent.content : '';
      showTextInputModal.value = true;
    };
  
    if (file.status === 'new') {
      openEditor(file);
    } else if (file.status === 'existing') {
      if (file.content !== undefined && file.content !== null) {
        openEditor(file);
      } else {
        const agentId = route.query.edit;
        if (agentId === undefined || agentId === null) {
          toast.error("Cannot fetch file content: Agent ID is missing.");
          return;
        }
        const cfg = getSecuredAxiosConfig({ responseType: 'blob' });
        if (cfg === null) {
          return;
        }
        toast.info("Loading file content...");
        axios.get(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/files/${encodeURIComponent(file.fileName)}`, cfg).then(response => {
          toast.dismiss();
          const blob = response.data;
          blob.text().then(text => {
            file.content = text; 
            openEditor(file);
          });
        }).catch(error => {
          toast.dismiss();
          handleApiError(error, `loading content for ${file.fileName}`);
        });
      }
    }
  }
  
  const isRtl = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');
  
  watch(agentType, (newAgentType) => {
    if (isEditMode.value === false) {
      enableFileUploadInChat.value = newAgentType === 'PrivateAgent';
    }
  });
  
  const itemsPerPageKbOptions = [10, 20, 50, 100];
  const itemsPerPageKb = ref(10);
  const bulkRescrapeIntervalDays = ref(1);
  const bulkRescrapeAtTime = ref('00:00');
  const selectedRowKeys = ref(new Set());
  const hasSelectedRows = computed(() => selectedRowKeys.value.size > 0);
  const immediateRescrapeRequested = ref(false);
  const immediateRescrapeQueue = ref([]);
  const currentPageRowKeys = computed(() => {
    const keys = [];
    for (let i = 0; i < paginatedCombinedKbItems.value.length; i = i + 1) {
      const entry = paginatedCombinedKbItems.value[i];
      if (entry.type === 'existing' || entry.type === 'new') {
        keys.push(entry.item.fileName);
      }
    }
    return keys;
  });
  const isAllSelectedOnPage = computed(() => {
    const keys = currentPageRowKeys.value;
    if (keys.length === 0) return false;
    for (let i = 0; i < keys.length; i = i + 1) {
      if (selectedRowKeys.value.has(keys[i]) === false) return false;
    }
    return true;
  });
  function isRowSelected(fileName) {
    return selectedRowKeys.value.has(fileName) === true;
  }
  function toggleSelectRow(file) {
    const key = file.fileName;
    const next = new Set(selectedRowKeys.value);
    if (next.has(key) === true) {
      next.delete(key);
    } else {
      next.add(key);
    }
    selectedRowKeys.value = next;
  }
  function toggleSelectAllOnPage(event) {
    const shouldSelect = event.target.checked === true;
    const keys = currentPageRowKeys.value;
    const next = new Set(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const key = keys[i];
      if (shouldSelect === true) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    selectedRowKeys.value = next;
  }
  function selectAllFilteredRows() {
    const keys = [
      ...filteredExistingFiles.value.map(i => i.fileName),
      ...filteredUploadedFiles.value.map(i => i.fileName)
    ];
    selectedRowKeys.value = new Set(keys);
  }
  function clearSelectedRows() {
    selectedRowKeys.value = new Set();
  }
  function deleteSelectedRows() {
    if (selectedRowKeys.value.size === 0) return;
    const toDelete = Array.from(selectedRowKeys.value);
    for (let i = 0; i < toDelete.length; i = i + 1) {
      const name = toDelete[i];
      const idxExisting = existingFiles.value.findIndex(ef => ef.fileName === name);
      if (idxExisting > -1) {
        existingFiles.value[idxExisting].status = 'deleted';
        continue;
      }
      const idxUploaded = uploadedFiles.value.findIndex(uf => uf.fileName === name);
      if (idxUploaded > -1) {
        uploadedFiles.value.splice(idxUploaded, 1);
        delete knowledgeBaseFileAssignments.value[name];
      }
    }
    selectedRowKeys.value = new Set();
  }
  
  function enableRescrapeAllWebsiteUrls() {
    const interval = Number(bulkRescrapeIntervalDays.value);
    const timeVal = typeof bulkRescrapeAtTime.value === 'string' ? bulkRescrapeAtTime.value : '';
    let updated = 0;
    const types = ['website','websiteurl','websitepage'];
    for (let i = 0; i < filteredExistingFiles.value.length; i = i + 1) {
      const file = filteredExistingFiles.value[i];
      const isWeb = types.includes(file.fileType) === true;
      if (isWeb === true && file.status !== 'deleted') {
        file.rescrapeEnabled = true;
        file.rescrapeIntervalDays = interval;
        file.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
        updated = updated + 1;
      }
    }
    for (let j = 0; j < filteredUploadedFiles.value.length; j = j + 1) {
      const file = filteredUploadedFiles.value[j];
      const isWeb = types.includes(file.fileType) === true;
      if (isWeb === true && file.status !== 'deleted') {
        file.rescrapeEnabled = true;
        file.rescrapeIntervalDays = interval;
        file.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
        updated = updated + 1;
      }
    }
    if (updated > 0) {
      toast.success(updated + ' website source(s) rescrape info updated. Changes will start on save.');
    } else {
      toast.info('No website sources found to update.');
    }
  }
  
  function enableRescrapeForSelected() {
    if (selectedRowKeys.value.size === 0) {
      toast.error('Please select at least one item.');
      return;
    }
    const interval = Number(bulkRescrapeIntervalDays.value);
    const timeVal = typeof bulkRescrapeAtTime.value === 'string' ? bulkRescrapeAtTime.value : '';
    const types = ['website','websiteurl','websitepage'];
    let updated = 0;
    const keys = Array.from(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const name = keys[i];
      const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
      if (existingIdx > -1) {
        const f = existingFiles.value[existingIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = true;
          f.rescrapeIntervalDays = interval;
          f.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
          updated = updated + 1;
        }
        continue;
      }
      const uploadedIdx = uploadedFiles.value.findIndex(f => f.fileName === name);
      if (uploadedIdx > -1) {
        const f = uploadedFiles.value[uploadedIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = true;
          f.rescrapeIntervalDays = interval;
          f.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
          updated = updated + 1;
        }
      }
    }
    if (updated > 0) {
      toast.success(updated + ' website source(s) rescrape info updated. Changes will start on save.');
    } else {
      toast.info('No selected website sources found to update.');
    }
  }
  
  function disableRescrapeForSelected() {
    if (selectedRowKeys.value.size === 0) {
      toast.error('Please select at least one item.');
      return;
    }
    const types = ['website','websiteurl','websitepage'];
    let updated = 0;
    const keys = Array.from(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const name = keys[i];
      const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
      if (existingIdx > -1) {
        const f = existingFiles.value[existingIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = false;
          f.rescrapeIntervalDays = '';
          f.rescrapeAtTime = '';
          updated = updated + 1;
        }
        continue;
      }
      const uploadedIdx = uploadedFiles.value.findIndex(f => f.fileName === name);
      if (uploadedIdx > -1) {
        const f = uploadedFiles.value[uploadedIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = false;
          f.rescrapeIntervalDays = '';
          f.rescrapeAtTime = '';
          updated = updated + 1;
        }
      }
    }
    if (updated > 0) {
      toast.success(updated + ' website source(s) rescrape disabled. Changes will start on save.');
    } else {
      toast.info('No selected website sources found to update.');
    }
  }
  
  async function rescanAllWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Set();
      let sent = 0;
      for (let i = 0; i < existingFiles.value.length; i = i + 1) {
        const f = existingFiles.value[i];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
          if (unique.has(rootUrl) === false) {
            unique.add(rootUrl);
            const body = { rootUrl: rootUrl, type: f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 };
            try {
              await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
              sent = sent + 1;
            } catch (e) {}
          }
        }
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No website sources to rescan.');
      }
    } catch (e) {}
  }
  
  async function rescanSelectedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (selectedRowKeys.value.size === 0) {
        toast.error('Please select at least one item.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Map();
      const keys = Array.from(selectedRowKeys.value);
      for (let i = 0; i < keys.length; i = i + 1) {
        const name = keys[i];
        const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
        if (existingIdx > -1) {
          const f = existingFiles.value[existingIdx];
          if (types.includes(f.fileType) === true && f.status !== 'deleted') {
            const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
            const entryType = f.fileType === 'websitepage' ? 'websiteurl' : f.fileType;
            if (unique.has(rootUrl) === false) unique.set(rootUrl, { type: entryType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 });
          }
        }
      }
      let sent = 0;
      for (const [rootUrl, data] of unique.entries()) {
        try {
          await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, { rootUrl: rootUrl, type: data.type, maxPages: data.maxPages }, cfg);
          sent = sent + 1;
        } catch (e) {}
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No selected website sources to rescan.');
      }
    } catch (e) {}
  }
  
  async function retryAllProcessingWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Map();
      for (let i = 0; i < existingFiles.value.length; i = i + 1) {
        const f = existingFiles.value[i];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          let needsRescan = false;
          const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
          if (ps === 'Processing') {
            needsRescan = true;
          } else if (shouldShowContinue(f) === true) {
            needsRescan = true;
          }
          if (needsRescan === true) {
            const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
            if (unique.has(rootUrl) === false) {
              unique.set(rootUrl, { type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 });
            }
          }
        }
      }
      let sent = 0;
      for (const [rootUrl, data] of unique.entries()) {
        const body = { rootUrl: rootUrl, type: data.type, maxPages: data.maxPages };
        try {
          await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
          sent = sent + 1;
        } catch (e) {}
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No website sources in processing state.');
      }
    } catch (e) {}
  }
  
  async function retryProcessingSelectedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (selectedRowKeys.value.size === 0) {
        toast.error('Please select at least one item.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Map();
      const keys = Array.from(selectedRowKeys.value);
      for (let i = 0; i < keys.length; i = i + 1) {
        const name = keys[i];
        const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
        if (existingIdx > -1) {
          const f = existingFiles.value[existingIdx];
          if (types.includes(f.fileType) === true && f.status !== 'deleted') {
            let needsRescan = false;
            const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
            if (ps === 'Processing') {
              needsRescan = true;
            } else if (shouldShowContinue(f) === true) {
              needsRescan = true;
            }
            if (needsRescan === true) {
              const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
              if (unique.has(rootUrl) === false) unique.set(rootUrl, { type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 });
            }
          }
        }
      }
      let sent = 0;
      for (const [rootUrl, data] of unique.entries()) {
        const body = { rootUrl: rootUrl, type: data.type, maxPages: data.maxPages };
        try {
          await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
          sent = sent + 1;
        } catch (e) {}
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No selected website sources in processing state.');
      }
    } catch (e) {}
  }
  
  watch(itemsPerPageKb, () => {
    currentKbPage.value = 1;
  });
  
  function shouldShowContinue(file) {
    if (['website','websiteurl','websitepage'].includes(file.fileType) === false) return false;
    const statusInfo = loadedAgentData !== null && Array.isArray(loadedAgentData.fileStatuses) === true ? loadedAgentData.fileStatuses.find(s => s.name === file.fileName) : null;
    if (statusInfo === null || statusInfo === undefined) return false;
    if (statusInfo.status === 'Completed' || statusInfo.status === 'Failed') return false;
    const prog = statusInfo.progress;
    if (prog === undefined || prog === null) return false;
    const totalVal = Number(prog.total);
    const currentVal = Number(prog.current);
    const totalIsNum = Number.isFinite(totalVal) === true;
    const currentIsNum = Number.isFinite(currentVal) === true;
    if (totalIsNum === true && currentIsNum === true) {
      if (totalVal > 0 && currentVal < totalVal) return true;
    }
    return false;
  }
  
  function shouldShowRetryFailed(file) {
    if (['website','websiteurl','websitepage'].includes(file.fileType) === false) return false;
    const statusInfo = loadedAgentData !== null && Array.isArray(loadedAgentData.fileStatuses) === true ? loadedAgentData.fileStatuses.find(s => s.name === file.fileName) : null;
    if (statusInfo === null || statusInfo === undefined) return false;
    if (Array.isArray(statusInfo.discoveredLinks) === true) {
      const failed = statusInfo.discoveredLinks.filter(l => l !== undefined && l !== null && l.status === 'failed').length;
      if (failed > 0) return true;
    }
    if (Number.isFinite(Number(statusInfo.failedCount)) === true && Number(statusInfo.failedCount) > 0) return true;
    return false;
  }
  async function runImmediateRescrape(agentId) {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const jobs = Array.isArray(immediateRescrapeQueue.value) === true ? immediateRescrapeQueue.value : [];
      for (let i = 0; i < jobs.length; i = i + 1) {
        const job = jobs[i];
        const rootUrl = job.rootUrl;
        const maxPages = Number.isFinite(Number(job.maxPages)) === true ? Number(job.maxPages) : 0;
        const action = job.action === 'retryFailed' ? 'retryFailed' : 'continue';
        if (action === 'retryFailed') {
          try {
            await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/retryFailed`, { rootUrl: rootUrl, maxPages: maxPages }, cfg);
          } catch (e) {
          }
        } else {
          try {
            await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/continue`, { rootUrl: rootUrl, maxPages: maxPages }, cfg);
          } catch (e) {
          }
        }
      }
      immediateRescrapeRequested.value = false;
      immediateRescrapeQueue.value = [];
    } catch (e) {}
  }
  const currentKbPage = ref(1);
  const totalKbPages = computed(() => {
    const totalItems = displayedFoldersInCurrentView.value.length + filteredExistingFiles.value.length + filteredUploadedFiles.value.length;
    const perPage = Number(itemsPerPageKb.value);
    return perPage > 0 ? Math.ceil(totalItems / perPage) : 1;
  });
  const paginatedCombinedKbItems = computed(() => {
    const combined = [
      ...displayedFoldersInCurrentView.value.map(i => ({ type: 'folder', item: i })),
      ...filteredExistingFiles.value.map(i => ({ type: 'existing', item: i })),
      ...filteredUploadedFiles.value.map(i => ({ type: 'new', item: i }))
    ];
    const perPage = Number(itemsPerPageKb.value);
    if (perPage <= 0) {
      return combined;
    }
    const start = (currentKbPage.value - 1) * perPage;
    const end = start + perPage;
    return combined.slice(start, end);
  });
  const paginatedFoldersInCurrentView = computed(() => paginatedCombinedKbItems.value.filter(i => i.type === 'folder').map(i => i.item));
  const paginatedFilteredExistingFiles = computed(() => paginatedCombinedKbItems.value.filter(i => i.type === 'existing').map(i => i.item));
  const paginatedFilteredUploadedFiles = computed(() => paginatedCombinedKbItems.value.filter(i => i.type === 'new').map(i => i.item));
  
  const selectedInappropriateContentLang = ref('en');
  const selectedDontKnowLang = ref('en');
  
  const currentInappropriateContentMessage = computed({
    get: () => isDefinedAndNotEmpty(inappropriateContentMessage.value[selectedInappropriateContentLang.value]) != false ? inappropriateContentMessage.value[selectedInappropriateContentLang.value] : '',
    set: (value) => {
      inappropriateContentMessage.value[selectedInappropriateContentLang.value] = value;
    }
  });
  
  const currentDontKnowMessage = computed({
    get: () => isDefinedAndNotEmpty(dontKnowMessage.value[selectedDontKnowLang.value]) != false ? dontKnowMessage.value[selectedDontKnowLang.value] : '',
    set: (value) => {
      dontKnowMessage.value[selectedDontKnowLang.value] = value;
    }
  });
  
  const selectedStartingMessageLang = ref('default');
  const currentStartingMessage = computed({
    get: () => isDefinedAndNotEmpty(startingMessage.value[selectedStartingMessageLang.value]) != false ? startingMessage.value[selectedStartingMessageLang.value] : '',
    set: (value) => {
      startingMessage.value[selectedStartingMessageLang.value] = value;
    }
  });
  
  const viewMode = ref('full');
  
  function switchView() {
    const agentId = route.query.edit;
    if (viewMode.value === 'light') {
      router.push({ path: '/aiChatAgentSetupLight', query: { edit: agentId } });
    } else {
      router.push({ path: '/aiChatAgentSetup', query: { edit: agentId } });
    }
  }
  
  function openChat() {
    const agentIdRaw = route.query.edit;
    const agentId = agentIdRaw !== undefined && agentIdRaw !== null ? String(agentIdRaw) : '';
    if (agentId.length === 0) {
      toast.error(t('aiChatAgentSetup.errors.missingAgentId', 'Cannot open chat: Agent ID is missing.'));
      return;
    }
    const safeAgentId = encodePathSegment(agentId);
    const type = agentType.value;
    const basePath = type === 'PublicAgent' ? '/agent' : '/aiChatAgentAssistance';
    if (type === 'PublicAgent') {
      let external = '';
      if (loadedAgentData !== null && typeof loadedAgentData.publicAgentServiceEndpoint === 'string' && loadedAgentData.publicAgentServiceEndpoint.trim() !== '') {
        external = loadedAgentData.publicAgentServiceEndpoint.trim();
      }
      const extVal = String(external);
      const isProd = process.env.environment === 'production';
      const isHttps = extVal.startsWith('https://') === true;
      const isHttp = extVal.startsWith('http://') === true;
      const hasAllowedProtocol = isHttps === true || (isHttp === true && isProd === false);
      const isPlaceholder = extVal.indexOf('${') > -1;
      if (extVal !== '' && hasAllowedProtocol === true && isPlaceholder === false) {
        const base = extVal.endsWith('/') === true ? extVal.slice(0, -1) : extVal;
        const safe = sanitizeWindowOpenUrl(`${base}/${safeAgentId}`);
        if (safe !== null) {
          window.open(safe, '_blank', 'noopener,noreferrer');
        } else {
          window.open(`${window.location.origin}${basePath}/${safeAgentId}`, '_blank', 'noopener,noreferrer');
        }
      } else {
        window.open(`${window.location.origin}${basePath}/${safeAgentId}`, '_blank', 'noopener,noreferrer');
      }
    } else {
      router.push(`${basePath}/${safeAgentId}`);
    }
  }
  
  const showScrape = ref(false);
  
  const showCreateStyleModal = ref(false);
  const onStyleCreated = (newStyle) => {
    let normalizedId = '';
    if (newStyle.styleguid !== undefined && newStyle.styleguid !== null && newStyle.styleguid !== '') {
      normalizedId = newStyle.styleguid;
    } else if (newStyle.styleGuid !== undefined && newStyle.styleGuid !== null && newStyle.styleGuid !== '') {
      normalizedId = newStyle.styleGuid;
    } else if (newStyle.styleid !== undefined && newStyle.styleid !== null && newStyle.styleid !== '') {
      normalizedId = newStyle.styleid;
    } else if (newStyle.styleId !== undefined && newStyle.styleId !== null && newStyle.styleId !== '') {
      normalizedId = newStyle.styleId;
    } else if (newStyle.id !== undefined && newStyle.id !== null && newStyle.id !== '') {
      normalizedId = newStyle.id;
    }
    newStyle.styleid = normalizedId;
    agentStyles.value.push(newStyle);
    selectedStyleGuid.value = normalizedId;
    showCreateStyleModal.value = false;
  };
  
  async function loadSharedKnowledgeBases() {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        sharedKnowledgeBases.value = [];
        return;
      }
      const res = await axios.get(`${apiEndpoint}/api/sharedKnowledgeBases`, cfg);
      sharedKnowledgeBases.value = Array.isArray(res.data) === true ? res.data : [];
    } catch (e) {
      sharedKnowledgeBases.value = [];
    }
  }
  
  const initializing = ref(true);
  
  const limitAgentSetupOptionRaw = process.env.VUE_APP_LIMIT_AGENT_SETUP_OPTION;
  const limitAgentSetupOption = limitAgentSetupOptionRaw !== undefined ? limitAgentSetupOptionRaw.toLowerCase() : null;
  const hideModeSelector = false;
  
  function openScrapeReport(file) {
    scrapeReportSource.value = { name: file.fileName, url: file.fileName };
    showScrapeReportModal.value = true;
  }
  
  function closeScrapeReportModal() {
    showScrapeReportModal.value = false;
    scrapeReportSource.value = null;
  }
  
  async function continueWebsiteScanForSetup(file) {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) return;
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const sourceUrl = file.url !== undefined && file.url !== null ? file.url : file.fileName;
      await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/continue`, { rootUrl: sourceUrl, maxPages: file.maxPages || 0 }, cfg);
      await refreshAgentData();
      toast.success('Continue command sent. Refreshing statuses.');
    } catch (e) {
      toast.error('Failed to continue website scan.');
    }
  }
  
  async function retryFailedWebsiteScanForSetup(file) {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) return;
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const sourceUrl = file.url !== undefined && file.url !== null ? file.url : file.fileName;
      await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/retryFailed`, { rootUrl: sourceUrl, maxPages: file.maxPages || 0 }, cfg);
      await refreshAgentData();
      toast.success('Retry failed command sent. Refreshing statuses.');
    } catch (e) {
      toast.error('Failed to retry failed website scan.');
    }
  }
  
  const fieldDirectionOverrides = reactive({});
  function handleDirKeydown(event, fieldKey) {
    const isCtrl = event.ctrlKey === true || event.metaKey === true;
    if (isCtrl !== true) return;
    const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
    if (key === 'shift') {
      if (event.ctrlKey === true || event.metaKey === true) {
        const loc = Number(event.location);
        if (loc === 1) {
          event.preventDefault();
          fieldDirectionOverrides[fieldKey] = 'ltr';
        } else if (loc === 2) {
          event.preventDefault();
          fieldDirectionOverrides[fieldKey] = 'rtl';
        }
        return;
      }
    }
    if (key === 'l') {
      event.preventDefault();
      fieldDirectionOverrides[fieldKey] = 'ltr';
    } else if (key === 'r') {
      event.preventDefault();
      fieldDirectionOverrides[fieldKey] = 'rtl';
    } else if (key === 'a') {
      if (event.shiftKey === true) {
        event.preventDefault();
        fieldDirectionOverrides[fieldKey] = 'auto';
      }
    }
  }
  function handleAgentDescriptionInput() {
    agentDescriptionError.value = false;
    if (typeof agentDescription.value === 'string') {
      const normalized = agentDescription.value.replace(/^\n+/, '');
      if (normalized !== agentDescription.value) {
        agentDescription.value = normalized;
      }
    }
  }
  function applyPersonalitySelection(personality) {
    agentDescription.value = personality.template;
    selectedAgentPersonality.value = personality.id;
  }
  function handlePersonalityChange(personality) {
    if (personality === undefined || personality === null) {
      return;
    }
    const hasDescription = typeof agentDescription.value === 'string' && agentDescription.value.trim().length > 0;
    if (hasDescription === true) {
      pendingPersonalitySelection.value = personality;
      showPersonalityReplaceModal.value = true;
      return;
    }
    applyPersonalitySelection(personality);
  }
  function confirmPersonalityReplace() {
    if (pendingPersonalitySelection.value !== null) {
      applyPersonalitySelection(pendingPersonalitySelection.value);
    }
    showPersonalityReplaceModal.value = false;
    pendingPersonalitySelection.value = null;
  }
  function cancelPersonalityReplace() {
    showPersonalityReplaceModal.value = false;
    pendingPersonalitySelection.value = null;
  }
  function getDirStyle(fieldKey) {
    const override = fieldDirectionOverrides[fieldKey];
    let dir = null;
    if (override === 'ltr') {
      dir = 'ltr';
    } else if (override === 'rtl') {
      dir = 'rtl';
    } else {
      dir = isRtl.value === true ? 'rtl' : 'ltr';
    }
    return dir === 'rtl' ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' };
  }
  
  function toUtcHourFromLocalTime(hhmm) {
    if (typeof hhmm !== 'string' || /^\d{2}:\d{2}$/.test(hhmm) === false) {
      return undefined;
    }
    const parts = hhmm.split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isFinite(h) === false || Number.isFinite(m) === false) {
      return undefined;
    }
    const now = new Date();
    const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
    return local.getUTCHours();
  }
  
  function toUtcMinuteFromLocalTime(hhmm) {
    if (typeof hhmm !== 'string' || /^\d{2}:\d{2}$/.test(hhmm) === false) {
      return undefined;
    }
    const parts = hhmm.split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isFinite(h) === false || Number.isFinite(m) === false) {
      return undefined;
    }
    const now = new Date();
    const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
    return local.getUTCMinutes();
  }
  
  function toLocalHHMMFromUtc(hour, minute) {
    if (Number.isFinite(Number(hour)) === false) {
      return '';
    }
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), Number(hour), Number.isFinite(Number(minute)) === true ? Number(minute) : 0, 0, 0));
    const lh = String(d.getHours()).padStart(2, '0');
    const lm = String(d.getMinutes()).padStart(2, '0');
    return `${lh}:${lm}`;
  }
  
  async function rescanAllFailedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Set();
      let sent = 0;
      for (let i = 0; i < existingFiles.value.length; i = i + 1) {
        const f = existingFiles.value[i];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
          if (ps === 'Failed' || ps === 'Error') {
            const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
            if (unique.has(rootUrl) === false) {
              unique.add(rootUrl);
              const body = { rootUrl: rootUrl, type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 };
              try {
                await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
                sent = sent + 1;
              } catch (e) {}
            }
          }
        }
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' failed website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No failed website sources to rescan.');
      }
    } catch (e) {}
  }
  
  async function rescanFailedSelectedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (selectedRowKeys.value.size === 0) {
        toast.error('Please select at least one item.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Set();
      let sent = 0;
      const keys = Array.from(selectedRowKeys.value);
      for (let i = 0; i < keys.length; i = i + 1) {
        const name = keys[i];
        const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
        if (existingIdx > -1) {
          const f = existingFiles.value[existingIdx];
          if (types.includes(f.fileType) === true && f.status !== 'deleted') {
            const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
            if (ps === 'Failed' || ps === 'Error') {
              const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
              if (unique.has(rootUrl) === false) {
                unique.add(rootUrl);
                try {
                  await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, { rootUrl: rootUrl, type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 }, cfg);
                  sent = sent + 1;
                } catch (e) {}
              }
            }
          }
        }
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' failed website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No selected failed website sources to rescan.');
      }
    } catch (e) {}
  }
  
  const isAdvancedOpen = ref(false);
  const advancedDropdownRef = ref(null);
  
  function onGlobalClickCloseAdvanced(event) {
    if (isAdvancedOpen.value !== true) {
      return;
    }
    const root = advancedDropdownRef.value;
    if (root === null || root === undefined) {
      isAdvancedOpen.value = false;
      return;
    }
    if (root.contains(event.target) === false) {
      isAdvancedOpen.value = false;
    }
  }
  
  onMounted(() => {
    window.addEventListener('click', onGlobalClickCloseAdvanced, true);
  });
  
  onBeforeUnmount(() => {
    closeTransferExportStream();
    window.removeEventListener('click', onGlobalClickCloseAdvanced, true);
  });
  
  function runAdvancedAction(action) {
    try {
      if (action === 'selectAllFiltered') {
        selectAllFilteredRows();
        isAdvancedOpen.value = false;
        return;
      }
      if (action === 'clearSelection') {
        clearSelectedRows();
        isAdvancedOpen.value = false;
        return;
      }
      if (action === 'enableRescrapeSelected') {
        toast.info('Updating rescrape info for selected...');
        isAdvancedOpen.value = false;
        enableRescrapeForSelected();
        return;
      }
      if (action === 'disableRescrapeSelected') {
        toast.info('Disabling rescrape for selected...');
        isAdvancedOpen.value = false;
        disableRescrapeForSelected();
        return;
      }
      if (action === 'deleteSelected') {
        toast.info('Deleting selected items...');
        isAdvancedOpen.value = false;
        deleteSelectedRows();
        return;
      }
      if (action === 'rescanSelected') {
        toast.info('Rescan selected - starting...');
        isAdvancedOpen.value = false;
        rescanSelectedWebsiteUrls();
        return;
      }
      if (action === 'retryProcessingSelected') {
        toast.info('Retry processing for selected - starting...');
        isAdvancedOpen.value = false;
        retryProcessingSelectedWebsiteUrls();
        return;
      }
      if (action === 'rescanFailedSelected') {
        toast.info('Rescan failed for selected - starting...');
        isAdvancedOpen.value = false;
        rescanFailedSelectedWebsiteUrls();
        return;
      }
    } catch (e) {
    } finally {
      isAdvancedOpen.value = false;
    }
  }
  
  </script>
  
  <style>
  .badge-error, .btn-error, .badge-success, .btn-success {
    color: #fff;
  }
  .tab:is(input[type=radio]):after{white-space:nowrap;}
  table {
    font-family: "Heebo", sans-serif !important;
  }
  
  
  .ai-agent-setup[dir='rtl'] .space-x-2 > :not([hidden]) ~ :not([hidden]){margin-right:0.5rem;margin-left:0}
  .ai-agent-setup[dir='rtl'] .space-x-3 > :not([hidden]) ~ :not([hidden]){margin-right:0.75rem;margin-left:0}
  .ai-agent-setup[dir='rtl'] .space-x-4 > :not([hidden]) ~ :not([hidden]){margin-right:1rem;margin-left:0}
  .ai-agent-setup[dir='rtl'] .ml-1{margin-left:0;margin-right:0.25rem}
  .ai-agent-setup[dir='rtl'] .ml-2{margin-left:0;margin-right:0.5rem}
  .ai-agent-setup[dir='rtl'] .ml-3{margin-left:0;margin-right:0.75rem}
  .ai-agent-setup[dir='rtl'] .ml-4{margin-left:0;margin-right:1rem}
  .ai-agent-setup[dir='rtl'] .mr-1{margin-right:0;margin-left:0.25rem}
  .ai-agent-setup[dir='rtl'] .mr-2{margin-right:0;margin-left:0.5rem}
  .ai-agent-setup[dir='rtl'] .mr-3{margin-right:0;margin-left:0.75rem}
  .ai-agent-setup[dir='rtl'] .mr-4{margin-right:0;margin-left:1rem}
  
  
  .custom-tab-button {
    border: none;
    background-color: transparent;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px; 
  }
  
  .custom-tab-button.inactive-tab {
    color: var(--fallback-bc, oklch(var(--bc)/0.6)); 
  }
  
  .custom-tab-button.inactive-tab:hover {
    color: var(--fallback-bc, oklch(var(--bc)/0.8));
    background-color: var(--fallback-b2, oklch(var(--b2)/0.5)); 
  }
  
  .custom-tab-button.active-tab {
    color: var(--fallback-p, oklch(var(--p)/1)); 
    border-bottom-color: var(--fallback-p, oklch(var(--p)/1)); 
    font-weight: 600;
  }
  
  .tab-pane {
    display: block;
  }
  
  .ai-agent-setup[dir='rtl'] .title-row{margin-right:1rem}
  
  @keyframes scaleIn {
    0% { transform: scale(0.96); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  
  </style>
  <style>
  
  
  
  
  .ai-agent-setup .btn{margin-inline:0.25rem}
  
  .ai-agent-setup[dir='rtl'] table th,
  .ai-agent-setup[dir='rtl'] table td{ text-align:right !important }
  .ai-agent-setup[dir='rtl'] table td .flex{ justify-content:flex-end }
  
  </style>
  
  